import React from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { Country, State } from "country-state-city";
import { SITENAMEALIAS } from "../utils/init";
import { showToast, showHttpError } from "../utils/library";
import {
  saveUserKycData,
  addUserKYCDetails,
  getUserKycDetails,
  finishKyc,
  addUserEnterpriseDetails,
  addEnterpriseOwner,
  deleteEnterpriseOwner,
  getCountries,
  getScannerToken,
  saveEnterpriseUserInfo,
  getEnterpriseKycDetails,
  getUserPhoto,
  getMerchantCategoryCodes,
} from "../utils/kycService";
import {
  SubmitKyc,
  GetUserDetails,
  getAuthToken,
  getAuthUuid,
} from "../utils/service";
import Webcam from "react-webcam";
import QRCode from "react-qr-code";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Loader from "../components/Loader";
import "../pages/kyc.css";

/* ============================
   CONSTANTS
   ============================ */

const MAX_FILE_SIZE = 6 * 1024 * 1024;
const ALLOWED_DOC_TYPES = ".jpg,.jpeg,.png,.pdf,.doc,.docx,.jfif,.heic";
const ALLOWED_IMAGE_TYPES = ".jpg,.jpeg,.png,.jfif,.heic";
const TIER3_ALLOWED_TYPES = ".jpg,.jpeg,.pdf,.doc,.docx,.jfif";
const TIER3_MAX_FILE_SIZE = 5 * 1024 * 1024;

const INDUSTRIES = [
  "Agriculture",
  "Alternative Medicine",
  "Automotive",
  "Banking",
  "Construction",
  "Education",
  "Energy",
  "Entertainment",
  "Finance",
  "Healthcare",
  "Hospitality",
  "Information Technology",
  "Insurance",
  "Manufacturing",
  "Media",
  "Mining",
  "Real Estate",
  "Retail",
  "Telecommunications",
  "Transportation",
  "Other",
];

const OCCUPATIONS = [
  "Accountant",
  "Adjudicator",
  "Analyst",
  "Attorney",
  "Business Owner",
  "Consultant",
  "Developer",
  "Doctor",
  "Engineer",
  "Executive",
  "Manager",
  "Nurse",
  "Professor",
  "Retired",
  "Salesperson",
  "Student",
  "Teacher",
  "Technician",
  "Trader",
  "Unemployed",
  "Other",
];

const FUNDS_SOURCES = [
  "Salary/Employment Income",
  "Salary",
  "Savings",
  "Investment Returns",
  "Inheritance",
  "Gift",
  "Sale of Property",
  "Retirement Funds",
  "Business Income",
  "Cryptocurrency Mining/Trading",
  "Loan",
  "Other",
];

const AMOUNT_OPTIONS = [
  { value: "1", label: "$0 - $10,000" },
  { value: "2", label: "$10,000 - $25,000" },
  { value: "3", label: "$25,000 - $50,000" },
  { value: "4", label: "$50,000 - $100,000" },
  { value: "5", label: "$100,000 - $200,000" },
  { value: "6", label: "$200,000 - $300,000" },
  { value: "8", label: "$300,000 - $1,000,000" },
  { value: "7", label: "$1,000,000+" },
];

const COMPANY_NET_WORTH_OPTIONS = [
  { value: "1", label: "$0 - $100,000" },
  { value: "2", label: "$100,000 - $500,000" },
  { value: "3", label: "$500,000 - $1,000,000" },
  { value: "4", label: "$1,000,000 - $5,000,000" },
  { value: "5", label: "$5,000,000 - $10,000,000" },
  { value: "6", label: "$10,000,000 - $50,000,000" },
  { value: "7", label: "$50,000,000+" },
];

const INDIVIDUAL_STEPS = [
  { label: "Personal Address", icon: "fas fa-user" },
  { label: "Date of Birth & SSN", icon: "fas fa-file-alt" },
  { label: "Employment Details", icon: "fas fa-briefcase" },
  { label: "Address Proof", icon: "fas fa-cloud-upload-alt" },
  { label: "ID Document", icon: "fas fa-id-card" },
  { label: "Photo Verification", icon: "fas fa-camera" },
];

const ENTERPRISE_STEPS = [
  { label: "Company Information", icon: "fas fa-building" },
  { label: "Company Documents", icon: "fas fa-cloud-upload-alt" },
  { label: "Authorization Letter", icon: "fas fa-file-alt" },
  { label: "Beneficial Owners", icon: "fas fa-users" },
];

const CORPORATE_STRUCTURES = [
  { value: "Corporation", label: "Corporation" },
  { value: "LLC", label: "LLC" },
  { value: "Partnership", label: "Partnership" },
  { value: "Sole Proprietorship", label: "Sole Proprietorship" },
  { value: "Non-Profit", label: "Non-Profit" },
  { value: "Trust", label: "Trust" },
  { value: "Other", label: "Other" },
];

const TRANSACTION_FREQUENCY = [
  { value: "0-10", label: "0-10 transactions/month" },
  { value: "10-100", label: "10-100 transactions/month" },
  { value: "100-1000", label: "100-1,000 transactions/month" },
  { value: "1000+", label: "1,000+ transactions/month" },
];

const BANKING_DURATION = [
  { value: "0-1 year", label: "0-1 year" },
  { value: "1-2 year", label: "1-2 years" },
  { value: "2+ year", label: "2+ years" },
];

const YEARS_IN_BUSINESS_OPTIONS = [
  { value: "< 1 year", label: "< 1 year" },
  { value: "1 - 2 years", label: "1 - 2 years" },
  { value: "2-5 years", label: "2-5 years" },
  { value: "5+ years", label: "5+ years" },
];

const PREMISE_TYPE_OPTIONS = [
  { value: "Kiosk", label: "Kiosk" },
  { value: "Mailbox", label: "Mailbox" },
  { value: "Office building", label: "Office building" },
  { value: "Residential/Home office", label: "Residential/Home office" },
  { value: "Retail store front", label: "Retail store front" },
  { value: "Mobile/on the go", label: "Mobile/on the go" },
  { value: "Website", label: "Website" },
];

const YEARS_IN_LOCATION_OPTIONS = [
  { value: "< 1 year", label: "< 1 year" },
  { value: "1 - 2 years", label: "1 - 2 years" },
  { value: "2-5 years", label: "2-5 years" },
  { value: "5+ years", label: "5+ years" },
];

// Change #1: Updated card processing methods - no longer checkboxes with percentages
const CARD_PROCESSING_METHODS = [
  { value: "Online", label: "Online" },
  { value: "In person swipe", label: "In person swipe" },
  { value: "Over the phone", label: "Over the phone" },
  { value: "Key entered", label: "Key entered" },
];

// Change #7: How Do You Advertise options
const ADVERTISE_OPTIONS = [
  { value: "Yellow Pages", label: "Yellow Pages" },
  { value: "Telemarketing", label: "Telemarketing" },
  { value: "Catalog", label: "Catalog" },
  { value: "Word of mouth", label: "Word of mouth" },
  { value: "Publications", label: "Publications" },
  { value: "Email marketing", label: "Email marketing" },
  { value: "Internet", label: "Internet" },
  { value: "Others", label: "Others" },
];

// Change #8: Fulfillment & Billing options
const CARD_CHARGED_OPTIONS = [
  {
    value: "After product or service is provided",
    label: "After product or service is provided",
  },
  { value: "In Advance", label: "In Advance" },
];

const DELIVERY_DAYS_OPTIONS = [
  { value: "Immediately", label: "Immediately" },
  { value: "Same day", label: "Same day" },
  { value: "7 days or less", label: "7 days or less" },
  { value: "30 days or less", label: "30 days or less" },
  { value: "60 days or less", label: "60 days or less" },
  { value: "60+ days", label: "60+ days" },
];

const REFUND_DAYS_OPTIONS = [
  { value: "No Refunds", label: "No Refunds" },
  { value: "Same Day", label: "Same Day" },
  { value: "3 Days", label: "3 Days" },
  { value: "7 Days", label: "7 Days" },
  { value: "14 Days", label: "14 Days" },
  { value: "15 Days", label: "15 Days" },
  { value: "30 Days", label: "30 Days (most common)" },
  { value: "45 Days", label: "45 Days" },
  { value: "60 Days", label: "60 Days" },
  { value: "90 Days", label: "90 Days" },
  { value: "More than 90 Days", label: "More than 90 Days" },
  { value: "Case-by-Case", label: "Case-by-Case" },
  { value: "Other", label: "Other" },
];

const REFUND_PROCESSING_DAYS_OPTIONS = [
  { value: "Same Day", label: "Same Day" },
  { value: "1-2 Business Days", label: "1–2 Business Days" },
  { value: "3-5 Business Days", label: "3–5 Business Days (common)" },
  { value: "5-7 Business Days", label: "5–7 Business Days (common)" },
  { value: "7-10 Business Days", label: "7–10 Business Days" },
  { value: "10-14 Business Days", label: "10–14 Business Days" },
  { value: "14-30 Business Days", label: "14–30 Business Days" },
  { value: "More than 30 Business Days", label: "More than 30 Business Days" },
  { value: "Case-by-Case", label: "Case-by-Case" },
  { value: "Not Applicable", label: "Not Applicable" },
  { value: "Other", label: "Other" },
];

// Change #6: Tax ID type options
const TAX_ID_TYPES = [
  { value: "EIN", label: "EIN (Employer Identification Number)" },
  { value: "SSN", label: "SSN (Social Security Number)" },
  { value: "ITIN", label: "ITIN (Individual Taxpayer Identification Number)" },
  { value: "TIN", label: "TIN (Taxpayer Identification Number)" },
];

const USER_KYC_FIELDS = [
  "firstName",
  "middleName",
  "lastName",
  "address",
  "city",
  "state",
  "country",
  "zip",
  "uuid",
  "dob",
  "ssn",
  "birthPlace",
  "industry",
  "occupation",
  "sourcesOfFunds",
  "txnVolumeId",
  "annualIncomeId",
  "netWorthId",
  "empCategory",
  "empType",
  "pep",
  "bankingPartner",
  "relationWithBank",
  "accountPurpose",
  "thirdParty",
  "userDocsStatus",
  "isKycFinished",
  "userTierDocsStatus",
  "panNo",
  "tin",
  "userType",
];

/* ============================
   VALIDATION HELPERS (module-level)
   ============================ */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9]{7,20}$/;
const ID_NUMBER_REGEX = /^[A-Za-z0-9][A-Za-z0-9 -]{3,50}[A-Za-z0-9]$/;

const QR_BASE_URL = window.location.origin + "/kyc-capture-photo";
const RETURN_REFUND_POLICY_REGEX =
  /^(?=.{5,255}$)(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/;

// Key Entered popup info text
const KEY_ENTERED_INFO = `Key Entered (Manually Entered / Card-Not-Present)

Key Entered means the merchant manually types the card details instead of swiping, dipping, tapping, or using an online checkout.

Examples:
• Merchant enters card number into a virtual terminal
• Phone order where customer reads card details
• Mail order processing
• Backup processing when card reader fails

Risk level: Higher fraud and chargeback risk because the card is not physically read.`;

const MCC_RECOMMENDATIONS = [
  {
    industry: "Crypto Exchange / OTC",
    mcc: "6051",
    description: "Non-Financial Institutions / Crypto / Money Orders",
    view: "High Risk",
  },
  {
    industry: "Crypto Wallet / Custody",
    mcc: "6051",
    description: "Non-Financial Institutions",
    view: "High Risk",
  },
  {
    industry: "CBD / Hemp",
    mcc: "5499",
    description: "Miscellaneous Food Stores",
    view: "High Risk / Processor Dependent",
  },
  {
    industry: "Peptides / Research Chemicals",
    mcc: "5169",
    description: "Chemicals & Allied Products",
    view: "High Risk / Enhanced Due Diligence",
  },
  {
    industry: "Peptide Clinic / Wellness",
    mcc: "8099",
    description: "Medical Services",
    view: "Healthcare / High Risk",
  },
  {
    industry: "Forex / CFD / Trading",
    mcc: "6211",
    description: "Securities / Brokers",
    view: "High Risk",
  },
  {
    industry: "Supplements / Nutraceuticals",
    mcc: "5499",
    description: "Miscellaneous Food Stores",
    view: "High Risk Monitoring",
  },
  {
    industry: "SaaS / Software",
    mcc: "5734",
    description: "Computer Software Stores",
    view: "Standard",
  },
  {
    industry: "Digital Services / Online Services",
    mcc: "7372",
    description: "Computer Programming & Data Processing",
    view: "Standard",
  },
  {
    industry: "Gaming / Gambling / Betting",
    mcc: "7995",
    description: "Betting / Gambling",
    view: "High Risk",
  },
  {
    industry: "Adult Content",
    mcc: "5967",
    description: "Direct Marketing / Inbound Telemarketing",
    view: "High Risk",
  },
  {
    industry: "Adult Subscription",
    mcc: "5968",
    description: "Subscription Services",
    view: "High Risk",
  },
  {
    industry: "Telemarketing Inbound",
    mcc: "5967",
    description: "Inbound Telemarketing",
    view: "High Risk",
  },
  {
    industry: "Telemarketing Outbound",
    mcc: "5966",
    description: "Outbound Telemarketing",
    view: "High Risk",
  },
  {
    industry: "Travel Agency",
    mcc: "4722",
    description: "Travel Agencies",
    view: "Medium Risk",
  },
  {
    industry: "Marketplace / Ecommerce",
    mcc: "5399",
    description: "General Merchandise",
    view: "Depends on Model",
  },
  {
    industry: "Dropshipping",
    mcc: "5969",
    description: "Direct Marketing",
    view: "Medium / High Risk",
  },
  {
    industry: "Pharmacy",
    mcc: "5912",
    description: "Drug Stores & Pharmacies",
    view: "Healthcare",
  },
  {
    industry: "Medical Clinic",
    mcc: "8011",
    description: "Doctors & Physicians",
    view: "Healthcare",
  },
  {
    industry: "Subscription Billing",
    mcc: "5968",
    description: "Subscription Services",
    view: "Chargeback Monitoring",
  },
  {
    industry: "Dating",
    mcc: "7273",
    description: "Dating Services",
    view: "High Risk",
  },
  {
    industry: "Financial Services / Lending",
    mcc: "6012",
    description: "Financial Institutions",
    view: "High Risk",
  },
  {
    industry: "Money Transfer / MSB",
    mcc: "6051",
    description: "Non-Financial Institutions",
    view: "Licensing",
  },
  {
    industry: "Charity / Nonprofit",
    mcc: "8398",
    description: "Charitable Organizations",
    view: "Enhanced Review",
  },
  {
    industry: "Education / Coaching",
    mcc: "8299",
    description: "Educational Services",
    view: "Standard",
  },
  {
    industry: "Travel Club",
    mcc: "5962",
    description: "Direct Marketing Travel",
    view: "High Risk",
  },
  {
    industry: "Debt Collection",
    mcc: "7321",
    description: "Consumer Credit Reporting / Collection",
    view: "High Risk",
  },
  {
    industry: "NFT / Web3",
    mcc: "6051",
    description: "Non-Financial Institutions",
    view: "High Risk",
  },
];

class KycContent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showLoader: false,
      kycType: "enterprise",
      showTypeSelection: false,
      activeStep: 0,
      kycStatus: -1,
      kycMessage: "",
      completeUserData: {},
      countries: [],
      countriesLoading: false,
      firstName: "",
      middleName: "",
      lastName: "",
      houseNo: "",
      streetArea: "",
      city: "",
      stateProvince: "",
      postalCode: "",
      country: "",
      dob: "",
      dobMonth: "",
      dobDay: "",
      dobYear: "",
      birthPlace: "",
      ssn: "",
      ssnConfirm: "",
      industry: "",
      occupation: "",
      sourceOfFunds: "",
      monthlyTransactionVolume: "",
      annualIncome: "",
      netWorth: "",
      employmentCategory: "",
      employmentType: "",
      pepStatus: "0",
      bankingPartner: "",
      bankingDuration: "",
      purposeAccount: "",
      thirdParty: "0",
      addressProofFile: null,
      addressProofFileName: "",
      idDocumentFrontFile: null,
      idDocumentFrontFileName: "",
      idDocumentBackFile: null,
      idDocumentBackFileName: "",
      panNumber: "",
      panCardPhotoFile: null,
      panCardPhotoFileName: "",
      showWebcam: false,
      capturedPhoto: null,
      capturedPhotoBlob: null,
      faceDetected: false,
      faceDetectionLoading: false,
      photoUploaded: false,
      showQR: false,
      qrCount: 300,
      qrInterval: null,
      twoFactorAuthKey: "",
      // Enterprise - Company Info
      companyName: "",
      companyRegNumber: "",
      companyWebsite: "",
      incorporationCountry: "",
      corporateStructure: "",
      businessActivity: "",
      businessActivityCode: "",
      dbaName: "",
      businessPhoneNumber: "",
      businessEmailAddress: "",
      yearsInBusiness: "",
      businessDescription: "",
      // Change #5: Stock Exchange fields
      stockExchangeName: "",
      stockTickerSymbol: "",
      // Change #6: Tax Information
      taxIdType: "EIN",
      taxpayerIdNumber: "",
      isExemptPayee: "",
      isNonprofit: "",
      // Registered Address
      companyregAddress: "",
      companyregCity: "",
      companyregState: "",
      companyregCountry: "",
      companyregZip: "",
      companyRegPremiseType: "",
      companyRegYearsInThisLocation: "",
      // Change #4: New registered address fields
      companyRegPremiseOwner: "",
      companyRegAreaZoned: "",
      companyRegSquareFootage: "",
      // Office Address
      companyOfficeAddress: "",
      companyOfficeCity: "",
      companyOfficeState: "",
      companyOfficeCountry: "",
      companyOfficeZip: "",
      companyOfficePremiseType: "",
      companyOfficeYearsInThisLocation: "",
      // Change #4: New office address fields
      companyOfficePremiseOwner: "",
      companyOfficeAreaZoned: "",
      companyOfficeSquareFootage: "",
      // Change #4: Number of locations (independent)
      numberOfLocations: "",
      sameAddress: true,
      revenue: "",
      profit: "",
      companyAssets: "",
      companyNetWorth: "",
      accountPurpose: "",
      investmentSource: "",
      transactionVolumes: "",
      transactionFrequency: "",
      relationWithBank: "",
      // Change #1: Updated processing info - percentages per method instead of checkboxes
      isProcessingCardTransaction: "",
      cardProcessingMethodPercentages: {
        Online: "",
        "In person swipe": "",
        "Over the phone": "",
        "Key entered": "",
      },
      transactionLimitInUsd: "",
      averageTransactionSizeInUsd: "",
      highestTransactionSizeInUsd: "",
      acceptAchPayment: "",
      // Change #2: Amex payments
      acceptAmexPayment: false,
      amexMonthlyVolume: "",
      amexAvgTicket: "",
      amexHighTicket: "",
      // Change #3: Processing URLs
      paymentProcessingUrl: "",
      demoLoginUsername: "",
      demoLoginPassword: "",
      // Change #7: Business Operations
      advertiseMethods: [],
      inboundPercent: "",
      outboundPercent: "",
      b2bPercent: "",
      retailPercent: "",
      isBusinessSeasonal: "",
      peakOffSeasonDescription: "",
      returnRefundPolicy: "",
      refundDaysAfterPurchase: "",
      refundProcessingDays: "",
      // Change #8: Fulfillment & Billing
      cardChargedWhen: "",
      deliveryDaysAfterCharge: "",
      usesThirdPartyFulfillment: "",
      thirdPartyFulfillmentName: "",
      // Change #9: Risk & Compliance
      isPciCompliant: "",
      wasTerminatedMerchant: "",
      hasDataCompromise: "",
      wasVisaRiskProgram: "",
      usesThirdPartySoftware: "",
      thirdPartySoftwareNames: "",
      bankruptcyType: "",
      bankruptcyFilingDate: "",
      bankruptcyExplanation: "",
      // Key entered info popup
      showKeyEnteredInfo: false,
      showMccInfo: false,
      // Enterprise docs
      incorporationCertificate: null,
      incorporationCertificateName: "",
      companyBankStatement: null,
      companyBankStatementName: "",
      memorandum: null,
      memorandumName: "",
      associationArticles: null,
      associationArticlesName: "",
      incumbencyCertificate: null,
      incumbencyCertificateName: "",
      directorsRegister: null,
      directorsRegisterName: "",
      shareholdersRegister: null,
      shareholdersRegisterName: "",
      boardResolution: null,
      boardResolutionName: "",
      wolfsbergDoc: null,
      wolfsbergDocName: "",
      authorizationLetter: null,
      authorizationLetterName: "",
      processingStatement: null,
      processingStatementName: "",
      beneficialOwners: [],
      showAddOwnerModal: false,
      editingOwner: null,
      showDeleteConfirm: false,
      ownerToDelete: null,
      ownerType: "Authorized signatory",
      ownerTitle: "President",
      ownerFirstName: "",
      ownerMiddleName: "",
      ownerLastName: "",
      ownerEmail: "",
      ownerPhone: "",
      ownerAddress: "",
      ownerCity: "",
      ownerState: "",
      ownerCountry: "",
      ownerZip: "",
      ownerDob: "",
      ownerBirthPlace: "",
      ownerSsn: "",
      ownerIdType: "",
      ownerIdNumber: "",
      ownerIdCountry: "",
      ownerIdState: "",
      ownerPoaType: "",
      idStates: [],
      ownerOwnershipPercent: "",
      ownerPep: "0",
      ownerUuid: "",
      ownerFrontId: null,
      ownerFrontIdName: "",
      ownerBackId: null,
      ownerBackIdName: "",
      ownerAddressProof: null,
      ownerAddressProofName: "",
      ownerSelfie: null,
      ownerSelfieName: "",
      ownerInvestingFund: null,
      ownerInvestingFundName: "",
      ownerEmailError: "",
      ownerPhoneError: "",
      phoneCountries: [],
      phoneFilteredCountries: [],
      phoneSelectedCountry: null,
      phoneDropdownOpen: false,
      phoneSearchTerm: "",
      userEmail: "",
      userName: "",
      userUuid: "",
      errors: {},
      userTierDocsStatus: null,
      tier3BankStatement: null,
      tier3BankStatementName: "",
      tier3Tin: "",
      tier3TaxStatement: null,
      tier3TaxStatementName: "",
      tier3Submitted: false,
      tier3Loading: false,
      // Change: Business Activity searchable dropdown
      businessActivities: [],
      businessActivitySearchTerm: "",
      showBusinessActivityDropdown: false,
    };

    this.webcamRef = React.createRef();
    this.canvasRef = React.createRef();
    this.addressProofInputRef = React.createRef();
    this.idDocFrontInputRef = React.createRef();
    this.idDocBackInputRef = React.createRef();
    this.panCardInputRef = React.createRef();
    this.incorporationCertInputRef = React.createRef();
    this.companyBankStatementInputRef = React.createRef();
    this.memorandumInputRef = React.createRef();
    this.associationArticlesInputRef = React.createRef();
    this.incumbencyCertInputRef = React.createRef();
    this.directorsRegisterInputRef = React.createRef();
    this.shareholdersRegisterInputRef = React.createRef();
    this.boardResolutionInputRef = React.createRef();
    this.wolfsbergDocInputRef = React.createRef();
    this.authLetterInputRef = React.createRef();
    this.processingStatementInputRef = React.createRef();
    this.ownerFrontIdInputRef = React.createRef();
    this.ownerBackIdInputRef = React.createRef();
    this.ownerAddressProofInputRef = React.createRef();
    this.ownerSelfieInputRef = React.createRef();
    this.ownerInvestingFundInputRef = React.createRef();
    this.tier3BankStatementInputRef = React.createRef();
    this.tier3TaxStatementInputRef = React.createRef();

    this.faceApiLoaded = false;
    this.lastDetectedTime = Date.now();
    this.detectInterval = null;
    this.photoPollingInterval = null;
  }

  componentDidMount() {
    this.fetchKycStatus();
    this.loadFaceApiModels();
    this.fetchCountries();
    this.fetchBusinessActivities();
    GetUserDetails()
      .then((res) => {
        const data = res.data;
        if (data && data.userTierDocsStatus !== undefined) {
          this.setState({ userTierDocsStatus: data.userTierDocsStatus });
        }
      })
      .catch(() => { });

    setTimeout(() => {
      document
        .querySelectorAll(
          ".modal-header .close, .modal-header .btn-close, button.close"
        )
        .forEach((btn) => {
          let span = btn.querySelector("span");
          if (!span) {
            span = document.createElement("span");
            btn.appendChild(span);
          }
          span.innerHTML = "×";
          span.setAttribute("aria-hidden", "true");
          span.style.fontSize = "1.8rem";
          span.style.fontWeight = "400";
          span.style.lineHeight = "1";
          btn.childNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) node.textContent = "";
          });
        });
    }, 100);
  }

  componentWillUnmount() {
    if (this.detectInterval) clearInterval(this.detectInterval);
    if (this.state.qrInterval) clearInterval(this.state.qrInterval);
    this.stopPhotoPolling();
  }

  /* ============================
     INPUT VALIDATION HELPERS
     ============================ */

  allowOnlyAlphabetsCityState = (e) => {
    const key = e.key;
    if (["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"].includes(key))
      return;
    if (!/^[a-zA-Z\s]$/.test(key)) e.preventDefault();
  };

  preventPasteCityState = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    if (!/^[a-zA-Z\s]*$/.test(paste)) e.preventDefault();
  };

  allowOnlyNumbersAndSpace = (e) => {
    const key = e.key;
    if (["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"].includes(key))
      return;
    if (!/^[0-9\s]$/.test(key)) e.preventDefault();
  };

  preventPasteNumbersAndSpace = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    if (!/^[0-9\s]*$/.test(paste)) e.preventDefault();
  };

  onlyAllowedInputHouse = (e) => {
    const key = e.key;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^[A-Za-z0-9 /,.#-]$/.test(key)) e.preventDefault();
  };

  preventPasteHouse = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    if (!/^[A-Za-z0-9 /,.#-]*$/.test(paste)) e.preventDefault();
  };

  onlyAllowedInputStreet = (e) => {
    const key = e.key;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^[\p{L}\p{N} ,\-\/.#&'()]*$/u.test(key)) e.preventDefault();
  };

  preventPasteStreet = (e) => {
    const paste = (e.clipboardData || window.clipboardData).getData("text");
    if (!/^[\p{L}\p{N} ,\-\/.#&'()]*$/u.test(paste)) e.preventDefault();
  };

  onlyAllowedZipCode = (e) => {
    const key = e.key;
    const value = e.target.value;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^[a-zA-Z0-9 ]$/.test(key) || value?.length >= 15) e.preventDefault();
  };

  onlyAllowedInput = (e) => {
    const key = e.key;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^[a-zA-Z0-9]$/.test(key)) e.preventDefault();
  };

  onlyAllowedInputBankPartner = (e) => {
    const key = e.key;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^[a-zA-Z0-9 .\-]$/.test(key)) {
      e.preventDefault();
      return;
    }
    if (e.target.value.length >= 100) {
      e.preventDefault();
    }
  };

  onlyAllowedStockExchangeName = (e) => {
    const key = e.key;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^[a-zA-Z0-9 .\-]$/.test(key)) {
      e.preventDefault();
      return;
    }
    if (e.target.value.length >= 100) {
      e.preventDefault();
    }
  };

  onlyAllowedStockTicker = (e) => {
    const key = e.key;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^[a-zA-Z0-9]$/.test(key)) {
      e.preventDefault();
      return;
    }
  };

  allowExplanationKeyDown = (e) => {
    const key = e.key;
    if (
      [
        "Backspace",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
        "Enter",
      ].includes(key)
    )
      return;
    if (!/^[a-zA-Z0-9./:-]$/.test(key)) e.preventDefault();
    if (e.target.value.length >= 5000) e.preventDefault();
  };

  onlyAllowedTIN = (e) => {
    const key = e.key;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^[a-zA-Z0-9]$/.test(key)) {
      e.preventDefault();
      return;
    }
    if (e.target.value.length >= 20) {
      e.preventDefault();
    }
  };

  onlyAllowedUsername = (e) => {
    const key = e.key;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^[a-zA-Z0-9_.\-]$/.test(key)) {
      e.preventDefault();
      return;
    }
    if (e.target.value.length >= 30) {
      e.preventDefault();
    }
  };

  onlyAllowedAmexVolume = (e) => {
    const key = e.key;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^[0-9,.]$/.test(key)) {
      e.preventDefault();
      return;
    }
    if (e.target.value.replace(/,/g, "").length >= 12) {
      e.preventDefault();
    }
  };

  onlyAllowedPassword = (e) => {
    const key = e.key;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^[a-zA-Z0-9$@.!]$/.test(key)) {
      e.preventDefault();
      return;
    }
    if (e.target.value.length >= 35) {
      e.preventDefault();
    }
  };

  allowOnlyAlphaNumSlash = (e) => {
    const key = e.key;
    if (["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"].includes(key))
      return;
    if (!/^[a-zA-Z0-9 /]$/.test(key)) e.preventDefault();
  };

  allowWebsiteUrl = (e) => {
    const key = e.key;
    if (["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"].includes(key))
      return;
    if (!/^[a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=]$/.test(key)) e.preventDefault();
  };

  allowReturnRefundPolicyKeyDown = (e) => {
    const key = e.key;
    if (["Backspace", "Tab", "ArrowLeft", "ArrowRight", "Delete"].includes(key))
      return;
    if (/\s/.test(key)) {
      e.preventDefault();
      return;
    }
    if (e.target.value.length >= 255) {
      e.preventDefault();
    }
  };

  handleReturnRefundPolicyPaste = (e) => {
    const pasteData = (e.clipboardData || window.clipboardData).getData("text");
    if (/\s/.test(pasteData)) {
      e.preventDefault();
    } else if (this.state.returnRefundPolicy.length + pasteData.length > 255) {
      e.preventDefault();
    }
  };

  handleBusinessDescriptionKeyDown = (e) => {
    const key = e.key;
    if (e.ctrlKey || e.metaKey) return;
    if (
      [
        "Backspace",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
        "Enter",
      ].includes(key)
    )
      return;
    if (!/^[a-zA-Z0-9(),\-&.' ]$/.test(key)) e.preventDefault();
    if (e.target.value.length >= 350) e.preventDefault();
  };

  handlePasteWithRegex = (e, regex, maxLength) => {
    const pasteData = (e.clipboardData || window.clipboardData).getData("text");
    if (!regex.test(pasteData)) {
      e.preventDefault();
    } else if (
      maxLength &&
      e.target.value.length + pasteData.length > maxLength
    ) {
      e.preventDefault();
    }
  };

  onlyAllowDigits = (e, maxVal) => {
    const key = e.key;
    if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(key))
      return;
    if (!/^\d$/.test(key)) {
      e.preventDefault();
      return;
    }
    const maxLen = String(maxVal).length;
    if (e.target.value.replace(/\D/g, "").length >= maxLen) e.preventDefault();
  };

  /* ============================
     OWNER MODAL — INLINE FIELD VALIDATION
     ============================ */

  validateOwnerEmail = (value) => {
    const val = (value !== undefined ? value : this.state.ownerEmail).trim();
    if (!val) {
      this.setState({ ownerEmailError: "Email address is required" });
    } else if (!EMAIL_REGEX.test(val)) {
      this.setState({
        ownerEmailError: "Enter a valid email (e.g. owner@company.com)",
      });
    } else {
      this.setState({ ownerEmailError: "" });
    }
  };

  validateOwnerPhone = (value) => {
    const val = (value !== undefined ? value : this.state.ownerPhone).trim();
    if (!val) {
      this.setState({ ownerPhoneError: "Phone number is required" });
    } else if (!PHONE_REGEX.test(val)) {
      this.setState({
        ownerPhoneError: "Enter a valid phone number (e.g. +15550000000)",
      });
    } else {
      this.setState({ ownerPhoneError: "" });
    }
  };

  handleIdCountryChange = (countryName) => {
    let states = [];
    const countryObj = Country.getAllCountries().find(
      (c) => c.name === countryName
    );
    if (countryObj) {
      states = State.getStatesOfCountry(countryObj.isoCode);
    }
    this.setState({
      ownerIdCountry: countryName,
      ownerIdState: "",
      idStates: states,
    });
  };

  /* ==== DATA FETCHING ==== */

  fetchCountries = () => {
    this.setState({ countriesLoading: true });
    getCountries()
      .then((res) => {
        if (res.data?.error?.error_data === 0) {
          const sortedCountries = res.data.countries.sort((a, b) =>
            a.country.localeCompare(b.country)
          );
          this.setState({ countries: sortedCountries });

          const phoneList = sortedCountries
            .filter((c) => c.code && c.dialCode)
            .map((c) => ({
              country: c.country,
              code: c.code,
              dialCode: String(c.dialCode).replace(/^\+/, ""),
              flag: `https://flagcdn.com/w40/${c.code.toLowerCase()}.png`,
            }));
          const defaultCountry =
            phoneList.find((c) => c.code === "US") || phoneList[0] || null;
          this.setState({
            phoneCountries: phoneList,
            phoneFilteredCountries: phoneList,
            phoneSelectedCountry: defaultCountry,
          });
        }
      })
      .catch((err) => console.error("Failed to fetch countries", err))
      .finally(() => this.setState({ countriesLoading: false }));
  };

  resolveBusinessActivityLabel = () => {
    const { businessActivity, businessActivities, businessActivityCode } =
      this.state;
    // If we have a code (which might be stored in businessActivity or businessActivityCode)
    // and we have the list of activities, try to find the label
    const codeToFind = businessActivityCode || businessActivity;

    if (codeToFind && businessActivities.length > 0) {
      const selected = businessActivities.find(
        (c) => String(c.MCC) === String(codeToFind)
      );
      if (selected) {
        this.setState({
          businessActivity: `${selected.MCC} - ${selected.DESCRIPTION}`,
          businessActivityCode: selected.MCC,
          businessActivitySearchTerm: selected.DESCRIPTION,
        });
      }
    }
  };

  fetchBusinessActivities = () => {
    getMerchantCategoryCodes()
      .then((res) => {
        console.log("Merchant category codes response:", res.data);

        const codes = res.data.mccList || [];
        this.setState({ businessActivities: codes }, () => {
          this.resolveBusinessActivityLabel();
        });
      })
      .catch((err) =>
        console.error("Failed to fetch merchant category codes", err)
      );
  };
  fetchKycStatus = (activeStepOverride = null) => {
    this.setState({ showLoader: true });
    const uuid = localStorage.getItem(SITENAMEALIAS + "_exchange_uuid");
    if (!uuid) {
      this.setState({ showLoader: false });
      showToast("error", "User UUID not found. Please log in again.");
      return;
    }

    getUserKycDetails({ uuid: uuid })
      .then((res) => {
        this.setState({ showLoader: false });
        let r = res.data;
        let d;
        if (r && r.error && r.error.error_data === 0 && r.userResult) {
          d = r.userResult;
        } else if (r && r.uuid) {
          d = r;
        } else {
          showToast("error", "Failed to fetch KYC details");
          return;
        }

        const completeUserData = {};
        USER_KYC_FIELDS.forEach((key) => {
          if (d[key] !== undefined) completeUserData[key] = d[key];
        });

        let kycStatus = -1;
        if (d.isKycFinished === 1) {
          if (d.userDocsStatus === "0") kycStatus = 0;
          else if (d.userDocsStatus === "1") kycStatus = 1;
          else if (d.userDocsStatus === "2") kycStatus = 2;
        }

        let userType = "enterprise";
        // if (d.userType === 1) userType = "individual";
        // else if (d.userType === 2) userType = "enterprise";

        let dobMonth = "",
          dobDay = "",
          dobYear = "",
          dobFormatted = "";
        if (d.dob) {
          const parts = d.dob.split("-");
          if (parts.length === 3) {
            if (parts[0].length === 4) {
              [dobYear, dobMonth, dobDay] = parts;
            } else {
              [dobMonth, dobDay, dobYear] = parts;
            }
            dobFormatted = d.dob;
          }
        }

        let activeStep = 0;
        if (activeStepOverride !== null) {
          activeStep = activeStepOverride;
        }

        this.setState(
          {
            completeUserData,
            kycStatus,
            kycMessage: d.message || "",
            kycType: userType || "enterprise",
            showTypeSelection: false, // Automatically land user on form
            userUuid: d.uuid || uuid || "",
            userEmail: d.email || "",
            userName: [d.firstName, d.middleName, d.lastName]
              .filter(Boolean)
              .join(" "),
            firstName: d.firstName || "",
            middleName: d.middleName || "",
            lastName: d.lastName || "",
            houseNo: d.address ? d.address.split(",")[0].trim() : "",
            streetArea: d.address
              ? d.address.split(",").slice(1).join(",").trim()
              : "",
            city: d.city || "",
            stateProvince: d.state || "",
            postalCode: d.zip || "",
            country: d.country || "",
            dob: dobFormatted,
            dobMonth,
            dobDay,
            dobYear,
            birthPlace: d.birthPlace || "",
            ssn: d.ssn || "",
            industry: d.industry || "",
            occupation: d.occupation || "",
            sourceOfFunds: d.sourcesOfFunds || "",
            monthlyTransactionVolume: d.txnVolumeId
              ? String(d.txnVolumeId)
              : "",
            annualIncome: d.annualIncomeId ? String(d.annualIncomeId) : "",
            netWorth: d.netWorthId ? String(d.netWorthId) : "",
            employmentCategory: d.empCategory || "",
            employmentType: d.empType || "",
            pepStatus:
              d.pep !== undefined && d.pep !== null ? String(d.pep) : "0",
            bankingPartner:
              d.bankingPartner || d.enterpriseUser?.bankingPartner
                ? String(d.bankingPartner || d.enterpriseUser?.bankingPartner)
                : "",
            bankingDuration:
              d.relationWithBank || d.enterpriseUser?.relationWithBank
                ? String(
                  d.relationWithBank || d.enterpriseUser?.relationWithBank
                )
                : "",
            purposeAccount:
              d.accountPurpose || d.enterpriseUser?.accountPurpose
                ? String(d.accountPurpose || d.enterpriseUser?.accountPurpose)
                : "",
            thirdParty:
              d.thirdParty !== undefined && d.thirdParty !== null
                ? String(d.thirdParty)
                : "0",
            panNumber: d.panNo || "",
            photoUploaded: !!d.userPhoto || d.isScannedPhoto === 1,
            activeStep,
            companyName:
              d.enterpriseUser?.companyName != null
                ? String(d.enterpriseUser.companyName)
                : "",
            companyRegNumber:
              d.enterpriseUser?.companyRegNo != null
                ? String(d.enterpriseUser.companyRegNo)
                : "",
            incorporationCountry: d.enterpriseUser?.incorporationCountry || "",
            companyWebsite: d.enterpriseUser?.companyWebsite || "",
            corporateStructure: d.enterpriseUser?.corporateStructure || "",
            businessActivity:
              d.enterpriseUser?.businessActivity != null
                ? String(d.enterpriseUser.businessActivity)
                : "",
            businessActivitySearchTerm:
              d.enterpriseUser?.businessActivity != null
                ? String(d.enterpriseUser.businessActivity)
                : "",
            dbaName:
              d.enterpriseUser?.dbaName != null
                ? String(d.enterpriseUser.dbaName)
                : "",
            businessPhoneNumber:
              d.enterpriseUser?.businessPhoneNumber != null
                ? String(d.enterpriseUser.businessPhoneNumber)
                : "",
            businessEmailAddress:
              d.enterpriseUser?.businessEmailAddress != null
                ? String(d.enterpriseUser.businessEmailAddress)
                : "",
            yearsInBusiness: d.enterpriseUser?.yearsInBusiness || "",
            businessDescription:
              d.enterpriseUser?.businessDescription != null
                ? String(d.enterpriseUser.businessDescription)
                : "",
            // Change #5
            stockExchangeName: d.enterpriseUser?.stockExchangeName || "",
            stockTickerSymbol: d.enterpriseUser?.stockTickerSymbol || "",
            // Change #6
            taxIdType: d.enterpriseUser?.identificationType || "EIN",
            taxpayerIdNumber: d.enterpriseUser?.identificationNumber || "",
            isExemptPayee:
              d.enterpriseUser?.isExemptPayee != null
                ? String(d.enterpriseUser.isExemptPayee)
                : "",
            isNonprofit:
              d.enterpriseUser?.isNpo != null
                ? String(d.enterpriseUser.isNpo)
                : "",
            companyregAddress:
              d.enterpriseUser?.companyregAddress != null
                ? String(d.enterpriseUser.companyregAddress)
                : "",
            companyregCity:
              d.enterpriseUser?.companyregCity != null
                ? String(d.enterpriseUser.companyregCity)
                : "",
            companyregState:
              d.enterpriseUser?.companyregState != null
                ? String(d.enterpriseUser.companyregState)
                : "",
            companyregCountry: d.enterpriseUser?.companyregCountry || "",
            companyregZip:
              d.enterpriseUser?.companyregZip != null
                ? String(d.enterpriseUser.companyregZip)
                : "",
            companyRegPremiseType:
              d.enterpriseUser?.companyRegPremiseType ||
              d.enterpriseUser?.premiseType ||
              "",
            companyRegYearsInThisLocation:
              d.enterpriseUser?.companyRegYearsInThisLocation ||
              d.enterpriseUser?.yearsInThisLocation ||
              "",
            // Change #4
            companyRegPremiseOwner: d.enterpriseUser?.premiseOwner || "",
            companyRegAreaZoned: d.enterpriseUser?.areaZoned || "",
            companyRegSquareFootage: d.enterpriseUser?.squareFootage || "",
            numberOfLocations: d.enterpriseUser?.numberOfLocations || "",
            companyOfficeAddress:
              d.enterpriseUser?.companyOfficeAddress != null
                ? String(d.enterpriseUser.companyOfficeAddress)
                : "",
            companyOfficeCity:
              d.enterpriseUser?.companyOfficeCity != null
                ? String(d.enterpriseUser.companyOfficeCity)
                : "",
            companyOfficeState:
              d.enterpriseUser?.companyOfficeState != null
                ? String(d.enterpriseUser.companyOfficeState)
                : "",
            companyOfficeCountry: d.enterpriseUser?.companyOfficeCountry || "",
            companyOfficeZip:
              d.enterpriseUser?.companyOfficeZip != null
                ? String(d.enterpriseUser.companyOfficeZip)
                : "",
            companyOfficePremiseType:
              d.enterpriseUser?.companyOfficePremiseType || "",
            companyOfficeYearsInThisLocation:
              d.enterpriseUser?.companyOfficeYearsInThisLocation || "",
            // Change #4
            companyOfficePremiseOwner:
              d.enterpriseUser?.companyOfficePremiseOwner || "",
            companyOfficeAreaZoned:
              d.enterpriseUser?.companyOfficeAreaZoned || "",
            companyOfficeSquareFootage:
              d.enterpriseUser?.companyOfficeSquareFootage || "",
            revenue: d.enterpriseUser?.revenue || "",
            profit: d.enterpriseUser?.profit || "",
            companyAssets: d.enterpriseUser?.companyAssets || "",
            companyNetWorth: d.enterpriseUser?.companyNetWorth || "",
            accountPurpose: d.enterpriseUser?.accountPurpose || "",
            investmentSource: d.enterpriseUser?.investmentSource || "",
            transactionVolumes: d.enterpriseUser?.transactionVolumes || "",
            transactionFrequency: d.enterpriseUser?.transactionFrequency || "",
            relationWithBank: d.enterpriseUser?.relationWithBank || "",
            // Change #1: percentage-based processing
            isProcessingCardTransaction:
              d.enterpriseUser?.isProcessingCardTransaction != null
                ? String(d.enterpriseUser.isProcessingCardTransaction)
                : "",
            cardProcessingMethodPercentages: d.enterpriseUser
              ? {
                Online: d.enterpriseUser?.onlineTxnPct,
                "In person swipe": d.enterpriseUser?.inPersonSwipeTxnPct,
                "Over the phone": d.enterpriseUser?.overThePhoneTxnPct,
                "Key entered": d.enterpriseUser?.keyEnteredTxnPct,
              }
              : {
                Online: "",
                "In person swipe": "",
                "Over the phone": "",
                "Key entered": "",
              },
            transactionLimitInUsd:
              d.enterpriseUser?.transactionLimitInUsd != null
                ? String(d.enterpriseUser.transactionLimitInUsd)
                : "",
            averageTransactionSizeInUsd:
              d.enterpriseUser?.averageTransactionSizeInUsd != null
                ? String(d.enterpriseUser.averageTransactionSizeInUsd)
                : "",
            highestTransactionSizeInUsd:
              d.enterpriseUser?.highestTransactionSizeInUsd != null
                ? String(d.enterpriseUser.highestTransactionSizeInUsd)
                : "",
            acceptAchPayment:
              d.enterpriseUser?.acceptAchPayment != null
                ? String(d.enterpriseUser.acceptAchPayment)
                : "",
            // Change #2
            acceptAmexPayment: !!d.enterpriseUser?.acceptAmexPayment,
            amexMonthlyVolume: d.enterpriseUser?.amexMonthlyVolumeInUsd || "",
            amexAvgTicket: d.enterpriseUser?.amexAvgTicketInUsd || "",
            amexHighTicket: d.enterpriseUser?.amexHighestTicketInUsd || "",
            // Change #3
            paymentProcessingUrl:
              d.enterpriseUser?.paymentProcessingWebsiteUrl || "",
            demoLoginUsername: d.enterpriseUser?.demoLoginUsername || "",
            demoLoginPassword: d.enterpriseUser?.demoLoginPassword || "",
            // Change #7
            advertiseMethods: d.enterpriseUser?.advertiseType
              ? Array.isArray(d.enterpriseUser.advertiseType)
                ? d.enterpriseUser.advertiseType
                : d.enterpriseUser.advertiseType.split(",")
              : [],
            inboundPercent: d.enterpriseUser?.inboundPct || "",
            outboundPercent: d.enterpriseUser?.outboundPct || "",
            b2bPercent: d.enterpriseUser?.b2bPct || "",
            retailPercent: d.enterpriseUser?.retailPct || "",
            isBusinessSeasonal:
              d.enterpriseUser?.isBusinessSeasonal != null
                ? String(d.enterpriseUser.isBusinessSeasonal)
                : "",
            peakOffSeasonDescription: d.enterpriseUser?.seasonalityDesc || "",
            returnRefundPolicy: d.enterpriseUser?.returnRefundPolicyLink || "",
            refundDaysAfterPurchase:
              d.enterpriseUser?.refundRequestWindow || "",
            refundProcessingDays: d.enterpriseUser?.refundProcessWindow || "",
            // Change #8
            cardChargedWhen: d.enterpriseUser?.cardChargeTiming || "",
            deliveryDaysAfterCharge:
              d.enterpriseUser?.serviceDeliveryTimeDays || "",
            usesThirdPartyFulfillment:
              d.enterpriseUser?.usesThirdPartyFulfillment != null
                ? String(d.enterpriseUser.usesThirdPartyFulfillment)
                : "",
            thirdPartyFulfillmentName:
              d.enterpriseUser?.thirdPartyCompanyName || "",
            // Change #9
            isPciCompliant:
              d.enterpriseUser?.isPciCompliant != null
                ? String(d.enterpriseUser.isPciCompliant)
                : "",
            wasTerminatedMerchant:
              d.enterpriseUser?.previouslyTerminatedByCardNetwork != null
                ? String(d.enterpriseUser.previouslyTerminatedByCardNetwork)
                : "",
            hasDataCompromise:
              d.enterpriseUser?.dataCompromiseInvestigationHistory != null
                ? String(d.enterpriseUser.dataCompromiseInvestigationHistory)
                : "",
            wasVisaRiskProgram:
              d.enterpriseUser?.identifiedInVisaRiskPrograms != null
                ? String(d.enterpriseUser.identifiedInVisaRiskPrograms)
                : "",
            usesThirdPartySoftware:
              d.enterpriseUser?.thirdPartyPaymentParticipation != null
                ? String(d.enterpriseUser.thirdPartyPaymentParticipation)
                : "",
            thirdPartySoftwareNames:
              d.enterpriseUser?.thirdPartyPlatformName || "",
            bankruptcyType: d.enterpriseUser?.bankruptcyStatus || "",
            bankruptcyFilingDate: d.enterpriseUser?.bankruptcyFillingDate || "",
            bankruptcyExplanation: d.enterpriseUser?.incidentDetails || "",
            beneficialOwners: d.enterpriseUser?.enterpriseOwners || [],
          },
          () => {
            this.resolveBusinessActivityLabel();
          }
        );
      })
      .catch((err) => {
        this.setState({ showLoader: false });
        showHttpError(err);
      });
  };

  /* ==== FACE API ==== */

  loadFaceApiModels = async () => {
    try {
      const faceapi = await import("face-api.js");
      const modelPath = process.env.PUBLIC_URL + "/models";
      await faceapi.nets.tinyFaceDetector.loadFromUri(modelPath);
      await faceapi.nets.faceLandmark68Net.loadFromUri(modelPath);
      this.faceapi = faceapi;
      this.faceApiLoaded = true;
    } catch (err) {
      this.faceApiLoaded = false;
    }
  };

  detectFace = async () => {
    if (!this.faceApiLoaded || !this.faceapi || !this.webcamRef.current?.video)
      return;
    const video = this.webcamRef.current.video;
    if (video.readyState !== 4) return;
    try {
      const detections = await this.faceapi
        .detectAllFaces(video, new this.faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks();
      const canvas = this.canvasRef.current;
      if (canvas) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const displaySize = {
          width: video.videoWidth,
          height: video.videoHeight,
        };
        this.faceapi.matchDimensions(canvas, displaySize);
        const resizedDetections = this.faceapi.resizeResults(
          detections,
          displaySize
        );
        const ctx = canvas.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        this.faceapi.draw.drawDetections(canvas, resizedDetections);
        this.faceapi.draw.drawFaceLandmarks(canvas, resizedDetections);
      }
      if (detections.length > 0) {
        this.setState({ faceDetected: true });
        this.lastDetectedTime = Date.now();
      } else {
        if (Date.now() - this.lastDetectedTime > 2000)
          this.setState({ faceDetected: false });
      }
    } catch (err) {
      console.error("Error detecting face:", err);
    }
  };

  startFaceDetection = () => {
    if (this.detectInterval) clearInterval(this.detectInterval);
    this.detectInterval = setInterval(this.detectFace, 300);
  };
  stopFaceDetection = () => {
    if (this.detectInterval) {
      clearInterval(this.detectInterval);
      this.detectInterval = null;
    }
  };

  /* ==== VALIDATION ==== */

  validateFileSize = (file) => {
    if (file && file.size > MAX_FILE_SIZE) {
      showToast("error", "File size must be less than 6MB");
      return false;
    }
    return true;
  };

  validateFileType = (file, allowedTypes) => {
    const fileExt = "." + file.name.split(".").pop().toLowerCase();
    const allowed = allowedTypes.split(",").map((t) => t.trim().toLowerCase());
    if (!allowed.includes(fileExt)) {
      showToast("error", `Invalid file type. Allowed: ${allowedTypes}`);
      return false;
    }
    return true;
  };

  sanitizeFileName = (fileName) => {
    const nameParts = fileName.split(".");
    const extension = nameParts.pop();
    const nameWithoutExt = nameParts.join(".");
    const sanitized = nameWithoutExt.replace(/[^\w-]/g, "_");
    return `${sanitized}.${extension}`;
  };

  /* Change #1: validate that all 4 processing method percentages add up to 100 */
  validateCardProcessingPercentages = () => {
    const percs = this.state.cardProcessingMethodPercentages;
    const total = Object.values(percs).reduce(
      (sum, v) => sum + (parseFloat(v) || 0),
      0
    );
    return Math.abs(total - 100) < 0.01;
  };

  validateInboundOutboundPercentages = () => {
    const total =
      (parseFloat(this.state.inboundPercent) || 0) +
      (parseFloat(this.state.outboundPercent) || 0);
    return Math.abs(total - 100) < 0.01;
  };

  validateB2BRetailPercentages = () => {
    const total =
      (parseFloat(this.state.b2bPercent) || 0) +
      (parseFloat(this.state.retailPercent) || 0);
    return Math.abs(total - 100) < 0.01;
  };

  validateCurrentStep = () => {
    let errors = {};
    /* if (this.state.kycType === "individual") {
      switch (this.state.activeStep) {
        case 0:
          if (!this.state.houseNo.trim()) errors.houseNo = "Required";
          if (!this.state.streetArea.trim()) errors.streetArea = "Required";
          if (!this.state.city.trim()) errors.city = "Required";
          if (!this.state.stateProvince.trim())
            errors.stateProvince = "Required";
          if (!this.state.country) errors.country = "Required";
          if (!this.state.postalCode.trim()) errors.postalCode = "Required";
          break;
        case 1:
          if (!this.state.dobMonth || !this.state.dobDay || !this.state.dobYear)
            errors.dob = "Required";
          else if (this.state.dobYear.length < 4)
            errors.dob = "Enter valid year (YYYY)";
          if (!this.state.birthPlace.trim()) errors.birthPlace = "Required";
          if (!this.state.ssn.trim()) {
            errors.ssn = "SSN or Passport is required";
          } else if (this.state.ssn.trim().length < 4) {
            errors.ssn = "Must be at least 4 characters";
          } else if (this.state.ssn.trim().length > 15) {
            errors.ssn = "Must be not more than 15 characters";
          }
          break;
        case 2:
          if (!this.state.industry) errors.industry = "Required";
          if (!this.state.occupation) errors.occupation = "Required";
          if (!this.state.sourceOfFunds) errors.sourceOfFunds = "Required";
          if (!this.state.monthlyTransactionVolume)
            errors.monthlyTransactionVolume = "Required";
          if (!this.state.annualIncome) errors.annualIncome = "Required";
          if (!this.state.netWorth) errors.netWorth = "Required";
          if (!this.state.employmentCategory)
            errors.employmentCategory = "Required";
          if (!this.state.employmentType) errors.employmentType = "Required";
          if (!this.state.pepStatus) errors.pepStatus = "Required";
          if (!this.state.bankingPartner.trim())
            errors.bankingPartner = "Required";
          if (!this.state.bankingDuration) errors.bankingDuration = "Required";
          if (!this.state.purposeAccount) errors.purposeAccount = "Required";
          if (!this.state.thirdParty) errors.thirdParty = "Required";
          break;
        case 3:
          if (!this.state.addressProofFile)
            errors.addressProofFile = "Required";
          break;
        case 4:
          if (!this.state.idDocumentFrontFile)
            errors.idDocumentFrontFile = "Required";
          if (!this.state.idDocumentBackFile)
            errors.idDocumentBackFile = "Required";
          break;
        default:
          break;
      }
    } else { */
    switch (this.state.activeStep) {
      case 0:
        if (!this.state.companyName.trim()) errors.companyName = "Required";
        if (!this.state.companyRegNumber.trim())
          errors.companyRegNumber = "Required";
        if (!this.state.incorporationCountry)
          errors.incorporationCountry = "Required";
        if (!this.state.corporateStructure)
          errors.corporateStructure = "Required";
        if (!this.state.businessActivity.trim())
          errors.businessActivity = "Required";
        if (!this.state.dbaName.trim()) errors.dbaName = "Required";
        if (!this.state.businessPhoneNumber.trim())
          errors.businessPhoneNumber = "Required";
        else if (!PHONE_REGEX.test(this.state.businessPhoneNumber.trim()))
          errors.businessPhoneNumber = "Enter a valid phone number";
        if (!this.state.businessEmailAddress.trim())
          errors.businessEmailAddress = "Required";
        else if (!EMAIL_REGEX.test(this.state.businessEmailAddress.trim()))
          errors.businessEmailAddress = "Enter a valid email";
        if (!this.state.yearsInBusiness) errors.yearsInBusiness = "Required";
        if (!this.state.businessDescription.trim())
          errors.businessDescription = "Required";
        // Change #6
        if (!this.state.taxIdType) errors.taxIdType = "Required";
        if (!this.state.taxpayerIdNumber.trim()) {
          errors.taxpayerIdNumber = "Required";
        } else if (
          this.state.taxpayerIdNumber.length < 8 ||
          this.state.taxpayerIdNumber.length > 20
        ) {
          errors.taxpayerIdNumber = "Must be between 8 and 20 characters";
        } else if (!/^[a-zA-Z0-9]+$/.test(this.state.taxpayerIdNumber)) {
          errors.taxpayerIdNumber = "Only alphanumeric characters allowed";
        }

        if (!this.state.isExemptPayee) errors.isExemptPayee = "Required";
        if (!this.state.isNonprofit) errors.isNonprofit = "Required";

        // Change #5: Stock Exchange
        if (this.state.stockExchangeName) {
          if (
            this.state.stockExchangeName.length < 2 ||
            this.state.stockExchangeName.length > 100
          ) {
            errors.stockExchangeName = "Must be between 2 and 100 characters";
          } else if (
            !/^[a-zA-Z0-9 .\-]+$/.test(this.state.stockExchangeName)
          ) {
            errors.stockExchangeName =
              "Only letters, numbers, spaces and . - allowed";
          }
        }
        if (this.state.stockTickerSymbol) {
          if (
            this.state.stockTickerSymbol.length < 1 ||
            this.state.stockTickerSymbol.length > 10
          ) {
            errors.stockTickerSymbol = "Must be between 1 and 10 characters";
          } else if (!/^[A-Z0-9]+$/.test(this.state.stockTickerSymbol)) {
            errors.stockTickerSymbol =
              "Only uppercase alphanumeric characters allowed";
          }
        }

        // Change #7: Business Operations
        if (
          !this.state.advertiseMethods ||
          this.state.advertiseMethods.length === 0
        )
          errors.advertiseMethods = "Required";
        if (!this.state.inboundPercent) errors.inboundPercent = "Required";
        if (!this.state.outboundPercent) errors.outboundPercent = "Required";
        if (
          this.state.inboundPercent &&
          this.state.outboundPercent &&
          !this.validateInboundOutboundPercentages()
        ) {
          errors.inboundOutboundPercentages =
            "Inbound and Outbound percentages must add up to 100%";
        }

        if (!this.state.b2bPercent) errors.b2bPercent = "Required";
        if (!this.state.retailPercent) errors.retailPercent = "Required";
        if (
          this.state.b2bPercent &&
          this.state.retailPercent &&
          !this.validateB2BRetailPercentages()
        ) {
          errors.b2bRetailPercentages =
            "B2B and Retail percentages must add up to 100%";
        }

        if (!this.state.isBusinessSeasonal)
          errors.isBusinessSeasonal = "Required";
        if (
          this.state.isBusinessSeasonal === "1" &&
          !this.state.peakOffSeasonDescription.trim()
        )
          errors.peakOffSeasonDescription = "Required";

        if (!this.state.returnRefundPolicy.trim()) {
          errors.returnRefundPolicy = "Required";
        } else if (
          !RETURN_REFUND_POLICY_REGEX.test(
            this.state.returnRefundPolicy.trim()
          )
        ) {
          errors.returnRefundPolicy =
            "Enter a valid URL (e.g., https://example.com/policy)";
        }

        if (!this.state.refundDaysAfterPurchase)
          errors.refundDaysAfterPurchase = "Required";
        if (!this.state.refundProcessingDays)
          errors.refundProcessingDays = "Required";
        // Change #8: Fulfillment & Billing
        if (!this.state.cardChargedWhen) errors.cardChargedWhen = "Required";
        if (!this.state.usesThirdPartyFulfillment)
          errors.usesThirdPartyFulfillment = "Required";
        // Change #9: Risk & Compliance
        if (!this.state.isPciCompliant) errors.isPciCompliant = "Required";
        if (!this.state.wasTerminatedMerchant)
          errors.wasTerminatedMerchant = "Required";
        if (!this.state.hasDataCompromise)
          errors.hasDataCompromise = "Required";
        if (!this.state.wasVisaRiskProgram)
          errors.wasVisaRiskProgram = "Required";
        if (!this.state.usesThirdPartySoftware)
          errors.usesThirdPartySoftware = "Required";
        if (!this.state.bankruptcyType) errors.bankruptcyType = "Required";
        else if (this.state.bankruptcyType !== "Never Filed for Bankruptcy") {
          if (!this.state.bankruptcyExplanation.trim()) {
            errors.bankruptcyExplanation = "Required";
          } else if (this.state.bankruptcyExplanation.trim().length < 10) {
            errors.bankruptcyExplanation = "Minimum 10 characters required";
          }
        }
        // Registered address
        if (!this.state.companyregAddress.trim())
          errors.companyregAddress = "Required";
        if (!this.state.companyregCity.trim())
          errors.companyregCity = "Required";
        if (!this.state.companyregState.trim())
          errors.companyregState = "Required";
        if (!this.state.companyregCountry)
          errors.companyregCountry = "Required";
        if (!this.state.companyregZip.trim())
          errors.companyregZip = "Required";

        if (this.state.companyWebsite.trim()) {
          const webVal = this.state.companyWebsite.trim();
          if (!webVal.includes(".")) {
            errors.companyWebsite = "Enter a valid domain (e.g. example.com)";
          } else {
            try {
              let urlToValidate = webVal;
              if (
                !urlToValidate.startsWith("http://") &&
                !urlToValidate.startsWith("https://")
              ) {
                urlToValidate = "https://" + urlToValidate;
              }
              new URL(urlToValidate);
            } catch (_) {
              errors.companyWebsite =
                "Enter a valid URL (e.g. https://example.com or example.com)";
            }
          }
        }
        if (!this.state.companyRegPremiseType)
          errors.companyRegPremiseType = "Required";
        if (!this.state.companyRegYearsInThisLocation)
          errors.companyRegYearsInThisLocation = "Required";
        if (!this.state.companyRegPremiseOwner.trim())
          errors.companyRegPremiseOwner = "Required";
        if (!this.state.companyRegAreaZoned.trim())
          errors.companyRegAreaZoned = "Required";

        const regSqFt = parseFloat(this.state.companyRegSquareFootage);
        if (!this.state.companyRegSquareFootage) {
          errors.companyRegSquareFootage = "Required";
        } else if (isNaN(regSqFt) || regSqFt <= 0 || regSqFt > 1000000) {
          errors.companyRegSquareFootage =
            "Must be a number between 1 and 1,000,000";
        }

        const numLocs = parseInt(this.state.numberOfLocations);
        if (!this.state.numberOfLocations) {
          errors.numberOfLocations = "Required";
        } else if (isNaN(numLocs) || numLocs < 1 || numLocs > 10000) {
          errors.numberOfLocations =
            "Must be an integer between 1 and 10,000";
        }

        // Office address (if different)
        if (!this.state.sameAddress) {
          if (!this.state.companyOfficeAddress?.trim())
            errors.companyOfficeAddress = "Required";
          if (!this.state.companyOfficeCity?.trim())
            errors.companyOfficeCity = "Required";
          if (!this.state.companyOfficeState?.trim())
            errors.companyOfficeState = "Required";
          if (!this.state.companyOfficeCountry)
            errors.companyOfficeCountry = "Required";
          if (!this.state.companyOfficeZip?.trim())
            errors.companyOfficeZip = "Required";
          if (!this.state.companyOfficePremiseType)
            errors.companyOfficePremiseType = "Required";
          if (!this.state.companyOfficeYearsInThisLocation)
            errors.companyOfficeYearsInThisLocation = "Required";
          if (!this.state.companyOfficePremiseOwner?.trim())
            errors.companyOfficePremiseOwner = "Required";
          if (!this.state.companyOfficeAreaZoned?.trim())
            errors.companyOfficeAreaZoned = "Required";

          const officeSqFt = parseFloat(
            this.state.companyOfficeSquareFootage
          );
          if (!this.state.companyOfficeSquareFootage?.trim()) {
            errors.companyOfficeSquareFootage = "Required";
          } else if (
            isNaN(officeSqFt) ||
            officeSqFt <= 0 ||
            officeSqFt > 1000000
          ) {
            errors.companyOfficeSquareFootage =
              "Must be a number between 1 and 1,000,000";
          }
        }
        // Financial
        if (!this.state.revenue) errors.revenue = "Required";
        if (!this.state.profit) errors.profit = "Required";
        if (!this.state.companyAssets) errors.companyAssets = "Required";
        if (!this.state.companyNetWorth) errors.companyNetWorth = "Required";
        if (!this.state.accountPurpose) errors.accountPurpose = "Required";
        if (!this.state.investmentSource)
          errors.investmentSource = "Required";
        if (!this.state.transactionVolumes)
          errors.transactionVolumes = "Required";
        if (!this.state.transactionFrequency)
          errors.transactionFrequency = "Required";

        if (!this.state.bankingPartner.trim()) {
          errors.bankingPartner = "Required";
        } else if (
          this.state.bankingPartner.length < 2 ||
          this.state.bankingPartner.length > 100
        ) {
          errors.bankingPartner = "Must be between 2 and 100 characters";
        } else if (!/^[a-zA-Z0-9 .\-]+$/.test(this.state.bankingPartner)) {
          errors.bankingPartner =
            "Only letters, numbers, spaces and . - allowed";
        }

        if (!this.state.relationWithBank)
          errors.relationWithBank = "Required";
        // Processing Information
        if (!this.state.isProcessingCardTransaction)
          errors.isProcessingCardTransaction = "Required";
        // Change #1: validate percentages add to 100
        if (!this.validateCardProcessingPercentages())
          errors.cardProcessingPercentages =
            "All four percentages must add up to 100%";
        if (!this.state.transactionLimitInUsd.trim())
          errors.transactionLimitInUsd = "Required";
        if (!this.state.averageTransactionSizeInUsd.trim())
          errors.averageTransactionSizeInUsd = "Required";
        if (!this.state.highestTransactionSizeInUsd.trim())
          errors.highestTransactionSizeInUsd = "Required";
        if (!this.state.acceptAchPayment)
          errors.acceptAchPayment = "Required";

        // Change #2: Amex
        if (this.state.acceptAmexPayment) {
          const amexVol = parseFloat(this.state.amexMonthlyVolume);
          if (!this.state.amexMonthlyVolume) {
            errors.amexMonthlyVolume = "Required";
          } else if (isNaN(amexVol) || amexVol < 0 || amexVol > 100000000) {
            errors.amexMonthlyVolume = "Must be a number up to 100,000,000";
          }

          const amexAvg = parseFloat(this.state.amexAvgTicket);
          if (!this.state.amexAvgTicket) {
            errors.amexAvgTicket = "Required";
          } else if (isNaN(amexAvg) || amexAvg <= 0 || amexAvg > 100000000) {
            errors.amexAvgTicket =
              "Must be a number between 1 and 100,000,000";
          }

          const amexHigh = parseFloat(this.state.amexHighTicket);
          if (!this.state.amexHighTicket) {
            errors.amexHighTicket = "Required";
          } else if (
            isNaN(amexHigh) ||
            amexHigh <= 0 ||
            amexHigh > 100000000
          ) {
            errors.amexHighTicket =
              "Must be a number between 1 and 100,000,000";
          } else if (amexHigh < amexAvg) {
            errors.amexHighTicket =
              "High ticket must be greater than or equal to average ticket";
          }
        }

        // Change #3
        if (!this.state.paymentProcessingUrl.trim()) {
          errors.paymentProcessingUrl = "Required";
        } else {
          const urlVal = this.state.paymentProcessingUrl.trim();
          if (!urlVal.includes(".")) {
            errors.paymentProcessingUrl =
              "Enter a valid domain (e.g. example.com)";
          } else {
            try {
              let urlToValidate = urlVal;
              if (
                !urlToValidate.startsWith("http://") &&
                !urlToValidate.startsWith("https://")
              ) {
                urlToValidate = "https://" + urlToValidate;
              }
              new URL(urlToValidate);
            } catch (_) {
              errors.paymentProcessingUrl =
                "Enter a valid URL (e.g. https://example.com or example.com)";
            }
          }
        }

        if (this.state.demoLoginUsername && this.state.demoLoginUsername.trim()) {
          if (
            this.state.demoLoginUsername.length < 2 ||
            this.state.demoLoginUsername.length > 30
          ) {
            errors.demoLoginUsername = "Must be between 2 and 30 characters";
          } else if (!/^[a-zA-Z0-9_.\-]+$/.test(this.state.demoLoginUsername)) {
            errors.demoLoginUsername = "Only alphanumeric and _ . - allowed";
          }
        }

        if (this.state.demoLoginPassword && this.state.demoLoginPassword.trim()) {
          const pwd = this.state.demoLoginPassword;
          if (pwd.length < 8 || pwd.length > 35) {
            errors.demoLoginPassword = "Must be between 8 and 35 characters";
          } else if (
            !/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@.!])/.test(pwd)
          ) {
            errors.demoLoginPassword =
              "Must contain uppercase, lowercase, number and special char ($@.!)";
          }
        }
        break;
      case 1:
        if (!this.state.incorporationCertificate)
          errors.incorporationCertificate = "Required";
        if (!this.state.addressProofFile)
          errors.addressProofFile = "Required";
        if (!this.state.companyBankStatement)
          errors.companyBankStatement = "Required";
        break;
      case 2:
        // Authorization Letter is now optional
        break;
      default:
        break;
    }
    // }
    this.setState({ errors });
    return Object.keys(errors).length === 0;
  };

  validateEnterpriseDocuments = () => {
    const requiredDocs = [
      "incorporationCertificate",
      "addressProofFile",
      "companyBankStatement",
    ];
    const missingDocs = requiredDocs.filter((doc) => !this.state[doc]);
    if (missingDocs.length > 0) {
      showToast(
        "error",
        `Missing required documents: ${missingDocs.join(", ")}`
      );
      return false;
    }
    return true;
  };

  handleFileChange = (
    e,
    fileKey,
    fileNameKey,
    allowedTypes = ALLOWED_DOC_TYPES
  ) => {
    const file = e.target.files[0];
    const { errors } = this.state;

    // Clear existing error for this field
    if (errors[fileKey]) {
      delete errors[fileKey];
    }

    if (!file) {
      this.setState({ [fileKey]: null, [fileNameKey]: "", errors });
      return;
    }

    if (!this.validateFileSize(file)) {
      e.target.value = "";
      errors[fileKey] = "File size must be less than 6MB";
      this.setState({ [fileKey]: null, [fileNameKey]: "", errors });
      return;
    }
    if (!this.validateFileType(file, allowedTypes)) {
      e.target.value = "";
      errors[fileKey] = `Invalid file type. Allowed: ${allowedTypes}`;
      this.setState({ [fileKey]: null, [fileNameKey]: "", errors });
      return;
    }
    const sanitizedName = this.sanitizeFileName(file.name);
    this.setState({ [fileKey]: file, [fileNameKey]: sanitizedName, errors });
  };

  /* ==== TYPE SELECTION ==== */
  selectKycType = (type) => {
    this.setState({
      kycType: type,
      showTypeSelection: false,
      activeStep: 0,
      errors: {},
    });
  };
  handleChangeType = () => {
    this.setState({
      showTypeSelection: true,
      activeStep: 0,
      errors: {},
      kycType: null,
    });
  };

  /* ==== DOB HELPERS ==== */
  formatDobForPayload = () => {
    const { dobMonth, dobDay, dobYear } = this.state;
    if (dobMonth && dobDay && dobYear) {
      return `${dobMonth.padStart(2, "0")}-${dobDay.padStart(
        2,
        "0"
      )}-${dobYear}`;
    }
    return "";
  };
  handleDobChange = (field, value) => {
    this.setState({ [field]: value.replace(/[^0-9]/g, "") });
  };

  /* ==== Change #1: Handle card processing method percentage change ==== */
  handleCardProcessingPercentageChange = (method, value) => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    this.setState((prev) => ({
      cardProcessingMethodPercentages: {
        ...prev.cardProcessingMethodPercentages,
        [method]: cleaned,
      },
    }));
  };

  /* ==== Change #7: Advertise methods multi-select ==== */
  handleAdvertiseMethodToggle = (value) => {
    this.setState((prev) => {
      const methods = prev.advertiseMethods || [];
      if (methods.includes(value)) {
        return { advertiseMethods: methods.filter((m) => m !== value) };
      } else {
        return { advertiseMethods: [...methods, value] };
      }
    });
  };

  buildIndividualUserPayload = () => {
    const completeData = this.state.completeUserData || {};
    return {
      ...completeData,
      firstName: this.state.firstName.trim(),
      middleName: this.state.middleName.trim() || null,
      lastName: this.state.lastName.trim(),
      address: `${this.state.houseNo.trim()},${this.state.streetArea.trim()}`,
      city: this.state.city.trim(),
      state: this.state.stateProvince.trim(),
      country: this.state.country,
      zip: this.state.postalCode.trim(),
      uuid: this.state.userUuid || getAuthUuid(),
      userDocsStatus: "0",
      dob: this.formatDobForPayload(),
      ssn: this.state.ssn,
      birthPlace: this.state.birthPlace.trim(),
      industry: this.state.industry,
      occupation: this.state.occupation,
      sourcesOfFunds: this.state.sourceOfFunds,
      txnVolumeId: this.state.monthlyTransactionVolume
        ? parseInt(this.state.monthlyTransactionVolume)
        : 0,
      annualIncomeId: this.state.annualIncome
        ? parseInt(this.state.annualIncome)
        : 0,
      netWorthId: this.state.netWorth ? parseInt(this.state.netWorth) : 0,
      empCategory: this.state.employmentCategory,
      empType: this.state.employmentType,
      pep: parseInt(this.state.pepStatus) || 0,
      bankingPartner: this.state.bankingPartner.trim(),
      relationWithBank: this.state.bankingDuration,
      accountPurpose: this.state.purposeAccount,
      thirdParty: parseInt(this.state.thirdParty) || 0,
      panNo: this.state.panNumber,
    };
  };

  buildIndividualFormData = () => {
    const fd = new FormData();
    fd.append("user", JSON.stringify(this.buildIndividualUserPayload()));
    fd.append(
      "adminUser",
      localStorage.getItem(SITENAMEALIAS + "_merchant_id")
    );
    if (this.state.panCardPhotoFile)
      fd.append("panFront", this.state.panCardPhotoFile);
    if (this.state.addressProofFile)
      fd.append("addressProof", this.state.addressProofFile);
    if (this.state.idDocumentFrontFile)
      fd.append("idProofFront", this.state.idDocumentFrontFile);
    if (this.state.idDocumentBackFile)
      fd.append("idProofBack", this.state.idDocumentBackFile);
    if (this.state.capturedPhotoBlob)
      fd.append("userPhoto", this.state.capturedPhotoBlob, "selfie.jpg");
    return fd;
  };

  /* ==== SAVE STEP ==== */
  handleSaveAndNext = () => {
    if (!this.validateCurrentStep()) {
      showToast("error", "Please fill in all required fields");
      return;
    }
    this.saveCurrentStep();
  };
  handleBack = () => {
    this.setState((prev) => ({ activeStep: prev.activeStep - 1, errors: {} }));
  };

  saveCurrentStep = () => {
    this.setState({ showLoader: true });
    /* if (this.state.kycType === "individual") {
      switch (this.state.activeStep) {
        case 0:
          this.saveAddressStep();
          return;
        case 1:
          this.saveDobStep();
          return;
        case 2:
          this.saveEmploymentStep();
          return;
        case 3:
          this.setState((prev) => ({
            showLoader: false,
            activeStep: prev.activeStep + 1,
            errors: {},
          }));
          return;
        case 4:
          this.setState((prev) => ({
            showLoader: false,
            activeStep: prev.activeStep + 1,
            errors: {},
          }));
          return;
        case 5:
          this.submitFinalIndividualKyc();
          return;
        default:
          break;
      }
      return;
    } */
    switch (this.state.activeStep) {
      case 0:
        this.saveEnterpriseInfo();
        return;
      case 1:
        this.saveEnterpriseDocuments();
        return;
      case 2:
        this.saveAuthorizationLetter();
        return;
      case 3:
        this.submitFinalEnterpriseKyc();
        return;
      default:
        break;
    }
  };

  saveAddressStep = () => {
    const completeData = this.state.completeUserData || {};
    const payload = {
      ...completeData,
      uuid: this.state.userUuid || getAuthUuid(),
      address: `${this.state.houseNo.trim()},${this.state.streetArea.trim()}`,
      city: this.state.city.trim(),
      state: this.state.stateProvince.trim(),
      country: this.state.country,
      zip: this.state.postalCode.trim(),
    };
    saveUserKycData(payload)
      .then((res) => {
        this.setState({ showLoader: false });
        if (res.data?.error?.error_data === 0) {
          this.setState((prev) => ({
            completeUserData: {
              ...prev.completeUserData,
              address: payload.address,
              city: payload.city,
              state: payload.state,
              country: payload.country,
              zip: payload.zip,
            },
            activeStep: prev.activeStep + 1,
            errors: {},
          }));
          showToast("success", "Address saved successfully");
        } else {
          showToast(
            "error",
            res.data?.error?.error_msg || "Failed to save address"
          );
        }
      })
      .catch((err) => {
        this.setState({ showLoader: false });
        showHttpError(err);
      });
  };

  saveDobStep = () => {
    const completeData = this.state.completeUserData || {};
    const payload = {
      ...completeData,
      uuid: this.state.userUuid || getAuthUuid(),
      dob: this.formatDobForPayload(),
      ssn: this.state.ssn,
      birthPlace: this.state.birthPlace.trim(),
    };
    saveUserKycData(payload)
      .then((res) => {
        this.setState({ showLoader: false });
        if (res.data?.error?.error_data === 0) {
          this.setState((prev) => ({
            completeUserData: {
              ...prev.completeUserData,
              dob: payload.dob,
              ssn: payload.ssn,
              birthPlace: payload.birthPlace,
            },
            activeStep: prev.activeStep + 1,
            errors: {},
          }));
          showToast("success", "DOB saved successfully");
        } else {
          showToast(
            "error",
            res.data?.error?.error_msg || "Failed to save DOB"
          );
        }
      })
      .catch((err) => {
        this.setState({ showLoader: false });
        showHttpError(err);
      });
  };

  saveEmploymentStep = () => {
    const completeData = this.state.completeUserData || {};
    const payload = {
      ...completeData,
      industry: this.state.industry,
      occupation: this.state.occupation,
      sourcesOfFunds: this.state.sourceOfFunds,
      txnVolumeId: this.state.monthlyTransactionVolume
        ? parseInt(this.state.monthlyTransactionVolume)
        : 0,
      annualIncomeId: this.state.annualIncome
        ? parseInt(this.state.annualIncome)
        : 0,
      netWorthId: this.state.netWorth ? parseInt(this.state.netWorth) : 0,
      empCategory: this.state.employmentCategory,
      empType: this.state.employmentType,
      pep: parseInt(this.state.pepStatus) || 0,
      bankingPartner: this.state.bankingPartner.trim(),
      relationWithBank: this.state.bankingDuration,
      accountPurpose: this.state.purposeAccount,
      thirdParty: parseInt(this.state.thirdParty) || 0,
      uuid: this.state.userUuid || getAuthUuid(),
    };
    saveUserKycData(payload)
      .then((res) => {
        this.setState({ showLoader: false });
        if (res.data?.error?.error_data === 0) {
          this.setState((prev) => ({
            completeUserData: { ...prev.completeUserData, ...payload },
            activeStep: prev.activeStep + 1,
            errors: {},
          }));
          showToast("success", "Employment details saved");
        } else {
          showToast(
            "error",
            res.data?.error?.error_msg || "Failed to save employment details"
          );
        }
      })
      .catch((err) => {
        this.setState({ showLoader: false });
        showHttpError(err);
      });
  };

  submitFinalIndividualKyc = () => {
    const fd = this.buildIndividualFormData();
    addUserKYCDetails(fd)
      .then((res) => {
        const data = res.data;
        if (data?.error?.error_data === 0) {
          const adminUser = localStorage.getItem(
            SITENAMEALIAS + "_merchant_id"
          );
          const uuid = this.state.userUuid || getAuthUuid();
          finishKyc({ adminUser, uuid })
            .then((finishRes) => {
              this.setState({ showLoader: false });
              if (finishRes.data?.error?.error_data === 0) {
                showToast("success", "KYC submitted successfully!");
                this.setState({ kycStatus: 0 });
                setTimeout(() => window.location.reload(), 3000);
              } else {
                showToast(
                  "error",
                  finishRes.data?.error?.error_msg || "Failed to finish KYC"
                );
              }
            })
            .catch((err) => {
              this.setState({ showLoader: false });
              showHttpError(err);
            });
        } else {
          this.setState({ showLoader: false });
          showToast("error", data?.error?.error_msg || "Failed to submit KYC");
        }
      })
      .catch((err) => {
        this.setState({ showLoader: false });
        showHttpError(err);
      });
  };

  /* ==== ENTERPRISE SAVE METHODS ==== */

  buildEnterprisePayload = () => ({
    companyName: this.state.companyName,
    companyRegNo: this.state.companyRegNumber,
    incorporationCountry: this.state.incorporationCountry,
    companyWebsite: this.state.companyWebsite,
    dbaName: this.state.dbaName,
    businessPhoneNumber: this.state.businessPhoneNumber,
    businessEmailAddress: this.state.businessEmailAddress,
    yearsInBusiness: this.state.yearsInBusiness,
    businessDescription: this.state.businessDescription,
    // Change #5
    stockExchangeName: this.state.stockExchangeName,
    stockTickerSymbol: this.state.stockTickerSymbol,

    isExemptPayee: this.state.isExemptPayee,

    // New Fields for API
    onlineTxnPct: Number(
      this.state.cardProcessingMethodPercentages.Online || 0
    ),
    inPersonSwipeTxnPct: Number(
      this.state.cardProcessingMethodPercentages["In person swipe"] || 0
    ),
    overThePhoneTxnPct: Number(
      this.state.cardProcessingMethodPercentages["Over the phone"] || 0
    ),
    keyEnteredTxnPct: Number(
      this.state.cardProcessingMethodPercentages["Key entered"] || 0
    ),

    amexMonthlyVolumeInUsd: Number(this.state.amexMonthlyVolume || 0),
    amexAvgTicketInUsd: Number(this.state.amexAvgTicket || 0),
    amexHighestTicketInUsd: Number(this.state.amexHighTicket || 0),
    acceptAmexPayment: this.state.acceptAmexPayment ? 1 : 0,
    paymentProcessingWebsiteUrl: this.state.paymentProcessingUrl,
    demoLoginUsername: this.state.demoLoginUsername,
    demoLoginPassword: this.state.demoLoginPassword,

    premiseOwner: this.state.companyRegPremiseOwner,
    // areaZoned: this.state.companyRegAreaZoned,

    // squareFootage: Number(this.state.companyRegSquareFootage || 0),
    numberOfLocations: Number(this.state.numberOfLocations || 0),

    identificationType: this.state.taxIdType,
    identificationNumber: this.state.taxpayerIdNumber,

    isNpo: this.state.isNonprofit === "1" ? 1 : 0,

    advertiseType: (this.state.advertiseMethods || []).join(","),

    inboundPct: Number(this.state.inboundPercent || 0),
    outboundPct: Number(this.state.outboundPercent || 0),
    b2bPct: Number(this.state.b2bPercent || 0),
    retailPct: Number(this.state.retailPercent || 0),

    isBusinessSeasonal: this.state.isBusinessSeasonal === "1" ? 1 : 0,
    seasonalityDesc: this.state.peakOffSeasonDescription,

    returnRefundPolicyLink: this.state.returnRefundPolicy,
    refundRequestWindow: this.state.refundDaysAfterPurchase,
    refundProcessWindow: this.state.refundProcessingDays,

    cardChargeTiming: this.state.cardChargedWhen,
    serviceDeliveryTimeDays: this.state.deliveryDaysAfterCharge,

    usesThirdPartyFulfillment:
      this.state.usesThirdPartyFulfillment === "1" ? 1 : 0,
    thirdPartyCompanyName: this.state.thirdPartyFulfillmentName,

    isPciCompliant: this.state.isPciCompliant === "1" ? 1 : 0,
    previouslyTerminatedByCardNetwork:
      this.state.wasTerminatedMerchant === "1" ? 1 : 0,
    dataCompromiseInvestigationHistory:
      this.state.hasDataCompromise === "1" ? 1 : 0,
    identifiedInVisaRiskPrograms: this.state.wasVisaRiskProgram === "1" ? 1 : 0,
    thirdPartyPaymentParticipation:
      this.state.usesThirdPartySoftware === "1" ? 1 : 0,

    thirdPartyPlatformName: this.state.thirdPartySoftwareNames,

    bankruptcyStatus: this.state.bankruptcyType,
    bankruptcyFillingDate: this.state.bankruptcyFilingDate,

    incidentDetails: this.state.bankruptcyExplanation,
    companyregAddress: this.state.companyregAddress,
    companyregCity: this.state.companyregCity,
    companyregState: this.state.companyregState,
    companyregZip: this.state.companyregZip,
    companyregCountry: this.state.companyregCountry,
    companyRegPremiseType: this.state.companyRegPremiseType,
    companyRegYearsInThisLocation: this.state.companyRegYearsInThisLocation,
    // Change #4
    // companyRegPremiseOwner: this.state.companyRegPremiseOwner,
    areaZoned: this.state.companyRegAreaZoned,
    squareFootage: this.state.companyRegSquareFootage,
    numberOfLocations: this.state.numberOfLocations,
    companyOfficeAddress: this.state.sameAddress
      ? this.state.companyregAddress
      : this.state.companyOfficeAddress,
    companyOfficeCity: this.state.sameAddress
      ? this.state.companyregCity
      : this.state.companyOfficeCity,
    companyOfficeState: this.state.sameAddress
      ? this.state.companyregState
      : this.state.companyOfficeState,
    companyOfficeZip: this.state.sameAddress
      ? this.state.companyregZip
      : this.state.companyOfficeZip,
    companyOfficeCountry: this.state.sameAddress
      ? this.state.companyregCountry
      : this.state.companyOfficeCountry,
    companyOfficePremiseType: this.state.sameAddress
      ? this.state.companyRegPremiseType
      : this.state.companyOfficePremiseType,
    companyOfficeYearsInThisLocation: this.state.sameAddress
      ? this.state.companyRegYearsInThisLocation
      : this.state.companyOfficeYearsInThisLocation,
    officePremiseOwner: this.state.sameAddress
      ? this.state.companyRegPremiseOwner
      : this.state.companyOfficePremiseOwner,
    officeAreaZoned: this.state.sameAddress
      ? this.state.companyRegAreaZoned
      : this.state.companyOfficeAreaZoned,
    officeSquareFootage: this.state.sameAddress
      ? this.state.companyRegSquareFootage
      : this.state.companyOfficeSquareFootage,
    businessActivity: this.state.businessActivity,
    accountPurpose: this.state.accountPurpose,
    investmentSource: this.state.investmentSource,
    revenue: this.state.revenue,
    profit: this.state.profit,
    companyAssets: this.state.companyAssets,
    companyNetWorth: this.state.companyNetWorth,
    transactionVolumes: this.state.transactionVolumes,
    transactionFrequency: this.state.transactionFrequency,
    bankingPartner: this.state.bankingPartner,
    corporateStructure: this.state.corporateStructure,
    relationWithBank: this.state.relationWithBank,
    // Change #1: percentages
    isProcessingCardTransaction: this.state.isProcessingCardTransaction,
    // cardProcessingMethodPercentages: JSON.stringify(
    //   this.state.cardProcessingMethodPercentages
    // ),
    onlineTxnPct: this.state.cardProcessingMethodPercentages.Online,
    inPersonSwipeTxnPct:
      this.state.cardProcessingMethodPercentages["In person swipe"],
    overThePhoneTxnPct:
      this.state.cardProcessingMethodPercentages["Over the phone"],
    keyEnteredTxnPct: this.state.cardProcessingMethodPercentages["Key entered"],
    transactionLimitInUsd: this.state.transactionLimitInUsd,
    averageTransactionSizeInUsd: this.state.averageTransactionSizeInUsd,
    highestTransactionSizeInUsd: this.state.highestTransactionSizeInUsd,
    acceptAchPayment: this.state.acceptAchPayment,

    // Change #8
    // cardChargedWhen: this.state.cardChargedWhen,
    // deliveryDaysAfterCharge: this.state.deliveryDaysAfterCharge,
    // usesThirdPartyFulfillment: this.state.usesThirdPartyFulfillment,
    // thirdPartyFulfillmentName: this.state.thirdPartyFulfillmentName,
    // Change #9
    // isPciCompliant: this.state.isPciCompliant,
    // wasTerminatedMerchant: this.state.wasTerminatedMerchant,
    // hasDataCompromise: this.state.hasDataCompromise,
    // wasVisaRiskProgram: this.state.wasVisaRiskProgram,
    // usesThirdPartySoftware: this.state.usesThirdPartySoftware,
    // thirdPartySoftwareNames: this.state.thirdPartySoftwareNames,
    // bankruptcyType: this.state.bankruptcyType,
    // bankruptcyFilingDate: this.state.bankruptcyFilingDate,
    // bankruptcyExplanation: this.state.bankruptcyExplanation,
  });

  saveEnterpriseInfo = () => {
    saveEnterpriseUserInfo(this.buildEnterprisePayload())
      .then((res) => {
        this.setState({ showLoader: false });
        if (res.data?.error?.error_data === 0) {
          showToast("success", "Company info saved");
          this.setState((prev) => ({
            activeStep: prev.activeStep + 1,
            errors: {},
          }));
        } else {
          showToast(
            "error",
            res.data?.error?.error_msg || "Failed to save company info"
          );
        }
      })
      .catch((err) => {
        this.setState({ showLoader: false });
        showHttpError(err);
      });
  };

  saveEnterpriseDocuments = () => {
    this.setState((prev) => ({
      showLoader: false,
      activeStep: prev.activeStep + 1,
      errors: {},
    }));
    showToast("success", "Documents saved");
  };

  saveAuthorizationLetter = () => {
    this.setState((prev) => ({
      showLoader: false,
      activeStep: prev.activeStep + 1,
      errors: {},
    }));
    showToast("success", "Authorization letter saved");
  };

  submitFinalEnterpriseKyc = () => {
    const hasUbo = this.state.beneficialOwners?.some(
      (o) => o.ownerType === "UBO"
    );
    if (!hasUbo) {
      showToast(
        "error",
        "Please add at least one UBO owner for the final submit"
      );
      this.setState({ showLoader: false });
      return;
    }
    if (!this.validateEnterpriseDocuments()) {
      this.setState({ showLoader: false });
      return;
    }
    this.setState({ showLoader: true });
    let fd = new FormData();
    fd.append(
      "merchant_id",
      localStorage.getItem(SITENAMEALIAS + "_merchant_id")
    );
    fd.append("uuid", this.state.userUuid || getAuthUuid());
    fd.append("enterpriseUser", JSON.stringify(this.buildEnterprisePayload()));
    if (this.state.incorporationCertificate)
      fd.append(
        "incorporationCertificate",
        this.state.incorporationCertificate
      );
    if (this.state.companyBankStatement)
      fd.append("bankStatement", this.state.companyBankStatement);
    if (this.state.memorandum) fd.append("memorandum", this.state.memorandum);
    if (this.state.associationArticles)
      fd.append("associationArticles", this.state.associationArticles);
    if (this.state.incumbencyCertificate)
      fd.append("incumbencyCertificate", this.state.incumbencyCertificate);
    if (this.state.directorsRegister)
      fd.append("directorsRegister", this.state.directorsRegister);
    if (this.state.shareholdersRegister)
      fd.append("shareholdersRegister", this.state.shareholdersRegister);
    if (this.state.boardResolution)
      fd.append("boardResolution", this.state.boardResolution);
    if (this.state.addressProofFile)
      fd.append("addressProof", this.state.addressProofFile);
    if (this.state.wolfsbergDoc)
      fd.append("wolfsbergDoc", this.state.wolfsbergDoc);
    if (this.state.authorizationLetter)
      fd.append("authorizationLetter", this.state.authorizationLetter);
    if (this.state.processingStatement)
      fd.append("processingStatement", this.state.processingStatement);
    addUserEnterpriseDetails(fd)
      .then((res) => {
        this.setState({ showLoader: false });
        if (res.data?.error?.error_data === 0) {
          showToast("success", "Enterprise KYC submitted successfully!");
          const adminUser = localStorage.getItem(
            SITENAMEALIAS + "_merchant_id"
          );
          const uuid = this.state.userUuid || getAuthUuid();
          finishKyc({ adminUser, uuid })
            .then((finishRes) => {
              if (finishRes.data?.error?.error_data === 0) {
                this.setState({ kycStatus: 0 });
                setTimeout(() => window.location.reload(), 3000);
              }
            })
            .catch((err) => showHttpError(err));
        } else {
          showToast(
            "error",
            res.data?.error?.error_msg || "Failed to submit enterprise KYC"
          );
        }
      })
      .catch((err) => {
        this.setState({ showLoader: false });
        showHttpError(err);
      });
  };

  /* ==== BENEFICIAL OWNERS ==== */

  handleAddOwnerClick = () => {
    const defaultCountry =
      this.state.phoneCountries.find((c) => c.code === "US") ||
      this.state.phoneCountries[0] ||
      null;
    this.setState({
      showAddOwnerModal: true,
      editingOwner: null,
      ownerType: "Authorized signatory",
      ownerTitle: "President",
      ownerFirstName: "",
      ownerMiddleName: "",
      ownerLastName: "",
      ownerEmail: "",
      ownerPhone: "",
      ownerAddress: "",
      ownerCity: "",
      ownerState: "",
      ownerCountry: "",
      ownerZip: "",
      ownerDob: "",
      ownerBirthPlace: "",
      ownerSsn: "",
      ownerIdType: "",
      ownerIdNumber: "",
      ownerIdCountry: "",
      ownerIdState: "",
      ownerPoaType: "",
      idStates: [],
      ownerOwnershipPercent: "",
      ownerPep: "0",
      ownerUuid: "",
      ownerFrontId: null,
      ownerFrontIdName: "",
      ownerBackId: null,
      ownerBackIdName: "",
      ownerAddressProof: null,
      ownerAddressProofName: "",
      ownerSelfie: null,
      ownerSelfieName: "",
      ownerInvestingFund: null,
      ownerInvestingFundName: "",
      ownerEmailError: "",
      ownerPhoneError: "",
      phoneSelectedCountry: defaultCountry,
      phoneDropdownOpen: false,
      phoneSearchTerm: "",
      phoneFilteredCountries: this.state.phoneCountries,
    });
  };

  handleEditOwner = (owner) => {
    const idCountryVal = owner.identityDocCountry || owner.idCountry || "";
    let states = [];
    if (idCountryVal) {
      const countryObj = Country.getAllCountries().find(
        (c) => c.name === idCountryVal
      );
      if (countryObj) {
        states = State.getStatesOfCountry(countryObj.isoCode);
      }
    }

    const titleOptions = [
      "President",
      "Vice President",
      "Secretary",
      "Sole Proprietor",
      "Treasurer",
      "Partner",
      "Member",
      "CFO",
      "Owner",
      "CEO",
      "Recorder",
    ];

    const validOwnerType = ["Authorized signatory", "Director", "UBO"].includes(
      owner.ownerType
    )
      ? owner.ownerType
      : "Authorized signatory";

    const validTitle = owner.title
      ? owner.title
      : titleOptions.includes(owner.ownerType)
        ? owner.ownerType
        : "President";

    this.setState({
      showAddOwnerModal: true,
      editingOwner: owner,
      ownerType: validOwnerType,
      ownerTitle: validTitle,
      ownerFirstName: owner.firstName || "",
      ownerMiddleName: owner.middleName || "",
      ownerLastName: owner.lastName || "",
      ownerEmail: owner.email || "",
      ownerPhone: owner.phone || "",
      ownerAddress: owner.address || "",
      ownerCity: owner.city || "",
      ownerState: owner.state || "",
      ownerCountry: owner.country || "",
      ownerZip: owner.zip || "",
      ownerDob: owner.dob || "",
      ownerBirthPlace: owner.birthPlace || "",
      ownerSsn: owner.ssn || "",
      ownerIdType: owner.identityDocType || owner.idType || "",
      ownerIdNumber: owner.identityDocNumber || owner.idNumber || "",
      ownerIdCountry: idCountryVal,
      ownerIdState: owner.identityDocState || owner.idState || "",
      ownerPoaType: owner.addressProofDocType || owner.poaType || "",
      idStates: states,
      ownerOwnershipPercent: owner.ownershipPer || "",
      ownerPep: String(owner.pep || "0"),
      ownerUuid: owner.ownerUuid || "",
      ownerFrontIdName: owner.idProofFront || owner.frontIdName || "",
      ownerBackIdName: owner.idProofBack || owner.backIdName || "",
      ownerAddressProofName:
        owner.addressProofDoc || owner.addressProofName || "",
      ownerSelfieName: owner.selfieDoc || owner.selfieName || "",
      ownerInvestingFundName:
        owner.investmentProofDoc || owner.investingFundName || "",
      ownerEmailError: "",
      ownerPhoneError: "",
      phoneDropdownOpen: false,
      phoneSearchTerm: "",
      phoneFilteredCountries: this.state.phoneCountries,
    });
  };

  handleAddOwner = () => {
    this.validateOwnerEmail(this.state.ownerEmail);
    this.validateOwnerPhone(this.state.ownerPhone);

    if (!this.state.ownerFirstName.trim() || !this.state.ownerLastName.trim()) {
      showToast("error", "First name and last name are required");
      return;
    }
    if (!this.state.ownerDob) {
      showToast("error", "Date of birth is required");
      return;
    }
    const age = this.calculateAge(this.state.ownerDob);
    if (age !== null && age < 18) {
      showToast("error", "Beneficial owner must be at least 18 years old");
      return;
    }

    if (!this.state.ownerSsn.trim()) {
      showToast("error", "SSN or Passport is required");
      return;
    }
    if (this.state.ownerSsn.trim().length < 4) {
      showToast("error", "Must be at least 4 characters");
      return;
    }
    if (this.state.ownerSsn.trim().length > 15) {
      showToast("error", "Must be not more than 15 characters");
      return;
    }

    const emailVal = this.state.ownerEmail.trim();
    if (!emailVal) {
      showToast("error", "Email address is required");
      return;
    }
    if (!EMAIL_REGEX.test(emailVal)) {
      showToast(
        "error",
        "Please enter a valid email address (e.g. owner@company.com)"
      );
      return;
    }
    const phoneVal = this.state.ownerPhone.trim();
    if (!phoneVal) {
      showToast("error", "Phone number is required");
      return;
    }
    if (!PHONE_REGEX.test(phoneVal)) {
      showToast(
        "error",
        "Please enter a valid phone number (e.g. +15550000000)"
      );
      return;
    }
    if (!this.state.ownerState.trim()) {
      showToast("error", "State is required for the beneficial owner");
      return;
    }
    if (!this.state.ownerCountry) {
      showToast("error", "Please select a country for the beneficial owner");
      return;
    }

    if (!this.state.ownerIdType) {
      showToast("error", "Please select an ID type for the beneficial owner");
      return;
    }
    if (!this.state.ownerIdNumber || !this.state.ownerIdNumber.trim()) {
      showToast("error", "Please enter the identity document number");
      return;
    }
    if (!ID_NUMBER_REGEX.test(this.state.ownerIdNumber.trim())) {
      showToast(
        "error",
        "Please enter a valid identity document number (5 to 52 characters, alphanumeric, spaces, and hyphens allowed, and cannot start or end with a space/hyphen)"
      );
      return;
    }
    if (!this.state.ownerIdCountry) {
      showToast("error", "Please select an ID issuing country for the beneficial owner");
      return;
    }
    if (this.state.ownerIdType === "Driver's license" && !this.state.ownerIdState) {
      showToast("error", "Please select an ID issuing state for the beneficial owner");
      return;
    }
    if (!this.state.ownerPoaType) {
      showToast("error", "Please select a Proof of Address type for the beneficial owner");
      return;
    }

    // Verify file uploads: valid if file object exists or if name exists (from prior upload)
    if (!this.state.ownerFrontId && !this.state.ownerFrontIdName) {
      showToast("error", "Government ID - Front document is required");
      return;
    }
    if (this.state.ownerIdType !== "Passport" && !this.state.ownerBackId && !this.state.ownerBackIdName) {
      showToast("error", "Government ID - Back document is required");
      return;
    }
    if (!this.state.ownerAddressProof && !this.state.ownerAddressProofName) {
      showToast("error", "Proof of Address document is required");
      return;
    }
    if (!this.state.ownerSelfie && !this.state.ownerSelfieName) {
      showToast("error", "Selfie holding ID is required");
      return;
    }

    const isSignatory = this.state.ownerType === "Authorized signatory";
    const ownershipVal = parseFloat(this.state.ownerOwnershipPercent);
    if (!isSignatory && (isNaN(ownershipVal) || ownershipVal <= 0)) {
      showToast(
        "error",
        "Ownership percentage is required and must be greater than 0 for Directors and UBOs"
      );
      return;
    }
    if (isNaN(ownershipVal) || ownershipVal < 0 || ownershipVal > 100) {
      showToast(
        "error",
        "A valid ownership percentage between 0 and 100 is required"
      );
      return;
    }

    const editingOwnerUuid = this.state.editingOwner?.ownerUuid || null;
    if (
      !this.validateTotalOwnership(
        this.state.ownerOwnershipPercent,
        editingOwnerUuid
      )
    )
      return;

    const savedStep = this.state.activeStep;
    this.setState({ showLoader: true });
    let fd = new FormData();
    const ownerPayload = {
      firstName: this.state.ownerFirstName,
      middleName: this.state.ownerMiddleName,
      lastName: this.state.ownerLastName,
      title: this.state.ownerTitle,
      email: this.state.ownerEmail,
      phone: this.state.ownerPhone.trim(),
      address: this.state.ownerAddress,
      city: this.state.ownerCity,
      state: this.state.ownerState,
      country: this.state.ownerCountry,
      zip: this.state.ownerZip,
      dob: this.state.ownerDob,
      birthPlace: this.state.ownerBirthPlace,
      ssn: this.state.ownerSsn,
      ownershipPer: this.state.ownerOwnershipPercent,
      pep: this.state.ownerPep === "1" ? 1 : 0,
      action: this.state.editingOwner ? "UPDATE" : "INSERT",
      ownerType: this.state.ownerType,
      ownerUuid: this.state.ownerUuid,
      identityDocType: this.state.ownerIdType,
      identityDocNumber: this.state.ownerIdNumber,
      identityDocCountry: this.state.ownerIdCountry,
      identityDocState: this.state.ownerIdState,
      addressProofDocType: this.state.ownerPoaType,
    };
    fd.append("owner", JSON.stringify(ownerPayload));
    fd.append(
      "merchant_id",
      localStorage.getItem(SITENAMEALIAS + "_merchant_id")
    );
    fd.append("userUuid", this.state.userUuid || getAuthUuid());
    if (this.state.ownerFrontId)
      fd.append("idProofFront", this.state.ownerFrontId);
    if (this.state.ownerBackId)
      fd.append("idProofBack", this.state.ownerBackId);
    if (this.state.ownerAddressProof)
      fd.append("addressProofDoc", this.state.ownerAddressProof);
    if (this.state.ownerSelfie) fd.append("selfieDoc", this.state.ownerSelfie);
    if (this.state.ownerInvestingFund)
      fd.append("investmentProofDoc", this.state.ownerInvestingFund);
    addEnterpriseOwner(fd)
      .then((res) => {
        this.setState({ showLoader: false });
        if (res.data?.error?.error_data === 0) {
          showToast(
            "success",
            this.state.editingOwner
              ? "Owner updated successfully"
              : "Beneficial owner added"
          );
          this.setState({ showAddOwnerModal: false });
          this.fetchKycStatus(savedStep);
        } else {
          showToast(
            "error",
            res.data?.error?.error_msg || "Failed to save owner"
          );
        }
      })
      .catch((err) => {
        this.setState({ showLoader: false });
        showHttpError(err);
      });
  };

  handleDeleteClick = (owner) => {
    this.setState({ showDeleteConfirm: true, ownerToDelete: owner });
  };

  confirmDelete = () => {
    if (!this.state.ownerToDelete) return;
    const savedStep = this.state.activeStep;
    this.setState({ showLoader: true });
    const fd = new FormData();
    fd.append("userUuid", this.state.userUuid || getAuthUuid());
    fd.append(
      "owner",
      JSON.stringify({
        ownerUuid: this.state.ownerToDelete.ownerUuid,
        enterpriseId: this.state.ownerToDelete.enterpriseId,
        action: "DELETE",
      })
    );
    deleteEnterpriseOwner(fd)
      .then((res) => {
        this.setState({ showLoader: false });
        if (res.data?.error?.error_data === 0) {
          showToast("success", "Owner removed");
          this.fetchKycStatus(savedStep);
          this.setState({ showDeleteConfirm: false, ownerToDelete: null });
        } else {
          showToast("error", res.data?.error?.error_msg || "Failed to remove");
        }
      })
      .catch((err) => {
        this.setState({ showLoader: false });
        showHttpError(err);
      });
  };

  /* ==== PHOTO & QR ==== */

  capturePhoto = async () => {
    if (this.webcamRef.current) {
      const src = this.webcamRef.current.getScreenshot();
      if (src) {
        const blob = this.base64ToBlob(src);
        this.setState({ capturedPhoto: src, capturedPhotoBlob: blob });
      }
    }
  };

  retakePhoto = () => {
    this.setState({
      capturedPhoto: null,
      capturedPhotoBlob: null,
      faceDetected: false,
    });
  };

  base64ToBlob = (b64) => {
    const bs = atob(b64.split(",")[1]);
    const mime = b64.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(bs.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < bs.length; i++) ia[i] = bs.charCodeAt(i);
    return new Blob([ab], { type: mime });
  };

  blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  uploadPhotoAndSubmit = () => {
    if (!this.state.capturedPhoto) {
      showToast("error", "Please capture a photo first");
      return;
    }
    if (!this.state.faceDetected) {
      showToast("error", "No face detected. Please retake.");
      return;
    }
    this.setState({ showLoader: true });
    this.submitFinalIndividualKyc();
  };

  generateQr = async () => {
    try {
      const res = await getScannerToken({
        merchant_id: localStorage.getItem(SITENAMEALIAS + "_merchant_id"),
        uuid: this.state.userUuid || getAuthUuid(),
      });
      if (res.data.error?.error_data === 0) {
        if (this.state.qrInterval) clearInterval(this.state.qrInterval);
        const interval = setInterval(() => {
          this.setState((prev) => {
            if (prev.qrCount <= 1) {
              clearInterval(interval);
              this.stopPhotoPolling();
              return { qrCount: 0, showQR: false, qrInterval: null };
            }
            return { qrCount: prev.qrCount - 1 };
          });
        }, 1000);
        this.setState(
          {
            qrCount: 300,
            qrInterval: interval,
            twoFactorAuthKey: `${QR_BASE_URL}/${btoa(
              this.state.userUuid || getAuthUuid()
            )}/${btoa(res.data.token)}`,
            showQR: true,
          },
          () => {
            this.startPhotoPolling();
          }
        );
      } else {
        showToast(
          "error",
          res.data.error?.error_msg || "Failed to generate QR"
        );
      }
    } catch (err) {
      showToast("error", "Failed to generate QR code");
    }
  };

  handleCloseQR = () => {
    if (this.state.qrInterval) clearInterval(this.state.qrInterval);
    this.stopPhotoPolling();
    this.setState({ showQR: false, qrCount: 300, qrInterval: null });
  };

  startPhotoPolling = () => {
    this.stopPhotoPolling();
    this.photoPollingInterval = setInterval(() => {
      if (this.state.showQR) {
        this.getUserPhotoFromApi();
      } else {
        this.stopPhotoPolling();
      }
    }, 2000);
  };

  stopPhotoPolling = () => {
    if (this.photoPollingInterval) {
      clearInterval(this.photoPollingInterval);
      this.photoPollingInterval = null;
    }
  };

  getUserPhotoFromApi = async () => {
    const uuid = this.state.userUuid || getAuthUuid();
    const country = this.state.country || "US";
    if (!uuid) return;
    try {
      const res = await getUserPhoto({ uuid, country });
      if (res.data && res.data.size > 0) {
        const base64Data = await this.blobToBase64(res.data);
        if (
          !base64Data ||
          base64Data === "data:application/octet-stream;base64,"
        )
          return;
        this.stopPhotoPolling();
        if (this.state.qrInterval) clearInterval(this.state.qrInterval);
        this.setState({
          capturedPhoto: base64Data,
          capturedPhotoBlob: res.data,
          faceDetected: true,
          showQR: false,
          qrCount: 300,
          qrInterval: null,
          showWebcam: false,
        });
        showToast(
          "success",
          "Photo captured successfully from your mobile device!"
        );
      }
    } catch (err) {
      if (err?.response?.status !== 404 && err?.response?.status !== 500) {
        console.debug("Photo polling - no photo yet:", err?.message || err);
      }
    }
  };

  getQrValue = () => {
    return this.state.twoFactorAuthKey;
  };
  formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };
  renderError = (field) =>
    this.state.errors[field] ? (
      <small className="text-danger">{this.state.errors[field]}</small>
    ) : null;

  /* ============================
     OWNER AGE & OWNERSHIP VALIDATION
     ============================ */

  calculateAge = (dateString) => {
    if (!dateString) return null;
    const birthDate = new Date(dateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    )
      age--;
    return age;
  };

  validateOwnerAge = (dobValue) => {
    if (!dobValue) return true;
    const age = this.calculateAge(dobValue);
    if (age !== null && age < 18) {
      showToast("error", "Beneficial owner must be at least 18 years old");
      return false;
    }
    return true;
  };

  validateTotalOwnership = (newOwnershipPercent, editingOwnerUuid = null) => {
    const { beneficialOwners } = this.state;
    const existingTotal = beneficialOwners.reduce((sum, owner) => {
      if (editingOwnerUuid && owner.ownerUuid === editingOwnerUuid) return sum;
      return sum + (parseFloat(owner.ownershipPer) || 0);
    }, 0);
    const newPercent = parseFloat(newOwnershipPercent) || 0;
    const newTotal = existingTotal + newPercent;
    if (newTotal > 100) {
      const available = 100 - existingTotal;
      showToast(
        "error",
        `Total ownership cannot exceed 100%. Current total from other owners is ${existingTotal}%. Maximum you can add is ${available}%`
      );
      return false;
    }
    return true;
  };

  getTotalOwnershipPercentage = () => {
    return this.state.beneficialOwners.reduce(
      (sum, owner) => sum + (parseFloat(owner.ownershipPer) || 0),
      0
    );
  };

  /* ============================
     RENDER METHODS
     ============================ */

  renderTypeSelection = () => (
    <div className="kyc-type-selection">
      <h2>KYC Verification</h2>
      <p className="kyc-subtitle">
        Please select your account type to begin verification
      </p>
      <div className="row justify-content-center">
        <div className="col-md-5 col-lg-4 mb-3">
          <div className="kyc-type-card">
            <div className="kyc-type-icon individual">
              <i className="fas fa-user" />
            </div>
            <h4>Individual</h4>
            <p className="kyc-type-desc">
              For personal accounts and individual traders
            </p>
            <div className="kyc-type-features">
              <div className="kyc-feature-item">
                <i className="fas fa-check-circle" /> Personal ID verification
              </div>
              <div className="kyc-feature-item">
                <i className="fas fa-check-circle" /> Address proof upload
              </div>
              <div className="kyc-feature-item">
                <i className="fas fa-check-circle" /> Employment details
              </div>
              <div className="kyc-feature-item">
                <i className="fas fa-check-circle" /> Photo verification
              </div>
            </div>
            <button
              className="btn btn-select-type"
              onClick={() => this.selectKycType("individual")}
            >
              Select Individual
            </button>
          </div>
        </div>
        <div className="col-md-5 col-lg-4 mb-3">
          <div className="kyc-type-card">
            <div className="kyc-type-icon enterprise">
              <i className="fas fa-building" />
            </div>
            <h4>Enterprise</h4>
            <p className="kyc-type-desc">
              For businesses and corporate accounts
            </p>
            <div className="kyc-type-features">
              <div className="kyc-feature-item">
                <i className="fas fa-check-circle" /> Company information
              </div>
              <div className="kyc-feature-item">
                <i className="fas fa-check-circle" /> Business documents
              </div>
              <div className="kyc-feature-item">
                <i className="fas fa-check-circle" /> Authorization letter
              </div>
              <div className="kyc-feature-item">
                <i className="fas fa-check-circle" /> Beneficial owners
              </div>
            </div>
            <button
              className="btn btn-select-type"
              onClick={() => this.selectKycType("enterprise")}
            >
              Select Enterprise
            </button>
          </div>
        </div>
      </div>
      <div className="row justify-content-center mt-5">
        <div className="col-md-10 col-lg-8">
          <div className="kyc-type-footer">
            <p>
              <strong>Not sure which to choose?</strong> Individual accounts are
              for personal use, while Enterprise accounts are for registered
              businesses.
            </p>
            <p className="mt-2">
              <i className="fas fa-info-circle mr-2" /> If you are a merchant
              seeking card payment services, you must complete Enterprise KYC
              verification.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  renderIconStepper = () => {
    const steps = ENTERPRISE_STEPS;
    /* const steps =
      this.state.kycType === "individual" ? INDIVIDUAL_STEPS : ENTERPRISE_STEPS; */
    return (
      <div className="kyc-icon-stepper">
        <div className="stepper-track">
          {steps.map((step, i) => {
            let cls =
              i < this.state.activeStep
                ? "completed"
                : i === this.state.activeStep
                  ? "active"
                  : "pending";
            return (
              <React.Fragment key={i}>
                <div className="stepper-step">
                  <div className={"step-icon-wrap " + cls}>
                    {i < this.state.activeStep ? (
                      <i className="fas fa-check" />
                    ) : (
                      <i className={step.icon} />
                    )}
                  </div>
                  <span className={"step-label " + cls}>{step.label}</span>
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={
                      "stepper-connector " +
                      (i < this.state.activeStep ? "completed" : "")
                    }
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  };

  handleTier3FileChange = (e, fileKey, fileNameKey) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > TIER3_MAX_FILE_SIZE) {
      showToast("error", "File size must be less than 5MB");
      e.target.value = "";
      return;
    }
    const fileExt = "." + file.name.split(".").pop().toLowerCase();
    const allowed = TIER3_ALLOWED_TYPES.split(",").map((t) =>
      t.trim().toLowerCase()
    );
    if (!allowed.includes(fileExt)) {
      showToast("error", `Invalid file type. Allowed: ${TIER3_ALLOWED_TYPES}`);
      e.target.value = "";
      return;
    }
    const sanitizedName = this.sanitizeFileName(file.name);
    this.setState({ [fileKey]: file, [fileNameKey]: sanitizedName });
  };

  submitTier3Kyc = () => {
    const { tier3BankStatement, tier3Tin, tier3TaxStatement, userUuid } =
      this.state;
    if (!tier3BankStatement) {
      showToast("error", "Please upload a Bank Statement");
      return;
    }
    if (!tier3Tin.trim()) {
      showToast("error", "Please enter Tax Identification Number");
      return;
    }
    if (!tier3TaxStatement) {
      showToast("error", "Please upload a Tax Statement");
      return;
    }
    this.setState({ tier3Loading: true });
    const uuid =
      userUuid ||
      localStorage.getItem(SITENAMEALIAS + "_exchange_uuid") ||
      getAuthUuid();
    const userId = localStorage.getItem("userId");
    const fd = new FormData();
    fd.append("userId", userId);
    fd.append("bankStatement", tier3BankStatement);
    fd.append("tin", tier3Tin.trim());
    fd.append("taxStatement", tier3TaxStatement);
    fd.append("uuid", uuid);
    SubmitKyc(fd)
      .then((res) => {
        const data = res.data;
        this.setState({ tier3Loading: false });
        if (data && data.error && data.error.error_data === 0) {
          this.setState({ tier3Submitted: true });
          showToast("success", "Tier 3 verification submitted");
        } else {
          showToast(
            "error",
            (data && data.error && data.error.error_msg) ||
            "Failed to submit Tier 3 verification"
          );
        }
      })
      .catch(() => {
        this.setState({ tier3Loading: false });
        showToast("error", "Failed to submit Tier 3 verification");
      });
  };

  renderTier3Section = () => {
    const {
      tier3BankStatement,
      tier3BankStatementName,
      tier3Tin,
      tier3TaxStatement,
      tier3TaxStatementName,
      tier3Submitted,
      tier3Loading,
      userTierDocsStatus,
    } = this.state;
    if (tier3Submitted) {
      return (
        <div className="kyc-form-card mt-4">
          <div className="text-center py-3">
            <i
              className="fas fa-clock text-warning"
              style={{ fontSize: "2.5rem" }}
            />
            <h5 className="text-warning mt-2">
              Tier 3 Identity verification documents submitted.
            </h5>
            <p className="text-muted small">
              Your documents are under review. We'll notify you once verified.
            </p>
          </div>
        </div>
      );
    }
    if (userTierDocsStatus === "")
      return (
        <div className="kyc-form-card mt-4">
          <div className="text-center py-3">
            <i
              className="fas fa-check-circle text-success"
              style={{ fontSize: "2.5rem" }}
            />
            <h5 className="text-success mt-2">
              You are already verified for Tier 2.
            </h5>
          </div>
        </div>
      );
    if (userTierDocsStatus === "0")
      return (
        <div className="kyc-form-card mt-4">
          <div className="text-center py-3">
            <i
              className="fas fa-clock text-warning"
              style={{ fontSize: "2.5rem" }}
            />
            <h5 className="text-warning mt-2">
              Tier 3 Identity verification documents submitted.
            </h5>
            <p className="text-muted small">
              Your documents are under review. We'll notify you once verified.
            </p>
          </div>
        </div>
      );
    if (userTierDocsStatus === "1")
      return (
        <div className="kyc-form-card mt-4">
          <div className="text-center py-3">
            <i
              className="fas fa-check-circle text-success"
              style={{ fontSize: "2.5rem" }}
            />
            <h5 className="text-success mt-2">
              Tier 3 Identity verification documents verified.
            </h5>
          </div>
        </div>
      );
    return (
      <div className="kyc-form-card mt-4">
        <h5>Tier 3 Verification</h5>
        {userTierDocsStatus === "2" && (
          <div className="alert alert-danger py-2">
            <i className="fas fa-times-circle mr-2" />
            Tier 3 Identity verification documents declined, please submit
            again.
          </div>
        )}
        <p className="text-muted small">
          Please provide the following documents for enhanced verification.
        </p>
        <div className="form-group mt-3">
          <label>
            Bank Statement <span className="text-danger">*</span>
          </label>
          <input
            type="file"
            className="form-control"
            ref={this.tier3BankStatementInputRef}
            accept={TIER3_ALLOWED_TYPES}
            onChange={(e) =>
              this.handleTier3FileChange(
                e,
                "tier3BankStatement",
                "tier3BankStatementName"
              )
            }
          />
          {tier3BankStatementName && (
            <small className="text-muted">{tier3BankStatementName}</small>
          )}
          <small className="form-text text-muted">
            Accepted: jpg, jpeg, pdf, doc, docx, jfif. Max 5MB.
          </small>
        </div>
        <div className="form-group mt-3">
          <label>
            Tax Identification Number (TIN){" "}
            <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            value={tier3Tin}
            onChange={(e) => this.setState({ tier3Tin: e.target.value })}
            placeholder="Enter Tax Identification Number"
          />
        </div>
        <div className="form-group mt-3">
          <label>
            Tax Statement <span className="text-danger">*</span>
          </label>
          <input
            type="file"
            className="form-control"
            ref={this.tier3TaxStatementInputRef}
            accept={TIER3_ALLOWED_TYPES}
            onChange={(e) =>
              this.handleTier3FileChange(
                e,
                "tier3TaxStatement",
                "tier3TaxStatementName"
              )
            }
          />
          {tier3TaxStatementName && (
            <small className="text-muted">{tier3TaxStatementName}</small>
          )}
          <small className="form-text text-muted">
            Accepted: jpg, jpeg, pdf, doc, docx, jfif. Max 5MB.
          </small>
        </div>
        <div className="mt-4">
          <button
            className="btn btn-primary"
            onClick={this.submitTier3Kyc}
            disabled={tier3Loading}
          >
            {tier3Loading ? (
              <>
                <i className="fas fa-spinner fa-spin mr-1" /> Submitting...
              </>
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </div>
    );
  };

  renderKycStatus = () => {
    if (this.state.kycStatus === 1)
      return (
        <div>
          <div className="kyc-status-screen">
            <i className="fas fa-check-circle text-success status-icon" />
            <h4 className="text-success">KYC Verified</h4>
            <p>Your identity has been verified successfully.</p>
            <Link to="/bank-info" className="btn btn-primary mt-3">
              Manage Bank Account
            </Link>
          </div>
          {this.renderTier3Section()}
        </div>
      );
    if (this.state.kycStatus === 0)
      return (
        <div className="kyc-status-screen">
          <i className="fas fa-clock text-warning status-icon" />
          <h4 className="text-warning">Verification Pending</h4>
          <p>
            Your KYC application is under review. We'll notify you once the
            verification is complete.
          </p>
          <Link to="/bank-info" className="btn btn-primary mt-3">
            Manage Bank Account
          </Link>
        </div>
      );
    if (this.state.kycStatus === 2)
      return (
        <div className="kyc-status-screen">
          <i className="fas fa-times-circle text-danger status-icon" />
          <h4 className="text-danger">Verification Rejected</h4>
          <p>
            {this.state.kycMessage ||
              "Your KYC was rejected. Please resubmit with correct documents."}
          </p>
          <button
            className="btn btn-primary mt-3"
            onClick={() =>
              this.setState({
                kycStatus: -1,
                activeStep: 0,
                showTypeSelection: false,
                kycType: "enterprise",
              })
            }
          >
            Resubmit KYC
          </button>
        </div>
      );
    return null;
  };

  renderPersonalInfoStep = () => (
    <div className="kyc-form-card">
      <h5>Address (This address should match with address proof document)</h5>
      <div className="row">
        <div className="form-group col-md-4">
          <label>House no./Apt no. *</label>
          <input
            type="text"
            className={
              "form-control " + (this.state.errors.houseNo ? "is-invalid" : "")
            }
            value={this.state.houseNo}
            onChange={(e) => this.setState({ houseNo: e.target.value })}
            onKeyDown={this.onlyAllowedInputHouse}
            onPaste={this.preventPasteHouse}
            placeholder="Enter House no./Apt no."
          />
          {this.renderError("houseNo")}
        </div>
        <div className="form-group col-md-4">
          <label>Street/Area *</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.streetArea ? "is-invalid" : "")
            }
            value={this.state.streetArea}
            onChange={(e) => this.setState({ streetArea: e.target.value })}
            onKeyDown={this.onlyAllowedInputStreet}
            onPaste={this.preventPasteStreet}
            placeholder="Enter Street/Area"
          />
          {this.renderError("streetArea")}
        </div>
        <div className="form-group col-md-4">
          <label>City *</label>
          <input
            type="text"
            className={
              "form-control " + (this.state.errors.city ? "is-invalid" : "")
            }
            value={this.state.city}
            onChange={(e) => this.setState({ city: e.target.value })}
            onKeyDown={this.allowOnlyAlphabetsCityState}
            onPaste={this.preventPasteCityState}
            placeholder="Enter City"
          />
          {this.renderError("city")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-4">
          <label>State *</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.stateProvince ? "is-invalid" : "")
            }
            value={this.state.stateProvince}
            onChange={(e) => this.setState({ stateProvince: e.target.value })}
            onKeyDown={this.allowOnlyAlphabetsCityState}
            onPaste={this.preventPasteCityState}
            placeholder="Enter State"
          />
          {this.renderError("stateProvince")}
        </div>
        <div className="form-group col-md-4">
          <label>Country *</label>
          <select
            className={
              "form-control " + (this.state.errors.country ? "is-invalid" : "")
            }
            value={this.state.country}
            onChange={(e) => this.setState({ country: e.target.value })}
          >
            <option value="">Select Country</option>
            {this.state.countries.map((c) => (
              <option key={c.code} value={c.country}>
                {c.country}
              </option>
            ))}
          </select>
          {this.renderError("country")}
        </div>
        <div className="form-group col-md-4">
          <label>Zip Code *</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.postalCode ? "is-invalid" : "")
            }
            value={this.state.postalCode}
            onChange={(e) => this.setState({ postalCode: e.target.value })}
            onKeyDown={this.onlyAllowedZipCode}
            placeholder="Enter Zip Code"
            maxLength="15"
          />
          {this.renderError("postalCode")}
        </div>
      </div>
      <div className="d-flex gap-2">
        <button className="btn btn-save-next" onClick={this.handleSaveAndNext}>
          Save and Next
        </button>
      </div>
    </div>
  );

  renderDobSsnStep = () => (
    <div className="kyc-form-card">
      <h5>Date of Birth and SSN / Passport No / National ID</h5>
      <div className="row">
        <div className="form-group col-md-4">
          <label>Date of Birth *</label>
          <div className="d-flex gap-1">
            <input
              type="text"
              className={
                "form-control " + (this.state.errors.dob ? "is-invalid" : "")
              }
              value={this.state.dobMonth}
              onChange={(e) => this.handleDobChange("dobMonth", e.target.value)}
              placeholder="MM"
              maxLength="2"
            />
            <input
              type="text"
              className={
                "form-control " + (this.state.errors.dob ? "is-invalid" : "")
              }
              value={this.state.dobDay}
              onChange={(e) => this.handleDobChange("dobDay", e.target.value)}
              placeholder="DD"
              maxLength="2"
            />
            <input
              type="text"
              className={
                "form-control " + (this.state.errors.dob ? "is-invalid" : "")
              }
              value={this.state.dobYear}
              onChange={(e) => this.handleDobChange("dobYear", e.target.value)}
              placeholder="YYYY"
              maxLength="4"
            />
          </div>
          {this.renderError("dob")}
        </div>
        <div className="form-group col-md-4">
          <label>Place of Birth *</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.birthPlace ? "is-invalid" : "")
            }
            value={this.state.birthPlace}
            onChange={(e) => this.setState({ birthPlace: e.target.value })}
            onKeyDown={this.allowOnlyAlphabetsCityState}
            onPaste={this.preventPasteCityState}
            placeholder="Enter Place of Birth"
          />
          {this.renderError("birthPlace")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-4">
          <label>SSN / Passport No. *</label>
          <input
            type="text"
            className={
              "form-control " + (this.state.errors.ssn ? "is-invalid" : "")
            }
            value={this.state.ssn}
            onChange={(e) =>
              this.setState({ ssn: e.target.value.toUpperCase() })
            }
            onKeyDown={this.onlyAllowedInput}
            placeholder="Enter last 4 digits of SSN or Complete Passport No"
            maxLength="15"
          />
          {this.renderError("ssn")}
        </div>
      </div>
      <div className="d-flex gap-2">
        <button className="btn btn-secondary" onClick={this.handleBack}>
          Back
        </button>
        <button className="btn btn-save-next" onClick={this.handleSaveAndNext}>
          Save and Next
        </button>
      </div>
    </div>
  );

  renderEmploymentStep = () => (
    <div className="kyc-form-card">
      <h5>Employment and Other Details</h5>
      <div className="row">
        <div className="form-group col-md-3">
          <label>Industry *</label>
          <select
            className={
              "form-control " + (this.state.errors.industry ? "is-invalid" : "")
            }
            value={this.state.industry}
            onChange={(e) => this.setState({ industry: e.target.value })}
          >
            <option value="">Select Industry</option>
            {INDUSTRIES.map((ind) => (
              <option key={ind} value={ind}>
                {ind}
              </option>
            ))}
          </select>
          {this.renderError("industry")}
        </div>
        <div className="form-group col-md-3">
          <label>Occupation *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.occupation ? "is-invalid" : "")
            }
            value={this.state.occupation}
            onChange={(e) => this.setState({ occupation: e.target.value })}
          >
            <option value="">Select Occupation</option>
            {OCCUPATIONS.map((occ) => (
              <option key={occ} value={occ}>
                {occ}
              </option>
            ))}
          </select>
          {this.renderError("occupation")}
        </div>
        <div className="form-group col-md-3">
          <label>Sources of Funds *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.sourceOfFunds ? "is-invalid" : "")
            }
            value={this.state.sourceOfFunds}
            onChange={(e) => this.setState({ sourceOfFunds: e.target.value })}
          >
            <option value="">Select Source</option>
            {FUNDS_SOURCES.map((fund) => (
              <option key={fund} value={fund}>
                {fund}
              </option>
            ))}
          </select>
          {this.renderError("sourceOfFunds")}
        </div>
        <div className="form-group col-md-3">
          <label>Monthly Transaction Volume *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.monthlyTransactionVolume ? "is-invalid" : "")
            }
            value={this.state.monthlyTransactionVolume}
            onChange={(e) =>
              this.setState({ monthlyTransactionVolume: e.target.value })
            }
          >
            <option value="">Select Volume</option>
            {AMOUNT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("monthlyTransactionVolume")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-3">
          <label>Annual Income *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.annualIncome ? "is-invalid" : "")
            }
            value={this.state.annualIncome}
            onChange={(e) => this.setState({ annualIncome: e.target.value })}
          >
            <option value="">Select Income</option>
            {AMOUNT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("annualIncome")}
        </div>
        <div className="form-group col-md-3">
          <label>Net Worth *</label>
          <select
            className={
              "form-control " + (this.state.errors.netWorth ? "is-invalid" : "")
            }
            value={this.state.netWorth}
            onChange={(e) => this.setState({ netWorth: e.target.value })}
          >
            <option value="">Select Net Worth</option>
            {AMOUNT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("netWorth")}
        </div>
        <div className="form-group col-md-3">
          <label>Employment Category *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.employmentCategory ? "is-invalid" : "")
            }
            value={this.state.employmentCategory}
            onChange={(e) =>
              this.setState({ employmentCategory: e.target.value })
            }
          >
            <option value="">Select Category</option>
            <option value="Employee">Employee</option>
            <option value="Self Employed">Self Employed</option>
            <option value="Business Owner">Business Owner</option>
            <option value="Retired">Retired</option>
            <option value="Student">Student</option>
            <option value="Unemployed">Unemployed</option>
          </select>
          {this.renderError("employmentCategory")}
        </div>
        <div className="form-group col-md-3">
          <label>Employment Type *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.employmentType ? "is-invalid" : "")
            }
            value={this.state.employmentType}
            onChange={(e) => this.setState({ employmentType: e.target.value })}
          >
            <option value="">Select Type</option>
            <option value="Full time">Full Time</option>
            <option value="Part time">Part Time</option>
            <option value="Contract">Contract</option>
            <option value="Freelance">Freelance</option>
          </select>
          {this.renderError("employmentType")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-3">
          <label>Are you a PEP? *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.pepStatus ? "is-invalid" : "")
            }
            value={this.state.pepStatus}
            onChange={(e) => this.setState({ pepStatus: e.target.value })}
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("pepStatus")}
        </div>
        <div className="form-group col-md-3">
          <label>Banking Partner *</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.bankingPartner ? "is-invalid" : "")
            }
            value={this.state.bankingPartner}
            onChange={(e) => {
              if (e.target.value.length <= 100) {
                this.setState({ bankingPartner: e.target.value });
              }
            }}
            onKeyDown={this.onlyAllowedInputBankPartner}
            placeholder="Enter Banking Partner"
            maxLength="100"
          />
          {this.renderError("bankingPartner")}
        </div>
        <div className="form-group col-md-3">
          <label>Banking Relationship Duration *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.bankingDuration ? "is-invalid" : "")
            }
            value={this.state.bankingDuration}
            onChange={(e) => this.setState({ bankingDuration: e.target.value })}
          >
            <option value="">Select Duration</option>
            {BANKING_DURATION.map((dur) => (
              <option key={dur.value} value={dur.value}>
                {dur.label}
              </option>
            ))}
          </select>
          {this.renderError("bankingDuration")}
        </div>
        <div className="form-group col-md-3">
          <label>Purpose of Account *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.purposeAccount ? "is-invalid" : "")
            }
            value={this.state.purposeAccount}
            onChange={(e) => this.setState({ purposeAccount: e.target.value })}
          >
            <option value="">Select Purpose</option>
            <option value="Day Trading">Day Trading</option>
            <option value="Investing">Investing</option>
            <option value="Cross-border payments">Cross-border payments</option>
            <option value="Domestic payments">Domestic payments</option>
            <option value="Lowering taxes">Lowering Taxes</option>
          </select>
          {this.renderError("purposeAccount")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-3">
          <label>Acting on behalf of third party? *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.thirdParty ? "is-invalid" : "")
            }
            value={this.state.thirdParty}
            onChange={(e) => this.setState({ thirdParty: e.target.value })}
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("thirdParty")}
        </div>
      </div>
      <div className="d-flex gap-2">
        <button className="btn btn-secondary" onClick={this.handleBack}>
          Back
        </button>
        <button className="btn btn-save-next" onClick={this.handleSaveAndNext}>
          Save and Next
        </button>
      </div>
    </div>
  );

  renderAddressProofStep = () => (
    <div className="kyc-form-card">
      <h5>Document Upload - Address Proof</h5>
      <p className="form-hint">
        Utility Bill or Bank Account Statement from the last 3 months
      </p>
      <div className="row">
        <div className="form-group col-md-6">
          <label>Address Proof Document *</label>
          <input
            key="address-proof-file"
            type="file"
            className="form-control"
            ref={this.addressProofInputRef}
            accept={ALLOWED_DOC_TYPES}
            onChange={(e) =>
              this.handleFileChange(
                e,
                "addressProofFile",
                "addressProofFileName",
                ALLOWED_DOC_TYPES
              )
            }
          />
          {this.state.addressProofFileName && (
            <small className="text-success mt-1 d-block">
              <i className="fas fa-file mr-1" />
              {this.state.addressProofFileName}
            </small>
          )}
          {this.renderError("addressProofFile")}
          <small className="text-muted d-block mt-1">
            Max 6MB. Accepted: JPG, PNG, PDF, DOC, DOCX, JFIF, HEIC
          </small>
        </div>
      </div>
      <div className="d-flex gap-2">
        <button className="btn btn-secondary" onClick={this.handleBack}>
          Back
        </button>
        <button className="btn btn-save-next" onClick={this.handleSaveAndNext}>
          Save and Next
        </button>
      </div>
    </div>
  );

  renderIdDocumentStep = () => {
    const isIndia =
      this.state.country === "India" || this.state.country === "IN";
    return (
      <div className="kyc-form-card">
        <h5>Document Upload - ID Proof</h5>
        {isIndia && (
          <div className="row mb-3">
            <div className="form-group col-md-4">
              <label>PAN Number *</label>
              <input
                type="text"
                key="pan-number"
                className="form-control"
                value={this.state.panNumber}
                onChange={(e) =>
                  this.setState({ panNumber: e.target.value.toUpperCase() })
                }
                placeholder="ABCDE1234F"
                maxLength="10"
              />
            </div>
            <div className="form-group col-md-4">
              <label>PAN Card Photo *</label>
              <input
                type="file"
                key="pan-card-file"
                className="form-control"
                ref={this.panCardInputRef}
                accept={ALLOWED_IMAGE_TYPES}
                onChange={(e) =>
                  this.handleFileChange(
                    e,
                    "panCardPhotoFile",
                    "panCardPhotoFileName",
                    ALLOWED_IMAGE_TYPES
                  )
                }
              />
              {this.state.panCardPhotoFileName && (
                <small className="text-success">
                  {this.state.panCardPhotoFileName}
                </small>
              )}
            </div>
          </div>
        )}
        <div className="row">
          <div className="form-group col-md-4">
            <label>ID Proof Front *</label>
            <input
              type="file"
              key="id-front-file"
              className="form-control"
              ref={this.idDocFrontInputRef}
              accept={ALLOWED_IMAGE_TYPES}
              onChange={(e) =>
                this.handleFileChange(
                  e,
                  "idDocumentFrontFile",
                  "idDocumentFrontFileName",
                  ALLOWED_IMAGE_TYPES
                )
              }
            />
            {this.state.idDocumentFrontFileName && (
              <small className="text-success mt-1 d-block">
                <i className="fas fa-file-image mr-1" />
                {this.state.idDocumentFrontFileName}
              </small>
            )}
            {this.renderError("idDocumentFrontFile")}
          </div>
          <div className="form-group col-md-4">
            <label>ID Proof Back *</label>
            <input
              type="file"
              key="id-back-file"
              className="form-control"
              ref={this.idDocBackInputRef}
              accept={ALLOWED_IMAGE_TYPES}
              onChange={(e) =>
                this.handleFileChange(
                  e,
                  "idDocumentBackFile",
                  "idDocumentBackFileName",
                  ALLOWED_IMAGE_TYPES
                )
              }
            />
            {this.state.idDocumentBackFileName && (
              <small className="text-success mt-1 d-block">
                <i className="fas fa-file-image mr-1" />
                {this.state.idDocumentBackFileName}
              </small>
            )}
            {this.renderError("idDocumentBackFile")}
          </div>
        </div>
        <small className="text-muted d-block mb-3">
          Max 6MB per file. Accepted: JPG, PNG, JFIF, HEIC
        </small>
        <div className="d-flex gap-2">
          <button className="btn btn-secondary" onClick={this.handleBack}>
            Back
          </button>
          <button
            className="btn btn-save-next"
            onClick={this.handleSaveAndNext}
          >
            Save and Next
          </button>
        </div>
      </div>
    );
  };

  renderPhotoVerificationStep = () => {
    const vc = { width: 480, height: 360, facingMode: "user" };
    return (
      <div className="kyc-form-card">
        <h5>Photo Verification</h5>
        <p className="form-hint">
          Take a clear selfie or scan the QR code to capture from your mobile
          device.
        </p>
        <div className="row">
          <div className="col-md-7 mb-3">
            <div className="card border">
              <div className="card-body text-center">
                {!this.state.showWebcam && !this.state.capturedPhoto && (
                  <div className="py-4">
                    <i
                      className="fas fa-camera text-muted mb-3"
                      style={{ fontSize: "48px" }}
                    />
                    <p className="text-muted">Click below to open camera</p>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        this.setState({ showWebcam: true });
                        setTimeout(this.startFaceDetection, 500);
                      }}
                    >
                      <i className="fas fa-camera mr-2" />
                      Open Camera
                    </button>
                  </div>
                )}
                {this.state.showWebcam && !this.state.capturedPhoto && (
                  <div>
                    <div style={{ position: "relative" }}>
                      <Webcam
                        ref={this.webcamRef}
                        audio={false}
                        screenshotFormat="image/jpeg"
                        videoConstraints={vc}
                        className="rounded"
                        style={{ width: "100%", maxWidth: "400px" }}
                      />
                      <canvas
                        ref={this.canvasRef}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          maxWidth: "400px",
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                    {!this.state.faceDetected && (
                      <p className="text-warning mt-2">
                        <i className="fas fa-exclamation-triangle mr-1" />
                        No face detected
                      </p>
                    )}
                    {this.state.faceDetected && (
                      <p className="text-success mt-2">
                        <i className="fas fa-check-circle mr-1" />
                        Face detected
                      </p>
                    )}
                    <div className="mt-3">
                      <button
                        className="btn btn-primary mr-2"
                        onClick={this.capturePhoto}
                        disabled={!this.state.faceDetected}
                      >
                        <i className="fas fa-camera mr-1" /> Capture
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          this.setState({ showWebcam: false });
                          this.stopFaceDetection();
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {this.state.capturedPhoto && (
                  <div>
                    <img
                      src={this.state.capturedPhoto}
                      alt="Captured"
                      className="rounded mb-3"
                      style={{ width: "100%", maxWidth: "400px" }}
                    />
                    {this.state.faceDetected && (
                      <p className="text-success">
                        <i className="fas fa-check-circle mr-1" />
                        Face detected
                      </p>
                    )}
                    {!this.state.faceDetected && (
                      <p className="text-danger">
                        <i className="fas fa-exclamation-circle mr-1" />
                        No face detected - please retake
                      </p>
                    )}
                    <div className="mt-2">
                      <button
                        className="btn btn-primary mr-2"
                        onClick={this.uploadPhotoAndSubmit}
                        disabled={!this.state.faceDetected}
                      >
                        <i className="fas fa-paper-plane mr-1" /> Submit KYC
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-5 mb-3">
            <div className="card border">
              <div className="card-body text-center">
                <h6 className="mb-3">Or use your mobile</h6>
                <p className="text-muted" style={{ fontSize: "13px" }}>
                  Scan this QR code with your phone to take a photo.
                </p>
                <button
                  className="btn btn-outline-primary mb-3"
                  onClick={this.generateQr}
                >
                  <i className="fas fa-qrcode mr-2" />
                  Generate QR Code
                </button>
                {this.state.showQR && (
                  <div className="mt-3">
                    <div
                      className="d-inline-block p-3 bg-white rounded"
                      style={{ border: "1px solid var(--border-color)" }}
                    >
                      <QRCode value={this.getQrValue()} size={160} />
                    </div>
                    <p className="mt-2 text-muted">
                      Code expires in: {this.formatTime(this.state.qrCount)}
                    </p>
                    <p className="text-info" style={{ fontSize: "12px" }}>
                      <i className="fas fa-sync-alt fa-spin mr-1" />
                      Waiting for photo from mobile device...
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-secondary" onClick={this.handleBack}>
            Back
          </button>
        </div>
      </div>
    );
  };

  /* =============================================
     HELPER: render address block (registered or office)
     ============================================= */
  renderAddressBlock = (prefix, labelPrefix, errPrefix, onChangeSameAs) => {
    const isReg = prefix === "companyReg";
    const addrKey = isReg ? "companyregAddress" : "companyOfficeAddress";
    const cityKey = isReg ? "companyregCity" : "companyOfficeCity";
    const stateKey = isReg ? "companyregState" : "companyOfficeState";
    const countryKey = isReg ? "companyregCountry" : "companyOfficeCountry";
    const zipKey = isReg ? "companyregZip" : "companyOfficeZip";
    const premiseTypeKey = isReg
      ? "companyRegPremiseType"
      : "companyOfficePremiseType";
    const yearsLocationKey = isReg
      ? "companyRegYearsInThisLocation"
      : "companyOfficeYearsInThisLocation";
    const premiseOwnerKey = isReg
      ? "companyRegPremiseOwner"
      : "companyOfficePremiseOwner";
    const areaZonedKey = isReg
      ? "companyRegAreaZoned"
      : "companyOfficeAreaZoned";
    const sqFtKey = isReg
      ? "companyRegSquareFootage"
      : "companyOfficeSquareFootage";

    return (
      <>
        <div className="row">
          <div className="form-group col-md-6">
            <label>Street Address *</label>
            <input
              type="text"
              className={
                "form-control " +
                (this.state.errors[addrKey] ? "is-invalid" : "")
              }
              value={this.state[addrKey]}
              onChange={(e) => this.setState({ [addrKey]: e.target.value })}
              onKeyDown={this.allowOnlyAlphaNumSlash}
              placeholder="Enter Address"
            />
            {this.renderError(addrKey)}
          </div>
          <div className="form-group col-md-6">
            <label>City *</label>
            <input
              type="text"
              className={
                "form-control " +
                (this.state.errors[cityKey] ? "is-invalid" : "")
              }
              value={this.state[cityKey]}
              onChange={(e) => this.setState({ [cityKey]: e.target.value })}
              onKeyDown={this.allowOnlyAlphabetsCityState}
              onPaste={this.preventPasteCityState}
              placeholder="Enter City"
            />
            {this.renderError(cityKey)}
          </div>
        </div>
        <div className="row">
          <div className="form-group col-md-6">
            <label>State *</label>
            <input
              type="text"
              className={
                "form-control " +
                (this.state.errors[stateKey] ? "is-invalid" : "")
              }
              value={this.state[stateKey]}
              onChange={(e) => this.setState({ [stateKey]: e.target.value })}
              onKeyDown={this.allowOnlyAlphabetsCityState}
              onPaste={this.preventPasteCityState}
              placeholder="Enter State"
            />
            {this.renderError(stateKey)}
          </div>
          <div className="form-group col-md-6">
            <label>Country *</label>
            <select
              className={
                "form-control " +
                (this.state.errors[countryKey] ? "is-invalid" : "")
              }
              value={this.state[countryKey]}
              onChange={(e) => this.setState({ [countryKey]: e.target.value })}
            >
              <option value="">Select Country</option>
              {this.state.countries.map((c) => (
                <option key={c.code} value={c.country}>
                  {c.country}
                </option>
              ))}
            </select>
            {this.renderError(countryKey)}
          </div>
        </div>
        <div className="row">
          <div className="form-group col-md-6">
            <label>Zip Code *</label>
            <input
              type="text"
              className={
                "form-control " +
                (this.state.errors[zipKey] ? "is-invalid" : "")
              }
              value={this.state[zipKey]}
              onChange={(e) => this.setState({ [zipKey]: e.target.value })}
              onKeyDown={this.onlyAllowedZipCode}
              placeholder="Enter Zip Code"
              maxLength="15"
            />
            {this.renderError(zipKey)}
          </div>
        </div>
        <div className="row">
          <div className="form-group col-md-6">
            <label>Premise Type *</label>
            <select
              className={
                "form-control " +
                (this.state.errors[premiseTypeKey] ? "is-invalid" : "")
              }
              value={this.state[premiseTypeKey]}
              onChange={(e) =>
                this.setState({ [premiseTypeKey]: e.target.value })
              }
            >
              <option value="">Select Premise Type</option>
              {PREMISE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {this.renderError(premiseTypeKey)}
          </div>
          <div className="form-group col-md-6">
            <label>Years in this Location *</label>
            <select
              className={
                "form-control " +
                (this.state.errors[yearsLocationKey] ? "is-invalid" : "")
              }
              value={this.state[yearsLocationKey]}
              onChange={(e) =>
                this.setState({ [yearsLocationKey]: e.target.value })
              }
            >
              <option value="">Select Years in Location</option>
              {YEARS_IN_LOCATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {this.renderError(yearsLocationKey)}
          </div>
        </div>
        {/* Change #4: New address fields */}
        <div className="row">
          <div className="form-group col-md-4">
            <label>Premise Owner *</label>
            <select
              className={
                "form-control " +
                (this.state.errors[premiseOwnerKey] ? "is-invalid" : "")
              }
              value={this.state[premiseOwnerKey]}
              onChange={(e) =>
                this.setState({ [premiseOwnerKey]: e.target.value })
              }
            >
              <option value="">Select</option>
              <option value="Rental">Rental</option>
              <option value="Owned">Owned</option>
            </select>
            {this.renderError(premiseOwnerKey)}
          </div>
          <div className="form-group col-md-4">
            <label>Area Zoned *</label>
            <select
              className={
                "form-control " +
                (this.state.errors[areaZonedKey] ? "is-invalid" : "")
              }
              value={this.state[areaZonedKey]}
              onChange={(e) =>
                this.setState({ [areaZonedKey]: e.target.value })
              }
            >
              <option value="">Select</option>
              <option value="Commercial">Commercial</option>
              <option value="Industrial">Industrial</option>
              <option value="Residential">Residential</option>
            </select>
            {this.renderError(areaZonedKey)}
          </div>
          <div className="form-group col-md-4">
            <label>Square Footage *</label>
            <div className="position-relative">
              <input
                type="text"
                className={
                  "form-control " +
                  (this.state.errors[sqFtKey] ? "is-invalid" : "")
                }
                value={this.state[sqFtKey]}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  this.setState({ [sqFtKey]: val });
                }}
                onKeyDown={(e) => this.onlyAllowDigits(e, 1000000)}
                placeholder="Enter square footage"
                maxLength="7"
                style={{ paddingRight: "50px" }}
              />
              <span
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6c757d",
                  pointerEvents: "none",
                  fontSize: "0.875rem",
                }}
              >
                sq ft
              </span>
            </div>
            {this.renderError(sqFtKey)}
          </div>
        </div>
      </>
    );
  };

  /* =============================================
     ENTERPRISE — COMPANY INFO STEP (UPDATED)
     ============================================= */
  renderCompanyInfoStep = () => (
    <div className="kyc-form-card">
      <h5>Company Information</h5>
      <div className="row">
        <div className="form-group col-md-6">
          <label>Company Name *</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.companyName ? "is-invalid" : "")
            }
            value={this.state.companyName}
            onChange={(e) => this.setState({ companyName: e.target.value })}
            onKeyDown={this.allowOnlyAlphaNumSlash}
            placeholder="Enter Company Name"
          />
          {this.renderError("companyName")}
        </div>
        <div className="form-group col-md-6">
          <label>Registration Number *</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.companyRegNumber ? "is-invalid" : "")
            }
            value={this.state.companyRegNumber}
            onChange={(e) =>
              this.setState({ companyRegNumber: e.target.value })
            }
            onKeyDown={this.allowOnlyAlphaNumSlash}
            placeholder="Enter Registration Number"
          />
          {this.renderError("companyRegNumber")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-6">
          <label>Company Website</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.companyWebsite ? "is-invalid" : "")
            }
            value={this.state.companyWebsite}
            onChange={(e) => this.setState({ companyWebsite: e.target.value })}
            onKeyDown={this.allowWebsiteUrl}
            placeholder="example.com"
          />
          {this.renderError("companyWebsite")}
        </div>
        <div className="form-group col-md-6">
          <label>Country of Incorporation *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.incorporationCountry ? "is-invalid" : "")
            }
            value={this.state.incorporationCountry}
            onChange={(e) =>
              this.setState({ incorporationCountry: e.target.value })
            }
          >
            <option value="">Select Country</option>
            {this.state.countries.map((c) => (
              <option key={c.code} value={c.country}>
                {c.country}
              </option>
            ))}
          </select>
          {this.renderError("incorporationCountry")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-6">
          <label>Corporate Structure *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.corporateStructure ? "is-invalid" : "")
            }
            value={this.state.corporateStructure}
            onChange={(e) =>
              this.setState({ corporateStructure: e.target.value })
            }
          >
            <option value="">Select Structure</option>
            {CORPORATE_STRUCTURES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
          {this.renderError("corporateStructure")}
        </div>
        <div className="form-group col-md-6">
          <label>
            Business Activity *
            <span
              className="ml-2 mcc-recommended-tag"
              onClick={() => this.setState({ showMccInfo: true })}
            >
              Recommended High-risk MCC
            </span>
          </label>
          <div className="searchable-dropdown-wrapper">
            <input
              type="text"
              id="business_activity_search_field_unique"
              name="business_activity_search_field_unique"
              autoComplete="one-time-code"
              autoCorrect="off"
              spellCheck="false"
              className={
                "form-control " +
                (this.state.errors.businessActivity ? "is-invalid" : "")
              }
              value={
                this.state.businessActivity ||
                this.state.businessActivitySearchTerm
              }
              onChange={(e) => {
                this.setState({
                  businessActivity: "",
                  businessActivitySearchTerm: e.target.value,
                  businessActivityCode: "",
                  showBusinessActivityDropdown: true,
                });
              }}
              onFocus={() =>
                this.setState({ showBusinessActivityDropdown: true })
              }
              onBlur={() => {
                // Small delay to allow clicking on an item
                setTimeout(() => {
                  this.setState({ showBusinessActivityDropdown: false });
                }, 200);
              }}
              placeholder="Search or Enter Business Activity"
            />
            {this.state.showBusinessActivityDropdown && (
              <div className="searchable-dropdown-list">
                {(() => {
                  const filtered = this.state.businessActivities.filter(
                    (item) => {
                      const term =
                        this.state.businessActivitySearchTerm.toLowerCase();
                      const desc = item.DESCRIPTION.toLowerCase();
                      const code = (item.MCC || "").toString();
                      return desc.includes(term) || code.includes(term);
                    }
                  );

                  if (filtered.length === 0) {
                    return (
                      <div className="searchable-dropdown-item no-results">
                        No activities found
                      </div>
                    );
                  }

                  return filtered.map((item, idx) => {
                    const label = item.DESCRIPTION;
                    const code = item.MCC;
                    return (
                      <div
                        key={idx}
                        className="searchable-dropdown-item"
                        onMouseDown={() => {
                          this.setState({
                            businessActivity: `${code} - ${label}`,
                            businessActivityCode: code,
                            businessActivitySearchTerm: label,
                            showBusinessActivityDropdown: false,
                            errors: {
                              ...this.state.errors,
                              businessActivity: "",
                            },
                          });
                        }}
                      >
                        {`${code} - ${label}`}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
          {this.renderError("businessActivity")}
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-6">
          <label>DBA Name *</label>
          <input
            type="text"
            className={
              "form-control " + (this.state.errors.dbaName ? "is-invalid" : "")
            }
            value={this.state.dbaName}
            onChange={(e) => this.setState({ dbaName: e.target.value })}
            onKeyDown={this.allowOnlyAlphabetsCityState}
            onPaste={this.preventPasteCityState}
            placeholder="Enter DBA (Doing Business As) Name"
          />
          {this.renderError("dbaName")}
        </div>
        <div className="form-group col-md-6">
          <label>Business Phone Number *</label>
          <PhoneInput
            country={"us"}
            value={this.state.businessPhoneNumber}
            onChange={(phone) => this.setState({ businessPhoneNumber: phone })}
            inputProps={{
              className:
                "form-control " +
                (this.state.errors.businessPhoneNumber ? "is-invalid" : ""),
              style: { width: "100%", height: "38px", paddingLeft: "48px" },
            }}
            containerStyle={{ width: "100%" }}
            inputStyle={{ width: "100%", borderRadius: "4px" }}
            buttonStyle={{
              borderRadius: "4px 0 0 4px",
              border: "1px solid #ced4da",
              borderRight: "none",
            }}
          />
          {this.renderError("businessPhoneNumber")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-6">
          <label>Business Email Address *</label>
          <input
            type="email"
            className={
              "form-control " +
              (this.state.errors.businessEmailAddress ? "is-invalid" : "")
            }
            value={this.state.businessEmailAddress}
            onChange={(e) =>
              this.setState({ businessEmailAddress: e.target.value })
            }
            placeholder="Enter Business Email Address"
            maxLength="100"
          />
          {this.renderError("businessEmailAddress")}
        </div>
        <div className="form-group col-md-6">
          <label>Years in Business *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.yearsInBusiness ? "is-invalid" : "")
            }
            value={this.state.yearsInBusiness}
            onChange={(e) => this.setState({ yearsInBusiness: e.target.value })}
          >
            <option value="">Select Years in Business</option>
            {YEARS_IN_BUSINESS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("yearsInBusiness")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-12">
          <label>Business Description *</label>
          <textarea
            className={
              "form-control " +
              (this.state.errors.businessDescription ? "is-invalid" : "")
            }
            value={this.state.businessDescription}
            onChange={(e) =>
              this.setState({ businessDescription: e.target.value })
            }
            onKeyDown={this.handleBusinessDescriptionKeyDown}
            onPaste={(e) =>
              this.handlePasteWithRegex(
                e,
                /^[a-zA-Z0-9(),\-&.' \r\n]*$/,
                350
              )
            }
            rows="3"
            maxLength="350"
            placeholder="Describe your business activities (max 350 characters)"
          />{" "}
          {this.renderError("businessDescription")}
        </div>
      </div>

      {/* Change #5: Stock Exchange fields */}
      <div className="row">
        <div className="form-group col-md-6">
          <label>Stock Exchange Name</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.stockExchangeName ? "is-invalid" : "")
            }
            value={this.state.stockExchangeName}
            onChange={(e) =>
              this.setState({ stockExchangeName: e.target.value })
            }
            onKeyDown={this.onlyAllowedStockExchangeName}
            maxLength="100"
            placeholder="e.g. NYSE, NASDAQ"
          />
          {this.renderError("stockExchangeName")}
        </div>
        <div className="form-group col-md-6">
          <label>Stock Ticker Symbol</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.stockTickerSymbol ? "is-invalid" : "")
            }
            value={this.state.stockTickerSymbol}
            onChange={(e) =>
              this.setState({ stockTickerSymbol: e.target.value.toUpperCase() })
            }
            onKeyDown={this.onlyAllowedStockTicker}
            maxLength="10"
            style={{ textTransform: "uppercase" }}
            placeholder="e.g. AAPL"
          />
          {this.renderError("stockTickerSymbol")}
        </div>
      </div>

      {/* Change #6: Tax Information */}
      <h5 className="mt-3">Tax Information</h5>
      <div className="row">
        <div className="form-group col-md-6">
          <label>
            Select the tax identification number applicable to you or your
            business *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.taxIdType ? "is-invalid" : "")
            }
            value={this.state.taxIdType}
            onChange={(e) => this.setState({ taxIdType: e.target.value })}
          >
            <option value="">Select Tax ID Type</option>
            {TAX_ID_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          {this.renderError("taxIdType")}
        </div>
        <div className="form-group col-md-6">
          <label>Taxpayer Identification Number (TIN) *</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.taxpayerIdNumber ? "is-invalid" : "")
            }
            value={this.state.taxpayerIdNumber}
            onChange={(e) =>
              this.setState({ taxpayerIdNumber: e.target.value })
            }
            onKeyDown={this.onlyAllowedTIN}
            maxLength="20"
            placeholder="Enter your EIN, SSN, or ITIN"
          />
          {this.renderError("taxpayerIdNumber")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-6">
          <label>Are you an Exempt Payee? *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.isExemptPayee ? "is-invalid" : "")
            }
            value={this.state.isExemptPayee}
            onChange={(e) => this.setState({ isExemptPayee: e.target.value })}
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("isExemptPayee")}
        </div>
        <div className="form-group col-md-6">
          <label>Are you a 501(c)(3) Nonprofit Organization? *</label>

          <select
            className={
              "form-control " +
              (this.state.errors.isNonprofit ? "is-invalid" : "")
            }
            value={this.state.isNonprofit}
            onChange={(e) => this.setState({ isNonprofit: e.target.value })}
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          <small className="form-text text-muted mb-2 d-block">
            Public charities and private foundations organized for religious,
            educational, charitable, scientific, literary, public safety, or
            amateur sports purposes.
          </small>
          {this.renderError("isNonprofit")}
        </div>
      </div>

      {/* REGISTERED ADDRESS */}
      <h5 className="mt-3">Registered Address</h5>
      {this.renderAddressBlock("companyReg", "Registered", "companyReg")}

      <div className="form-check mb-3">
        <input
          type="checkbox"
          className="form-check-input"
          id="sameAddress"
          checked={this.state.sameAddress}
          onChange={(e) => this.setState({ sameAddress: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="sameAddress">
          Office address same as registered address
        </label>
      </div>

      {!this.state.sameAddress && (
        <>
          <h5 className="mt-3">Office Address</h5>
          {this.renderAddressBlock("companyOffice", "Office", "companyOffice")}
        </>
      )}

      {/* Change #4: Number of Locations (independent field) */}
      <div className="row">
        <div className="form-group col-md-4">
          <label>Number of Locations *</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.numberOfLocations ? "is-invalid" : "")
            }
            value={this.state.numberOfLocations}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, "");
              this.setState({ numberOfLocations: val });
            }}
            onKeyDown={(e) => this.onlyAllowDigits(e, 10000)}
            placeholder="e.g. 5"
            maxLength="5"
          />
          {this.renderError("numberOfLocations")}
        </div>
      </div>

      {/* FINANCIAL INFORMATION */}
      <h5 className="mt-3">Financial Information</h5>
      <div className="row">
        <div className="form-group col-md-6">
          <label>Annual Revenue *</label>
          <select
            className={
              "form-control " + (this.state.errors.revenue ? "is-invalid" : "")
            }
            value={this.state.revenue}
            onChange={(e) => this.setState({ revenue: e.target.value })}
          >
            <option value="">Select Revenue</option>
            {AMOUNT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("revenue")}
        </div>
        <div className="form-group col-md-6">
          <label>Profit *</label>
          <select
            className={
              "form-control " + (this.state.errors.profit ? "is-invalid" : "")
            }
            value={this.state.profit}
            onChange={(e) => this.setState({ profit: e.target.value })}
          >
            <option value="">Select Profit</option>
            {AMOUNT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("profit")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-6">
          <label>Total Company Assets *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.companyAssets ? "is-invalid" : "")
            }
            value={this.state.companyAssets}
            onChange={(e) => this.setState({ companyAssets: e.target.value })}
          >
            <option value="">Select Assets</option>
            {AMOUNT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("companyAssets")}
        </div>
        <div className="form-group col-md-6">
          <label>Company Net Worth *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.companyNetWorth ? "is-invalid" : "")
            }
            value={this.state.companyNetWorth}
            onChange={(e) => this.setState({ companyNetWorth: e.target.value })}
          >
            <option value="">Select Net Worth</option>
            {COMPANY_NET_WORTH_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("companyNetWorth")}
        </div>
      </div>

      {/* BANKING AND TRANSACTION DETAILS */}
      <h5 className="mt-3">Banking and Transaction Details</h5>
      <div className="row">
        <div className="form-group col-md-6">
          <label>Account Purpose *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.accountPurpose ? "is-invalid" : "")
            }
            value={this.state.accountPurpose}
            onChange={(e) => this.setState({ accountPurpose: e.target.value })}
          >
            <option value="">Select Purpose</option>
            <option value="Trading">Trading</option>
            <option value="Investment">Investment</option>
            <option value="Payment Processing">Payment Processing</option>
            <option value="Treasury Management">Treasury Management</option>
            <option value="Other">Other</option>
          </select>
          {this.renderError("accountPurpose")}
        </div>
        <div className="form-group col-md-6">
          <label>Source of Investment *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.investmentSource ? "is-invalid" : "")
            }
            value={this.state.investmentSource}
            onChange={(e) =>
              this.setState({ investmentSource: e.target.value })
            }
          >
            <option value="">Select Source</option>
            <option value="Company Revenue">Company Revenue</option>
            <option value="Investor Funding">Investor Funding</option>
            <option value="Bank Loan">Bank Loan</option>
            <option value="Asset Sale">Asset Sale</option>
            <option value="Other">Other</option>
          </select>
          {this.renderError("investmentSource")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-6">
          <label>Monthly Transaction Volume *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.transactionVolumes ? "is-invalid" : "")
            }
            value={this.state.transactionVolumes}
            onChange={(e) =>
              this.setState({ transactionVolumes: e.target.value })
            }
          >
            <option value="">Select Volume</option>
            {AMOUNT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("transactionVolumes")}
        </div>
        <div className="form-group col-md-6">
          <label>Transaction Frequency *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.transactionFrequency ? "is-invalid" : "")
            }
            value={this.state.transactionFrequency}
            onChange={(e) =>
              this.setState({ transactionFrequency: e.target.value })
            }
          >
            <option value="">Select Frequency</option>
            {TRANSACTION_FREQUENCY.map((freq) => (
              <option key={freq.value} value={freq.value}>
                {freq.label}
              </option>
            ))}
          </select>
          {this.renderError("transactionFrequency")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-6">
          <label>Banking Partner *</label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.bankingPartner ? "is-invalid" : "")
            }
            value={this.state.bankingPartner}
            onChange={(e) => {
              if (e.target.value.length <= 100) {
                this.setState({ bankingPartner: e.target.value });
              }
            }}
            onKeyDown={this.onlyAllowedInputBankPartner}
            placeholder="Enter Banking Partner"
            maxLength="100"
          />
          {this.renderError("bankingPartner")}
        </div>
        <div className="form-group col-md-6">
          <label>Banking Relationship Duration *</label>
          <select
            className={
              "form-control " +
              (this.state.errors.relationWithBank ? "is-invalid" : "")
            }
            value={this.state.relationWithBank}
            onChange={(e) =>
              this.setState({ relationWithBank: e.target.value })
            }
          >
            <option value="">Select Duration</option>
            {BANKING_DURATION.map((dur) => (
              <option key={dur.value} value={dur.value}>
                {dur.label}
              </option>
            ))}
          </select>
          {this.renderError("relationWithBank")}
        </div>
      </div>

      {/* PROCESSING INFORMATION */}
      <h5 className="mt-4" style={{ textTransform: "none" }}>
        Processing Information
      </h5>

      <div className="row">
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Are you currently processing credit and debit card transactions? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.isProcessingCardTransaction
                ? "is-invalid"
                : "")
            }
            value={this.state.isProcessingCardTransaction}
            onChange={(e) =>
              this.setState({ isProcessingCardTransaction: e.target.value })
            }
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("isProcessingCardTransaction")}
        </div>
      </div>

      {/* Change #1: Processing methods with individual percentage inputs, total must = 100% */}
      <div className="row">
        <div className="form-group col-md-12">
          <label style={{ textTransform: "none" }}>
            How do you want to process card transactions? *
          </label>
          <small className="form-text text-muted mb-2 d-block">
            Enter the percentage for each processing method. All percentages
            must add up to 100%.
          </small>
          {this.state.errors.cardProcessingPercentages && (
            <div className="alert alert-danger py-2 mb-2">
              {this.state.errors.cardProcessingPercentages}
            </div>
          )}
          <div className="row ">
            {CARD_PROCESSING_METHODS.map((method) => (
              <div key={method.value} className="form-group col-md-3 pl-0 mb-0">
                <label style={{ textTransform: "none" }}>
                  {method.label}
                  {method.value === "Key entered" && (
                    <button
                      type="button"
                      className="btn btn-link p-0 ml-1"
                      style={{
                        color: "var(--primary, #0052ff)",
                        verticalAlign: "middle",
                        lineHeight: "1",
                      }}
                      onClick={() =>
                        this.setState({ showKeyEnteredInfo: true })
                      }
                      title="More info"
                    >
                      <i
                        className="fas fa-info-circle"
                        style={{ fontSize: "inherit" }}
                      />
                    </button>
                  )}{" "}
                  %
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  className={
                    "form-control " +
                    (this.state.errors.cardProcessingPercentages
                      ? "is-invalid"
                      : "")
                  }
                  value={
                    this.state.cardProcessingMethodPercentages[method.value] ||
                    ""
                  }
                  onChange={(e) =>
                    this.handleCardProcessingPercentageChange(
                      method.value,
                      e.target.value
                    )
                  }
                  placeholder="0"
                  maxLength="5"
                />
              </div>
            ))}
          </div>
          {/* Total display */}
          {(() => {
            const total = Object.values(
              this.state.cardProcessingMethodPercentages
            ).reduce((s, v) => s + (parseFloat(v) || 0), 0);
            const isValid = Math.abs(total - 100) < 0.01;
            return (
              <div
                className={`mt-1 small font-weight-bold ${isValid ? "text-success" : "text-danger"
                  }`}
              >
                Total: {total.toFixed(total % 1 === 0 ? 0 : 2)}%{" "}
                {isValid ? (
                  <i className="fas fa-check-circle" />
                ) : (
                  "(must equal 100%)"
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="row processing-info-row mt-2">
        <div
          className="form-group col-md-4"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div style={{ flex: 1 }}>
            <label style={{ textTransform: "none" }}>
              How much in USD do you want to process in card transactions per
              month? *
            </label>
          </div>
          <div style={{ marginTop: "auto" }}>
            <input
              type="text"
              inputMode="numeric"
              className={
                "form-control " +
                (this.state.errors.transactionLimitInUsd ? "is-invalid" : "")
              }
              value={this.state.transactionLimitInUsd}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                if (val.length <= 10)
                  this.setState({ transactionLimitInUsd: val });
              }}
              onKeyDown={(e) => this.onlyAllowDigits(e, 9000000000)}
              placeholder="Enter amount in USD"
            />
            {this.renderError("transactionLimitInUsd")}
          </div>
        </div>
        <div
          className="form-group col-md-4"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div style={{ flex: 1 }}>
            <label style={{ textTransform: "none" }}>
              What is your average transaction size (USD)? *
            </label>
          </div>
          <div style={{ marginTop: "auto" }}>
            <input
              type="text"
              inputMode="numeric"
              className={
                "form-control " +
                (this.state.errors.averageTransactionSizeInUsd
                  ? "is-invalid"
                  : "")
              }
              value={this.state.averageTransactionSizeInUsd}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                if (val.length <= 7)
                  this.setState({ averageTransactionSizeInUsd: val });
              }}
              onKeyDown={(e) => this.onlyAllowDigits(e, 900000)}
              placeholder="Enter average transaction size"
            />
            {this.renderError("averageTransactionSizeInUsd")}
          </div>
        </div>
        <div
          className="form-group col-md-4"
          style={{ display: "flex", flexDirection: "column" }}
        >
          <div style={{ flex: 1 }}>
            <label style={{ textTransform: "none" }}>
              What is your highest transaction size (USD)? *
            </label>
          </div>
          <div style={{ marginTop: "auto" }}>
            <input
              type="text"
              inputMode="numeric"
              className={
                "form-control " +
                (this.state.errors.highestTransactionSizeInUsd
                  ? "is-invalid"
                  : "")
              }
              value={this.state.highestTransactionSizeInUsd}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, "");
                if (val.length <= 8)
                  this.setState({ highestTransactionSizeInUsd: val });
              }}
              onKeyDown={(e) => this.onlyAllowDigits(e, 9000000)}
              placeholder="Enter highest transaction size"
            />
            {this.renderError("highestTransactionSizeInUsd")}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Do you want to accept ACH/eCheck payments? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.acceptAchPayment ? "is-invalid" : "")
            }
            value={this.state.acceptAchPayment}
            onChange={(e) =>
              this.setState({ acceptAchPayment: e.target.value })
            }
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("acceptAchPayment")}
        </div>
      </div>

      {/* Change #2: American Express */}
      <div className="row">
        <div className="form-group col-md-12">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="acceptAmex"
              checked={this.state.acceptAmexPayment}
              onChange={(e) =>
                this.setState({ acceptAmexPayment: e.target.checked })
              }
            />
            <label
              className="form-check-label"
              htmlFor="acceptAmex"
              style={{ textTransform: "none" }}
            >
              Would you like to accept American Express payments? *
            </label>
          </div>
        </div>
      </div>
      {this.state.acceptAmexPayment && (
        <div className="row">
          <div className="form-group col-md-4">
            <label style={{ textTransform: "none" }}>
              Amex Monthly Volume *
            </label>
            <input
              type="text"
              inputMode="numeric"
              className={
                "form-control " +
                (this.state.errors.amexMonthlyVolume ? "is-invalid" : "")
              }
              value={this.state.amexMonthlyVolume}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9,.]/g, "");
                this.setState({ amexMonthlyVolume: val });
              }}
              onKeyDown={this.onlyAllowedAmexVolume}
              placeholder="Enter amount in USD"
              maxLength="15"
            />
            {this.renderError("amexMonthlyVolume")}
          </div>
          <div className="form-group col-md-4">
            <label style={{ textTransform: "none" }}>Amex Avg Ticket *</label>
            <input
              type="text"
              inputMode="numeric"
              className={
                "form-control " +
                (this.state.errors.amexAvgTicket ? "is-invalid" : "")
              }
              value={this.state.amexAvgTicket}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9,.]/g, "");
                this.setState({ amexAvgTicket: val });
              }}
              onKeyDown={this.onlyAllowedAmexVolume}
              placeholder="Enter amount in USD"
              maxLength="15"
            />
            {this.renderError("amexAvgTicket")}
          </div>
          <div className="form-group col-md-4">
            <label style={{ textTransform: "none" }}>Amex High Ticket *</label>
            <input
              type="text"
              inputMode="numeric"
              className={
                "form-control " +
                (this.state.errors.amexHighTicket ? "is-invalid" : "")
              }
              value={this.state.amexHighTicket}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9,.]/g, "");
                this.setState({ amexHighTicket: val });
              }}
              onKeyDown={this.onlyAllowedAmexVolume}
              placeholder="Enter amount in USD"
              maxLength="15"
            />
            {this.renderError("amexHighTicket")}
          </div>
        </div>
      )}

      {/* Change #3: Processing URLs */}
      <h5 className="mt-4" style={{ textTransform: "none" }}>
        Processing URLs
      </h5>
      <div className="row">
        <div className="form-group col-md-12">
          <label style={{ textTransform: "none" }}>
            What website URL will be used for payment processing? *
          </label>
          <input
            type="text"
            className={
              "form-control " +
              (this.state.errors.paymentProcessingUrl ? "is-invalid" : "")
            }
            value={this.state.paymentProcessingUrl}
            onChange={(e) =>
              this.setState({ paymentProcessingUrl: e.target.value })
            }
            onKeyDown={this.allowWebsiteUrl}
            maxLength="255"
            placeholder="Enter the website URL (e.g. example.com)"
          />
          {this.renderError("paymentProcessingUrl")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-12">
          <label style={{ textTransform: "none" }}>
            If login access is required, please provide demo credentials for our
            admin team. Two-factor authentication (2FA) must be disabled for
            these credentials.
          </label>
          <div className="row">
            <div className="col-md-6 pl-0">
              <label>Username</label>
              <input
                type="text"
                className={
                  "form-control " +
                  (this.state.errors.demoLoginUsername ? "is-invalid" : "")
                }
                value={this.state.demoLoginUsername}
                onChange={(e) =>
                  this.setState({ demoLoginUsername: e.target.value })
                }
                onKeyDown={this.onlyAllowedUsername}
                maxLength="30"
                placeholder="Enter Demo Login Username"
              />
              {this.renderError("demoLoginUsername")}
            </div>
            <div className="col-md-6 pl-0">
              <label>Password</label>
              <input
                type="text"
                className={
                  "form-control " +
                  (this.state.errors.demoLoginPassword ? "is-invalid" : "")
                }
                value={this.state.demoLoginPassword}
                onChange={(e) =>
                  this.setState({ demoLoginPassword: e.target.value })
                }
                onKeyDown={this.onlyAllowedPassword}
                maxLength="35"
                placeholder="Enter Demo Login Password"
              />
              {this.renderError("demoLoginPassword")}
            </div>
          </div>
        </div>
      </div>

      {/* Change #7: Business Operations */}
      <h5 className="mt-4" style={{ textTransform: "none" }}>
        Business Operations
      </h5>

      <div className="row">
        <div className="form-group col-md-12">
          <label style={{ textTransform: "none" }}>
            How Do You Advertise? *
          </label>
          {this.state.errors.advertiseMethods && (
            <div className="text-danger small mb-1">
              {this.state.errors.advertiseMethods}
            </div>
          )}
          <div className="d-flex flex-wrap gap-3 mt-1">
            {ADVERTISE_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                className="form-check"
                style={{ minWidth: "150px" }}
              >
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`adv-${opt.value}`}
                  checked={(this.state.advertiseMethods || []).includes(
                    opt.value
                  )}
                  onChange={() => this.handleAdvertiseMethodToggle(opt.value)}
                />
                <label
                  className="form-check-label"
                  htmlFor={`adv-${opt.value}`}
                  style={{ textTransform: "none" }}
                >
                  {opt.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Please provide the percentage breakdown of inbound and outbound
            sales *
          </label>
          <div className="d-flex gap-3 mt-1">
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "13px", textTransform: "none" }}>
                Inbound %
              </label>
              <input
                type="text"
                inputMode="numeric"
                className={
                  "form-control " +
                  (this.state.errors.inboundPercent ||
                    this.state.errors.inboundOutboundPercentages
                    ? "is-invalid"
                    : "")
                }
                value={this.state.inboundPercent}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (Number(val) <= 100)
                    this.setState({ inboundPercent: val });
                }}
                placeholder="___%"
              />
              {this.renderError("inboundPercent")}
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "13px", textTransform: "none" }}>
                Outbound %
              </label>
              <input
                type="text"
                inputMode="numeric"
                className={
                  "form-control " +
                  (this.state.errors.outboundPercent ||
                    this.state.errors.inboundOutboundPercentages
                    ? "is-invalid"
                    : "")
                }
                value={this.state.outboundPercent}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (Number(val) <= 100)
                    this.setState({ outboundPercent: val });
                }}
                placeholder="___%"
              />
              {this.renderError("outboundPercent")}
            </div>
          </div>
          {this.renderError("inboundOutboundPercentages")}
        </div>
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Sales Type Distribution (B2B / Retail) *
          </label>
          <div className="d-flex gap-3 mt-1">
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "13px", textTransform: "none" }}>
                B2B %
              </label>
              <input
                type="text"
                inputMode="numeric"
                className={
                  "form-control " +
                  (this.state.errors.b2bPercent ||
                    this.state.errors.b2bRetailPercentages
                    ? "is-invalid"
                    : "")
                }
                value={this.state.b2bPercent}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (Number(val) <= 100) this.setState({ b2bPercent: val });
                }}
                placeholder="___%"
              />
              {this.renderError("b2bPercent")}
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: "13px", textTransform: "none" }}>
                Retail (B2C) %
              </label>
              <input
                type="text"
                inputMode="numeric"
                className={
                  "form-control " +
                  (this.state.errors.retailPercent ||
                    this.state.errors.b2bRetailPercentages
                    ? "is-invalid"
                    : "")
                }
                value={this.state.retailPercent}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (Number(val) <= 100) this.setState({ retailPercent: val });
                }}
                placeholder="___%"
              />
              {this.renderError("retailPercent")}
            </div>
          </div>
          {this.renderError("b2bRetailPercentages")}
        </div>
      </div>

      <div className="row">
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Is your business seasonal? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.isBusinessSeasonal ? "is-invalid" : "")
            }
            value={this.state.isBusinessSeasonal}
            onChange={(e) =>
              this.setState({ isBusinessSeasonal: e.target.value })
            }
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("isBusinessSeasonal")}
        </div>
        {this.state.isBusinessSeasonal === "1" && (
          <div className="form-group col-md-6">
            <label style={{ textTransform: "none" }}>
              Please describe your peak and off-season periods *
            </label>
            <textarea
              className={
                "form-control " +
                (this.state.errors.peakOffSeasonDescription ? "is-invalid" : "")
              }
              value={this.state.peakOffSeasonDescription}
              onChange={(e) =>
                this.setState({ peakOffSeasonDescription: e.target.value })
              }
              onKeyDown={this.allowExplanationKeyDown}
              onPaste={(e) =>
                this.handlePasteWithRegex(
                  e,
                  /^[a-zA-Z0-9\s.,;:'"()\[\]{}\-_/&@!?%#]*$/,
                  3000
                )
              }
              rows="2"
              placeholder="Enter text here"
            />
            {this.renderError("peakOffSeasonDescription")}
          </div>
        )}
      </div>

      <div className="row">
        <div className="form-group col-md-12">
          <label style={{ textTransform: "none" }}>
            What is your Return and Refund Policy? *
          </label>
          <textarea
            className={
              "form-control " +
              (this.state.errors.returnRefundPolicy ? "is-invalid" : "")
            }
            value={this.state.returnRefundPolicy}
            onChange={(e) =>
              this.setState({ returnRefundPolicy: e.target.value })
            }
            onKeyDown={this.allowReturnRefundPolicyKeyDown}
            onPaste={this.handleReturnRefundPolicyPaste}
            rows="2"
            maxLength="255"
            placeholder="Enter the URL of your return and refund policy (e.g. https://example.com/refund)"
          />
          <div className="d-flex justify-content-between">
            {this.renderError("returnRefundPolicy")}
            <small className="text-muted">
              {this.state.returnRefundPolicy.length}/255
            </small>
          </div>
        </div>
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Customers may request refunds within how many days of purchase? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.refundDaysAfterPurchase ? "is-invalid" : "")
            }
            value={this.state.refundDaysAfterPurchase}
            onChange={(e) =>
              this.setState({ refundDaysAfterPurchase: e.target.value })
            }
          >
            <option value="">Select</option>
            {REFUND_DAYS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("refundDaysAfterPurchase")}
        </div>
      </div>
      <div className="row">
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Refunds are processed within how many business days? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.refundProcessingDays ? "is-invalid" : "")
            }
            value={this.state.refundProcessingDays}
            onChange={(e) =>
              this.setState({ refundProcessingDays: e.target.value })
            }
          >
            <option value="">Select</option>
            {REFUND_PROCESSING_DAYS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("refundProcessingDays")}
        </div>
      </div>

      {/* Change #8: Fulfillment & Billing */}
      <h5 className="mt-4" style={{ textTransform: "none" }}>
        Fulfillment &amp; Billing
      </h5>
      <div className="row">
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            When is the card charged? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.cardChargedWhen ? "is-invalid" : "")
            }
            value={this.state.cardChargedWhen}
            onChange={(e) => this.setState({ cardChargedWhen: e.target.value })}
          >
            <option value="">Select</option>
            {CARD_CHARGED_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {this.renderError("cardChargedWhen")}
        </div>
        {this.state.cardChargedWhen === "In Advance" && (
          <div className="form-group col-md-6">
            <label style={{ textTransform: "none" }}>
              Product or Services Generally Provided in how many days after card
              is charged? *
            </label>
            <select
              className="form-control"
              value={this.state.deliveryDaysAfterCharge}
              onChange={(e) =>
                this.setState({ deliveryDaysAfterCharge: e.target.value })
              }
            >
              <option value="">Select</option>
              {DELIVERY_DAYS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="row">
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Do you use a third-party company to fulfill or deliver your products
            or services? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.usesThirdPartyFulfillment ? "is-invalid" : "")
            }
            value={this.state.usesThirdPartyFulfillment}
            onChange={(e) =>
              this.setState({ usesThirdPartyFulfillment: e.target.value })
            }
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("usesThirdPartyFulfillment")}
        </div>
        {this.state.usesThirdPartyFulfillment === "1" && (
          <div className="form-group col-md-6">
            <label style={{ textTransform: "none" }}>
              Please provide the name of the third-party fulfillment company.
            </label>
            <input
              type="text"
              className="form-control"
              value={this.state.thirdPartyFulfillmentName}
              onChange={(e) =>
                this.setState({ thirdPartyFulfillmentName: e.target.value })
              }
              placeholder="Enter third-party fulfillment company names here"
            />
          </div>
        )}
      </div>

      {/* Change #9: Risk & Compliance */}
      <h5 className="mt-4" style={{ textTransform: "none" }}>
        Risk &amp; Compliance
      </h5>
      <div className="row align-items-end">
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Is your business PCI compliant? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.isPciCompliant ? "is-invalid" : "")
            }
            value={this.state.isPciCompliant}
            onChange={(e) => this.setState({ isPciCompliant: e.target.value })}
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("isPciCompliant")}
        </div>
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Business or any associated owner ever been terminated as a
            VISA/MasterCard/Discover/AMEX merchant? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.wasTerminatedMerchant ? "is-invalid" : "")
            }
            value={this.state.wasTerminatedMerchant}
            onChange={(e) =>
              this.setState({ wasTerminatedMerchant: e.target.value })
            }
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("wasTerminatedMerchant")}
        </div>
      </div>
      <div className="row align-items-end">
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Business had any ongoing or prior data compromise investigations? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.hasDataCompromise ? "is-invalid" : "")
            }
            value={this.state.hasDataCompromise}
            onChange={(e) =>
              this.setState({ hasDataCompromise: e.target.value })
            }
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("hasDataCompromise")}
        </div>
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Business was previously identified with Visa Risk Programs? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.wasVisaRiskProgram ? "is-invalid" : "")
            }
            value={this.state.wasVisaRiskProgram}
            onChange={(e) =>
              this.setState({ wasVisaRiskProgram: e.target.value })
            }
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("wasVisaRiskProgram")}
        </div>
      </div>
      <div className="row align-items-end">
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Does any third-party software or platform participate in your
            payment flow? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.usesThirdPartySoftware ? "is-invalid" : "")
            }
            value={this.state.usesThirdPartySoftware}
            onChange={(e) =>
              this.setState({ usesThirdPartySoftware: e.target.value })
            }
          >
            <option value="">Select</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          {this.renderError("usesThirdPartySoftware")}
        </div>
        {this.state.usesThirdPartySoftware === "1" && (
          <div className="form-group col-md-6">
            <label style={{ textTransform: "none" }}>
              If Yes: Please provide the third-party application or platform
              name(s).
            </label>
            <input
              type="text"
              className="form-control"
              value={this.state.thirdPartySoftwareNames}
              onChange={(e) =>
                this.setState({ thirdPartySoftwareNames: e.target.value })
              }
              placeholder="Enter third-party application or platform name(s) here"
            />
          </div>
        )}
      </div>

      {/* Change #10: Bankruptcy */}
      <h6 className="mt-3" style={{ textTransform: "none" }}>
        Bankruptcy
      </h6>
      <div className="row">
        <div className="form-group col-md-6">
          <label style={{ textTransform: "none" }}>
            Have you ever filed for bankruptcy? *
          </label>
          <select
            className={
              "form-control " +
              (this.state.errors.bankruptcyType ? "is-invalid" : "")
            }
            value={this.state.bankruptcyType}
            onChange={(e) => this.setState({ bankruptcyType: e.target.value })}
          >
            <option value="">Select</option>
            <option value="Business Bankruptcy">Business Bankruptcy</option>
            <option value="Personal Bankruptcy">Personal Bankruptcy</option>
            <option value="Never Filed for Bankruptcy">
              Never Filed for Bankruptcy
            </option>
          </select>
          {this.renderError("bankruptcyType")}
        </div>
        {this.state.bankruptcyType &&
          this.state.bankruptcyType !== "Never Filed for Bankruptcy" && (
            <div className="form-group col-md-6">
              <label style={{ textTransform: "none" }}>
                Filing Date (if applicable)
              </label>
              <input
                type="date"
                className="form-control"
                value={this.state.bankruptcyFilingDate}
                onChange={(e) =>
                  this.setState({ bankruptcyFilingDate: e.target.value })
                }
              />
            </div>
          )}
      </div>
      {this.state.bankruptcyType &&
        this.state.bankruptcyType !== "Never Filed for Bankruptcy" && (
          <div className="row">
            <div className="form-group col-md-12">
              <label style={{ textTransform: "none" }}>
                Please explain the circumstances and current status or
                resolution.
              </label>
              <textarea
                className={
                  "form-control " +
                  (this.state.errors.bankruptcyExplanation ? "is-invalid" : "")
                }
                value={this.state.bankruptcyExplanation}
                onChange={(e) =>
                  this.setState({ bankruptcyExplanation: e.target.value })
                }
                onKeyDown={this.allowExplanationKeyDown}
                onPaste={(e) =>
                  this.handlePasteWithRegex(
                    e,
                    /^[a-zA-Z0-9\s.,;:'"()\[\]{}\-_/&@!?%#]*$/,
                    3000
                  )
                }
                rows="3"
                maxLength="3000"
                placeholder="Enter text here"
              />
              {this.renderError("bankruptcyExplanation")}
            </div>
          </div>
        )}

      <div className="d-flex gap-2 mt-3">
        <button className="btn btn-save-next" onClick={this.handleSaveAndNext}>
          Save and Next
        </button>
      </div>
    </div>
  );

  renderCompanyDocumentsStep = () => {
    const BOARD_RESOLUTION_URL =
      process.env.PUBLIC_URL + "/templates/board-resolution-template.docx";
    const documents = [
      {
        key: "incorporationCertificate",
        label: "Certificate of Incorporation *",
        ref: this.incorporationCertInputRef,
      },
      {
        key: "memorandum",
        label: "Memorandum or Operating Agreement (optional)",
        ref: this.memorandumInputRef,
        optional: true,
      },
      {
        key: "associationArticles",
        label: "Articles of Association (optional)",
        ref: this.associationArticlesInputRef,
        optional: true,
      },
      {
        key: "incumbencyCertificate",
        label: "Certificate of Incumbency (optional)",
        ref: this.incumbencyCertInputRef,
        optional: true,
      },
      {
        key: "directorsRegister",
        label: "Register of Directors (optional)",
        ref: this.directorsRegisterInputRef,
        optional: true,
      },
      {
        key: "shareholdersRegister",
        label: "Register of Shareholders (optional)",
        ref: this.shareholdersRegisterInputRef,
        optional: true,
      },
      {
        key: "boardResolution",
        label: "Board Resolution (optional)",
        ref: this.boardResolutionInputRef,
        optional: true,
        hint: (
          <p className="text-muted small mb-1">
            Kindly download the Board Resolution or similar written
            authorization to open an account{" "}
            <a
              href={BOARD_RESOLUTION_URL}
              download="board-resolution-template.docx"
              className="kyc-download-link"
            >
              here
            </a>
            . Please fill it and upload it.
          </p>
        ),
      },
      {
        key: "addressProofFile",
        label: "Proof of Address *",
        ref: this.addressProofInputRef,
        hint: (
          <p className="text-muted small mb-1">
            Upload a recent proof of address document showing your full name,
            address, and issue date (issued within the last 3 months). Accepted
            documents include Utility bill, Bank statement or Credit card
            statement.
          </p>
        ),
      },
      {
        key: "companyBankStatement",
        label: "Bank Statement *",
        ref: this.companyBankStatementInputRef,
        hint: (
          <p className="text-muted small mb-1">
            Upload your recent company bank statement (last 3 months). The
            statement should clearly show your business name, account number,
            and transaction history.
          </p>
        ),
      },
      {
        key: "processingStatement",
        label: "Processing Statement (optional)",
        ref: this.processingStatementInputRef,
        hint: (
          <p className="text-muted small mb-1">
            Upload your recent payment processing statement (last 3 months). The
            statement should clearly show your business name, processing volume,
            transaction history, and processor details.
          </p>
        ),
        optional: true,
      },
    ];

    return (
      <div className="kyc-form-card">
        <h5 style={{ marginBottom: "24px" }}>Company Documents Upload</h5>
        <div className="row" style={{ marginTop: "8px" }}>
          {documents.map((doc) => (
            <div
              key={doc.key}
              className="form-group col-md-6"
              style={{
                marginBottom: "32px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ flex: 1 }}>
                <label>{doc.label}</label>
                {doc.hint && doc.hint}
              </div>
              <div style={{ marginTop: "auto" }}>
                <input
                  type="file"
                  className="form-control"
                  ref={doc.ref}
                  accept={ALLOWED_DOC_TYPES}
                  onChange={(e) =>
                    this.handleFileChange(
                      e,
                      doc.key,
                      doc.key + "Name",
                      ALLOWED_DOC_TYPES
                    )
                  }
                />
                <div>
                  <small
                    className="form-text text-muted"
                    style={{ marginTop: "2px" }}
                  >
                    Accepted: JPG, PDF, PNG, JPEG, DOC, DOCX, JFIF, HEIC. Max
                    6MB.
                  </small>
                  {this.state[doc.key + "Name"] && (
                    <small className="text-success d-block">
                      <i className="fas fa-file-check mr-1" />
                      {this.state[doc.key + "Name"]}
                    </small>
                  )}
                  {this.renderError(doc.key)}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="d-flex gap-2" style={{ marginTop: "16px" }}>
          <button className="btn btn-secondary" onClick={this.handleBack}>
            Back
          </button>
          <button
            className="btn btn-save-next"
            onClick={this.handleSaveAndNext}
          >
            Save and Next
          </button>
        </div>
      </div>
    );
  };

  renderAuthLetterStep = () => {
    const WOLFSBERG_URL =
      process.env.PUBLIC_URL + "/templates/wolfsberg-questionnaire.docx";
    const AUTH_LETTER_URL =
      process.env.PUBLIC_URL + "/templates/authorization-letter-template.pdf";
    return (
      <div className="kyc-form-card">
        <h5 style={{ marginBottom: "24px" }}>Authorization Letter Upload</h5>
        <div className="row" style={{ marginTop: "8px" }}>
          <div
            className="form-group col-md-6"
            style={{
              marginBottom: "32px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ flex: 1 }}>
              <label>Wolfsberg Questionnaire (Optional)</label>
              <p className="text-muted small mb-1">
                Kindly download the Wolfsberg Questionnaire template{" "}
                <a
                  href={WOLFSBERG_URL}
                  download="wolfsberg-questionnaire.docx"
                  className="kyc-download-link"
                >
                  here
                </a>
                . Please fill it and upload it. This is mandatory for all
                financial companies.
              </p>
            </div>
            <div style={{ marginTop: "auto" }}>
              <input
                type="file"
                className="form-control"
                ref={this.wolfsbergDocInputRef}
                accept={ALLOWED_DOC_TYPES}
                onChange={(e) =>
                  this.handleFileChange(
                    e,
                    "wolfsbergDoc",
                    "wolfsbergDocName",
                    ALLOWED_DOC_TYPES
                  )
                }
              />
              <div>
                {this.state.wolfsbergDocName && (
                  <small className="text-success mt-1 d-block">
                    <i className="fas fa-file-check mr-1" />
                    {this.state.wolfsbergDocName}
                  </small>
                )}
                {this.renderError("wolfsbergDoc")}
              </div>
            </div>
          </div>
          <div
            className="form-group col-md-6"
            style={{
              marginBottom: "32px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ flex: 1 }}>
              <label>Authorization Letter (Optional)</label>
              <p className="text-muted small mb-1">
                If there are persons authorized to access and operate the
                account, upload an Authorization Letter indicating their
                capacity. Kindly download the template{" "}
                <a
                  href={AUTH_LETTER_URL}
                  download="authorization-letter-template.pdf"
                  className="kyc-download-link"
                >
                  here
                </a>
                . Please fill it out and sign it. Upload the signed copy.
              </p>
            </div>
            <div style={{ marginTop: "auto" }}>
              <input
                type="file"
                className="form-control"
                ref={this.authLetterInputRef}
                accept={ALLOWED_DOC_TYPES}
                onChange={(e) =>
                  this.handleFileChange(
                    e,
                    "authorizationLetter",
                    "authorizationLetterName",
                    ALLOWED_DOC_TYPES
                  )
                }
              />
              <div>
                {this.state.authorizationLetterName && (
                  <small className="text-success mt-1 d-block">
                    <i className="fas fa-file-check mr-1" />
                    {this.state.authorizationLetterName}
                  </small>
                )}
                {this.renderError("authorizationLetter")}
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-secondary" onClick={this.handleBack}>
            Back
          </button>
          <button
            className="btn btn-save-next"
            onClick={this.handleSaveAndNext}
          >
            Save and Next
          </button>
        </div>
      </div>
    );
  };

  renderBeneficialOwnersStep = () => {
    const totalOwnership = this.getTotalOwnershipPercentage();
    const ownershipExceeded = totalOwnership > 100;
    return (
      <div className="kyc-form-card">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Beneficial Owners / UBO Information</h5>
          <button
            className="btn btn-primary"
            onClick={this.handleAddOwnerClick}
          >
            <i className="fas fa-plus mr-1" style={{ fontSize: "14px" }} /> Add
            Owner
          </button>
        </div>
        <div className="mb-3">
          <div className="d-flex align-items-center">
            <span className="font-weight-bold mr-2">Total Ownership:</span>
            <span
              className={totalOwnership > 100 ? "text-danger" : "text-info"}
              style={{ fontSize: "1.2rem", fontWeight: "bold" }}
            >
              {totalOwnership}%
            </span>
          </div>
          {ownershipExceeded && (
            <div className="alert alert-danger mt-2 py-2">
              <i className="fas fa-times-circle mr-2" />
              Total ownership exceeds 100%. Please adjust the percentages.
            </div>
          )}
        </div>
        {this.state.beneficialOwners.length > 0 ? (
          <div
            style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}
            className="mb-3"
          >
            <table
              className="table table-bordered mb-0"
              style={{ minWidth: "820px" }}
            >
              <thead>
                <tr>
                  <th>Owner Type</th>
                  <th>Title</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Country</th>
                  <th>SSN/Passport</th>
                  <th>Ownership %</th>
                  <th>PEP</th>
                  <th
                    style={{
                      position: "sticky",
                      right: 0,
                      background: "var(--table-header-bg, #1e1e2e)",
                      zIndex: 2,
                      boxShadow: "-2px 0 4px rgba(0,0,0,0.15)",
                      minWidth: "90px",
                      textAlign: "center",
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {this.state.beneficialOwners.map((o) => (
                  <tr key={o.ownerUuid || o.id}>
                    <td>{o.ownerType}</td>
                    <td>{o.title || "-"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {o.firstName} {o.middleName} {o.lastName}
                    </td>
                    <td>{o.email}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{o.phone}</td>
                    <td>{o.country}</td>
                    <td>{o.ssn}</td>
                    <td>{o.ownershipPer}%</td>
                    <td>{o.pep === 1 ? "Yes" : "No"}</td>
                    <td
                      style={{
                        position: "sticky",
                        right: 0,
                        background: "var(--table-row-bg, #16162a)",
                        zIndex: 1,
                        boxShadow: "-2px 0 4px rgba(0,0,0,0.15)",
                        textAlign: "center",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <button
                        className="btn btn-sm btn-primary mr-1"
                        title="Edit"
                        onClick={() => this.handleEditOwner(o)}
                      >
                        <i className="fas fa-edit" />
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        title="Delete"
                        onClick={() => this.handleDeleteClick(o)}
                      >
                        <i className="fas fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-4 mb-3 border rounded">
            <i
              className="fas fa-users text-muted mb-2"
              style={{ fontSize: "48px" }}
            />
            <p className="text-muted">No Beneficial Owners Added</p>
            <p className="text-muted small">
              Click "Add Owner" to add beneficial owners or authorized
              signatories
            </p>
          </div>
        )}
        <div className="d-flex gap-2">
          <button className="btn btn-secondary" onClick={this.handleBack}>
            Back
          </button>
          {this.state.beneficialOwners.length > 0 && totalOwnership <= 100 && (
            <button
              className="btn btn-save-next"
              onClick={this.submitFinalEnterpriseKyc}
            >
              Submit KYC Application
            </button>
          )}
          {totalOwnership > 100 && (
            <button
              className="btn btn-save-next"
              disabled
              style={{ opacity: 0.5 }}
            >
              Submit KYC Application (Fix Ownership %)
            </button>
          )}
        </div>
      </div>
    );
  };

  renderCurrentStep = () => {
    /* if (this.state.kycType === "individual") {
      switch (this.state.activeStep) {
        case 0:
          return this.renderPersonalInfoStep();
        case 1:
          return this.renderDobSsnStep();
        case 2:
          return this.renderEmploymentStep();
        case 3:
          return this.renderAddressProofStep();
        case 4:
          return this.renderIdDocumentStep();
        case 5:
          return this.renderPhotoVerificationStep();
        default:
          return null;
      }
    } else { */
    switch (this.state.activeStep) {
      case 0:
        return this.renderCompanyInfoStep();
      case 1:
        return this.renderCompanyDocumentsStep();
      case 2:
        return this.renderAuthLetterStep();
      case 3:
        return this.renderBeneficialOwnersStep();
      default:
        return null;
    }
    // }
  };

  render() {
    return (
      <div className="kyc-wrapper">
        {/* {this.state.kycStatus !== 1 && !this.state.showTypeSelection && (
          <button className="btn-change-type" onClick={this.handleChangeType}>
            <i className="fas fa-exchange-alt mr-2" />
            Change Type
          </button>
        )} */}
        {this.state.kycStatus >= 0 && this.renderKycStatus()}
        {/* {this.state.kycStatus === -1 &&
          this.state.showTypeSelection &&
          this.renderTypeSelection()} */}
        {this.state.kycStatus === -1 && !this.state.showTypeSelection && (
          <div>
            <div className="kyc-header">
              <div className="kyc-header-left">
                <h2>KYC Verification</h2>
                <span className={"kyc-badge " + this.state.kycType}>
                  {this.state.kycType === "individual"
                    ? "Individual"
                    : "Enterprise"}
                </span>
              </div>
            </div>
            <p className="kyc-header-subtitle">
              Complete your identity verification to unlock all features
            </p>
            {this.renderIconStepper()}
            {this.renderCurrentStep()}
          </div>
        )}

        {/* Beneficial Owner Modal */}
        {/* show={this.state.showAddOwnerModal} */}
        <Modal
          show={this.state.showAddOwnerModal}
          size="lg"
          backdrop="static"
          onHide={() => this.setState({ showAddOwnerModal: false })}
          className="kyc-owner-modal"
        >
          <Modal.Header>
            <Modal.Title>
              <i
                className={
                  this.state.editingOwner
                    ? "fas fa-user-edit mr-2"
                    : "fas fa-user-plus mr-2"
                }
              />
              {this.state.editingOwner ? "Edit" : "Add"} Beneficial Owner
            </Modal.Title>
            <button
              type="button"
              onClick={() => this.setState({ showAddOwnerModal: false })}
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                fontSize: "1.6rem",
                fontWeight: "400",
                lineHeight: "1",
                color: "inherit",
                opacity: 0.7,
                cursor: "pointer",
                padding: "0 4px",
                marginLeft: "auto",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
            >
              &times;
            </button>
          </Modal.Header>
          <Modal.Body
            onClick={(e) => {
              if (
                this.state.phoneDropdownOpen &&
                !e.target.closest(".phone-country-picker")
              ) {
                this.setState({
                  phoneDropdownOpen: false,
                  phoneSearchTerm: "",
                  phoneFilteredCountries: this.state.phoneCountries,
                });
              }
            }}
          >
            <div className="owner-modal-section">
              <div className="owner-section-label">Owner Classification</div>
              <div className="row">
                <div className="form-group col-md-6">
                  <label>Owner Type *</label>
                  <div className="kyc-select-wrapper">
                    <select
                      className="form-control"
                      value={this.state.ownerType}
                      onChange={(e) =>
                        this.setState({ ownerType: e.target.value })
                      }
                    >
                      <option value="Authorized signatory">
                        Authorized Signatory
                      </option>
                      <option value="Director">Director</option>
                      <option value="UBO">
                        UBO (Ultimate Beneficial Owner)
                      </option>
                    </select>
                    <i className="fas fa-chevron-down kyc-select-chevron" />
                  </div>
                </div>
                <div className="form-group col-md-6">
                  <label>Ownership Percentage *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={this.state.ownerOwnershipPercent}
                    onChange={(e) =>
                      this.setState({ ownerOwnershipPercent: e.target.value })
                    }
                    onBlur={(e) => {
                      const editingOwnerUuid =
                        this.state.editingOwner?.ownerUuid || null;
                      this.validateTotalOwnership(
                        e.target.value,
                        editingOwnerUuid
                      );
                    }}
                    placeholder="e.g. 25"
                    min="0"
                    max="100"
                  />
                </div>
              </div>
            </div>
            <div className="owner-modal-section">
              <div className="owner-section-label">Personal Information</div>
              <div className="row">
                <div className="form-group col-md-3">
                  <label>Title *</label>
                  <div className="kyc-select-wrapper">
                    <select
                      className="form-control"
                      value={this.state.ownerTitle}
                      onChange={(e) =>
                        this.setState({ ownerTitle: e.target.value })
                      }
                    >
                      <option value="President">President</option>
                      <option value="Vice President">Vice President</option>
                      <option value="Secretary">Secretary</option>
                      <option value="Sole Proprietor">Sole Proprietor</option>
                      <option value="Treasurer">Treasurer</option>
                      <option value="Partner">Partner</option>
                      <option value="Member">Member</option>
                      <option value="CFO">CFO</option>
                      <option value="Owner">Owner</option>
                      <option value="CEO">CEO</option>
                      <option value="Recorder">Recorder</option>
                    </select>
                    <i className="fas fa-chevron-down kyc-select-chevron" />
                  </div>
                </div>
                <div className="form-group col-md-3">
                  <label>First Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.ownerFirstName}
                    onChange={(e) =>
                      this.setState({ ownerFirstName: e.target.value })
                    }
                    onKeyDown={this.allowOnlyAlphabetsCityState}
                    onPaste={this.preventPasteCityState}
                    placeholder="First name"
                  />
                </div>
                <div className="form-group col-md-3">
                  <label>Middle Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.ownerMiddleName}
                    onChange={(e) =>
                      this.setState({ ownerMiddleName: e.target.value })
                    }
                    onKeyDown={this.allowOnlyAlphabetsCityState}
                    onPaste={this.preventPasteCityState}
                    placeholder="Middle name"
                  />
                </div>
                <div className="form-group col-md-3">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.ownerLastName}
                    onChange={(e) =>
                      this.setState({ ownerLastName: e.target.value })
                    }
                    onKeyDown={this.allowOnlyAlphabetsCityState}
                    onPaste={this.preventPasteCityState}
                    placeholder="Last name"
                  />
                </div>
              </div>
              <div className="row">
                <div className="form-group col-md-6">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    className={
                      "form-control " +
                      (this.state.ownerEmailError ? "is-invalid" : "")
                    }
                    value={this.state.ownerEmail}
                    onChange={(e) =>
                      this.setState({ ownerEmail: e.target.value })
                    }
                    onBlur={(e) => this.validateOwnerEmail(e.target.value)}
                    onKeyUp={(e) => this.validateOwnerEmail(e.target.value)}
                    placeholder="owner@company.com"
                  />
                  {this.state.ownerEmailError && (
                    <small className="text-danger d-block mt-1">
                      {this.state.ownerEmailError}
                    </small>
                  )}
                </div>
                <div className="form-group col-md-6">
                  <label>Phone Number *</label>
                  <PhoneInput
                    country={
                      this.state.phoneSelectedCountry
                        ? this.state.phoneSelectedCountry.code.toLowerCase()
                        : "us"
                    }
                    value={this.state.ownerPhone}
                    onChange={(phone, countryData) => {
                      this.setState(
                        {
                          ownerPhone: phone,
                          phoneSelectedCountry: {
                            code: countryData.countryCode?.toUpperCase(),
                            dialCode: countryData.dialCode,
                            country: countryData.name,
                          },
                        },
                        () => this.validateOwnerPhone(this.state.ownerPhone)
                      );
                    }}
                    inputProps={{
                      className:
                        "form-control " +
                        (this.state.ownerPhoneError ? "is-invalid" : ""),
                      style: {
                        width: "100%",
                        height: "38px",
                        paddingLeft: "48px",
                      },
                    }}
                    containerStyle={{ width: "100%" }}
                    inputStyle={{ width: "100%", borderRadius: "4px" }}
                    buttonStyle={{
                      borderRadius: "4px 0 0 4px",
                      border: "1px solid #ced4da",
                      borderRight: "none",
                    }}
                  />
                  {this.state.ownerPhoneError && (
                    <small className="text-danger d-block mt-1">
                      {this.state.ownerPhoneError}
                    </small>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="form-group col-md-4">
                  <label>Date of Birth *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={this.state.ownerDob}
                    onChange={(e) =>
                      this.setState({ ownerDob: e.target.value })
                    }
                    onBlur={(e) => this.validateOwnerAge(e.target.value)}
                  />
                </div>
                <div className="form-group col-md-4">
                  <label>Place of Birth *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.ownerBirthPlace}
                    onChange={(e) =>
                      this.setState({ ownerBirthPlace: e.target.value })
                    }
                    onKeyDown={this.allowOnlyAlphabetsCityState}
                    onPaste={this.preventPasteCityState}
                    placeholder="City, Country"
                  />
                </div>
                <div className="form-group col-md-4">
                  <label>SSN / Passport No. *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.ownerSsn}
                    onChange={(e) =>
                      this.setState({ ownerSsn: e.target.value.toUpperCase() })
                    }
                    onKeyDown={this.onlyAllowedInput}
                    placeholder="SSN or Passport number"
                    maxLength="15"
                  />
                </div>
              </div>
            </div>
            <div className="owner-modal-section">
              <div className="owner-section-label">Residential Address</div>
              <div className="row">
                <div className="form-group col-md-12">
                  <label>Street Address *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.ownerAddress}
                    onChange={(e) =>
                      this.setState({ ownerAddress: e.target.value })
                    }
                    onKeyDown={this.onlyAllowedInputStreet}
                    onPaste={this.preventPasteStreet}
                    placeholder="123 Main Street, Apt 4B"
                  />
                </div>
              </div>
              <div className="row">
                <div className="form-group col-md-3">
                  <label>City *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.ownerCity}
                    onChange={(e) =>
                      this.setState({ ownerCity: e.target.value })
                    }
                    onKeyDown={this.allowOnlyAlphabetsCityState}
                    onPaste={this.preventPasteCityState}
                    placeholder="City"
                  />
                </div>
                <div className="form-group col-md-3">
                  <label>State *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.ownerState}
                    onChange={(e) =>
                      this.setState({ ownerState: e.target.value })
                    }
                    onKeyDown={this.allowOnlyAlphabetsCityState}
                    onPaste={this.preventPasteCityState}
                    placeholder="State"
                  />
                </div>
                <div className="form-group col-md-3">
                  <label>Country *</label>
                  <div className="kyc-select-wrapper">
                    <select
                      className="form-control"
                      value={this.state.ownerCountry}
                      onChange={(e) =>
                        this.setState({ ownerCountry: e.target.value })
                      }
                    >
                      <option value="">Select Country</option>
                      {this.state.countries.map((c) => (
                        <option key={c.code} value={c.country}>
                          {c.country}
                        </option>
                      ))}
                    </select>
                    <i className="fas fa-chevron-down kyc-select-chevron" />
                  </div>
                </div>
                <div className="form-group col-md-3">
                  <label>Zip Code *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.ownerZip}
                    onChange={(e) =>
                      this.setState({ ownerZip: e.target.value })
                    }
                    onKeyDown={this.onlyAllowedZipCode}
                    placeholder="Zip Code"
                    maxLength="15"
                  />
                </div>
              </div>
            </div>
            <div className="owner-modal-section">
              <div className="owner-section-label">Compliance</div>
              <div className="row">
                <div className="form-group col-md-6">
                  <label>Politically Exposed Person (PEP)? *</label>
                  <div className="kyc-select-wrapper">
                    <select
                      className="form-control"
                      value={this.state.ownerPep}
                      onChange={(e) =>
                        this.setState({ ownerPep: e.target.value })
                      }
                    >
                      <option value="0">No</option>
                      <option value="1">Yes</option>
                    </select>
                    <i className="fas fa-chevron-down kyc-select-chevron" />
                  </div>
                </div>
              </div>
            </div>
            <div className="owner-modal-section">
              <div className="owner-section-label">Identity Documents</div>
              <div className="row">
                <div className={this.state.ownerIdType === "Driver's license" ? "form-group col-md-3" : "form-group col-md-4"}>
                  <label>ID type *</label>
                  <div className="kyc-select-wrapper">
                    <select
                      className="form-control"
                      value={this.state.ownerIdType}
                      onChange={(e) =>
                        this.setState({ ownerIdType: e.target.value })
                      }
                    >
                      <option value="">Select ID Type</option>
                      <option value="Driver's license">Driver's license</option>
                      <option value="Passport">Passport</option>
                      <option value="National ID Card">National ID Card</option>
                      <option value="Residence Permit / Green Card">
                        Residence Permit / Green Card
                      </option>
                    </select>
                    <i className="fas fa-chevron-down kyc-select-chevron" />
                  </div>
                </div>

                <div className={this.state.ownerIdType === "Driver's license" ? "form-group col-md-3" : "form-group col-md-4"}>
                  <label>ID Number *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.ownerIdNumber}
                    onChange={(e) =>
                      this.setState({ ownerIdNumber: e.target.value })
                    }
                    placeholder="Enter ID number"
                  />
                </div>

                <div className={this.state.ownerIdType === "Driver's license" ? "form-group col-md-3" : "form-group col-md-4"}>
                  <label>Country *</label>
                  <div className="kyc-select-wrapper">
                    <select
                      className="form-control"
                      value={this.state.ownerIdCountry}
                      onChange={(e) =>
                        this.handleIdCountryChange(e.target.value)
                      }
                    >
                      <option value="">Select Country</option>
                      {this.state.countries.map((c) => (
                        <option key={c.code} value={c.country}>
                          {c.country}
                        </option>
                      ))}
                    </select>
                    <i className="fas fa-chevron-down kyc-select-chevron" />
                  </div>
                </div>

                {this.state.ownerIdType === "Driver's license" && (
                  <div className="form-group col-md-3">
                    <label>State *</label>
                    <div className="kyc-select-wrapper">
                      <select
                        className="form-control"
                        value={this.state.ownerIdState}
                        onChange={(e) =>
                          this.setState({ ownerIdState: e.target.value })
                        }
                      >
                        <option value="">Select State</option>
                        {this.state.idStates.map((s) => (
                          <option key={s.isoCode} value={s.name}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                      <i className="fas fa-chevron-down kyc-select-chevron" />
                    </div>
                  </div>
                )}
              </div>
              <div className="row">
                <div className="form-group col-md-6">
                  <div>
                    <label>Government ID - Front *</label>
                  </div>
                  <div>
                    <input
                      key="owner-front-id"
                      type="file"
                      className="form-control"
                      ref={this.ownerFrontIdInputRef}
                      accept={ALLOWED_DOC_TYPES}
                      onChange={(e) =>
                        this.handleFileChange(
                          e,
                          "ownerFrontId",
                          "ownerFrontIdName",
                          ALLOWED_DOC_TYPES
                        )
                      }
                    />
                    {this.state.ownerFrontIdName && (
                      <small className="owner-file-badge">
                        <i className="fas fa-file-check" />{" "}
                        {this.state.ownerFrontIdName}
                      </small>
                    )}
                  </div>
                </div>
                <div className="form-group col-md-6">
                  <div>
                    <label>Government ID - Back *</label>
                  </div>
                  <div>
                    <input
                      key="owner-back-id"
                      type="file"
                      className="form-control"
                      ref={this.ownerBackIdInputRef}
                      accept={ALLOWED_DOC_TYPES}
                      onChange={(e) =>
                        this.handleFileChange(
                          e,
                          "ownerBackId",
                          "ownerBackIdName",
                          ALLOWED_DOC_TYPES
                        )
                      }
                    />
                    {this.state.ownerBackIdName && (
                      <small className="owner-file-badge">
                        <i className="fas fa-file-check" />{" "}
                        {this.state.ownerBackIdName}
                      </small>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="form-group col-md-12">
                  <label>Proof of Address document type *</label>
                  <div className="kyc-select-wrapper">
                    <select
                      className="form-control"
                      value={this.state.ownerPoaType}
                      onChange={(e) =>
                        this.setState({ ownerPoaType: e.target.value })
                      }
                    >
                      <option value="">Select POA Type</option>
                      <option value="Utility Bill (Electricity / Water / Gas / Internet)">
                        Utility Bill (Electricity / Water / Gas / Internet)
                      </option>
                      <option value="Bank Statement">Bank Statement</option>
                      <option value="Credit Card Statement">
                        Credit Card Statement
                      </option>
                      <option value="Government-Issued Letter">
                        Government-Issued Letter
                      </option>
                      <option value="Processing Statement">
                        Processing Statement
                      </option>
                      <option value="Tax Document / Tax Assessment">
                        Tax Document / Tax Assessment
                      </option>
                      <option value="Rental / Lease Agreement">
                        Rental / Lease Agreement
                      </option>
                      <option value="Mortgage Statement">
                        Mortgage Statement
                      </option>
                      <option value="Insurance Statement">
                        Insurance Statement
                      </option>
                      <option value="Residence Permit with Address">
                        Residence Permit with Address
                      </option>
                      <option value="Municipal / Local Authority Letter">
                        Municipal / Local Authority Letter
                      </option>
                      <option value="Telephone / Mobile Bill">
                        Telephone / Mobile Bill
                      </option>
                    </select>
                    <i className="fas fa-chevron-down kyc-select-chevron" />
                  </div>
                </div>
              </div>
              <div className="row">
                <div
                  className="form-group col-md-12"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <div style={{ flex: 1 }}>
                    <label>Proof of Address *</label>
                    <p className="text-muted small mb-1">
                      Upload a recent proof of address document showing your
                      full name, address, and issue date (issued within the last
                      3 months). Accepted documents include Utility bill, Bank
                      statement or Credit card statement.
                    </p>
                  </div>
                  <div style={{ marginTop: "auto" }}>
                    <input
                      key="owner-address-proof"
                      type="file"
                      className="form-control"
                      ref={this.ownerAddressProofInputRef}
                      accept={ALLOWED_DOC_TYPES}
                      onChange={(e) =>
                        this.handleFileChange(
                          e,
                          "ownerAddressProof",
                          "ownerAddressProofName",
                          ALLOWED_DOC_TYPES
                        )
                      }
                    />
                    {this.state.ownerAddressProofName && (
                      <small className="owner-file-badge">
                        <i className="fas fa-file-check" />{" "}
                        {this.state.ownerAddressProofName}
                      </small>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="form-group col-md-6">
                  <div>
                    <label>Selfie holding ID *</label>
                  </div>
                  <div>
                    <input
                      key="owner-selfie"
                      type="file"
                      className="form-control"
                      ref={this.ownerSelfieInputRef}
                      accept={ALLOWED_IMAGE_TYPES}
                      onChange={(e) =>
                        this.handleFileChange(
                          e,
                          "ownerSelfie",
                          "ownerSelfieName",
                          ALLOWED_IMAGE_TYPES
                        )
                      }
                    />
                    {this.state.ownerSelfieName && (
                      <small className="owner-file-badge">
                        <i className="fas fa-file-check" />{" "}
                        {this.state.ownerSelfieName}
                      </small>
                    )}
                  </div>
                </div>
                <div className="form-group col-md-6">
                  <div>
                    <label>
                      Proof of Investment Fund{" "}
                      <span className="owner-optional-tag">Optional</span>
                    </label>
                  </div>
                  <div>
                    <input
                      key="owner-investing-fund"
                      type="file"
                      className="form-control"
                      ref={this.ownerInvestingFundInputRef}
                      accept={ALLOWED_DOC_TYPES}
                      onChange={(e) =>
                        this.handleFileChange(
                          e,
                          "ownerInvestingFund",
                          "ownerInvestingFundName",
                          ALLOWED_DOC_TYPES
                        )
                      }
                    />
                    {this.state.ownerInvestingFundName && (
                      <small className="owner-file-badge">
                        <i className="fas fa-file-check" />{" "}
                        {this.state.ownerInvestingFundName}
                      </small>
                    )}
                  </div>
                </div>
              </div>
              <small className="text-muted d-block">
                Max 6MB per file. Accepted: JPG, PNG, PDF, DOC, DOCX, JFIF, HEIC
              </small>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-secondary"
              onClick={() => this.setState({ showAddOwnerModal: false })}
            >
              Cancel
            </button>
            <button className="btn btn-primary" onClick={this.handleAddOwner}>
              <i
                className={
                  this.state.editingOwner
                    ? "fas fa-save mr-1"
                    : "fas fa-plus mr-1"
                }
                style={{ fontSize: "14px" }}
              />
              {this.state.editingOwner ? "Update Owner" : "Add Owner"}
            </button>
          </Modal.Footer>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          show={this.state.showDeleteConfirm}
          onHide={() =>
            this.setState({ showDeleteConfirm: false, ownerToDelete: null })
          }
          centered
        >
          <Modal.Header>
            <Modal.Title>Confirm Delete</Modal.Title>
            <button
              type="button"
              onClick={() =>
                this.setState({ showDeleteConfirm: false, ownerToDelete: null })
              }
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                fontSize: "1.6rem",
                fontWeight: "400",
                lineHeight: "1",
                color: "inherit",
                opacity: 0.7,
                cursor: "pointer",
                padding: "0 4px",
                marginLeft: "auto",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
            >
              &times;
            </button>
          </Modal.Header>
          <Modal.Body className="p-4">
            <p>
              Are you sure you want to delete this beneficial owner? This action
              cannot be undone.
            </p>
            {this.state.ownerToDelete && (
              <p className="font-weight-bold">
                {this.state.ownerToDelete.firstName}{" "}
                {this.state.ownerToDelete.lastName}
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-secondary"
              onClick={() =>
                this.setState({ showDeleteConfirm: false, ownerToDelete: null })
              }
            >
              Cancel
            </button>
            <button className="btn btn-danger" onClick={this.confirmDelete}>
              Delete
            </button>
          </Modal.Footer>
        </Modal>

        {/* QR Code Modal */}
        <Modal show={this.state.showQR} onHide={this.handleCloseQR} centered>
          <Modal.Header>
            <Modal.Title>Scan QR Code</Modal.Title>
            <button
              type="button"
              onClick={this.handleCloseQR}
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                fontSize: "1.6rem",
                fontWeight: "400",
                lineHeight: "1",
                color: "inherit",
                opacity: 0.7,
                cursor: "pointer",
                padding: "0 4px",
                marginLeft: "auto",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
            >
              &times;
            </button>
          </Modal.Header>
          <Modal.Body className="p-4 text-center">
            <div
              className="d-inline-block p-3 bg-white rounded mb-3"
              style={{ border: "1px solid var(--border-color)" }}
            >
              <QRCode value={this.getQrValue()} size={200} />
            </div>
            <p className="mb-2">
              Scan this QR code with your mobile device to take a photo.
            </p>
            <p className="text-muted">
              Code expires in:{" "}
              <strong>{this.formatTime(this.state.qrCount)}</strong>
            </p>
            <p className="text-info" style={{ fontSize: "12px" }}>
              <i className="fas fa-sync-alt fa-spin mr-1" />
              Waiting for photo from mobile device...
            </p>
          </Modal.Body>
        </Modal>

        {/* Change #1: Key Entered Info Popup */}
        <Modal
          show={this.state.showKeyEnteredInfo}
          onHide={() => this.setState({ showKeyEnteredInfo: false })}
          centered
        >
          <Modal.Header>
            <Modal.Title>Key Entered (Card-Not-Present)</Modal.Title>
            <button
              type="button"
              onClick={() => this.setState({ showKeyEnteredInfo: false })}
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                fontSize: "1.6rem",
                fontWeight: "400",
                lineHeight: "1",
                color: "inherit",
                opacity: 0.7,
                cursor: "pointer",
                padding: "0 4px",
                marginLeft: "auto",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
            >
              &times;
            </button>
          </Modal.Header>
          <Modal.Body className="p-4">
            <p>
              Key Entered means the merchant manually types the card details
              instead of swiping, dipping, tapping, or using an online checkout.
            </p>
            <p>
              <strong>Examples:</strong>
            </p>
            <ul>
              <li>Merchant enters card number into a virtual terminal</li>
              <li>Phone order where customer reads card details</li>
              <li>Mail order processing</li>
              <li>Backup processing when card reader fails</li>
            </ul>
            <div className="alert alert-warning py-2 mb-0">
              {/* <i className="fas fa-exclamation-triangle mr-2" /> */}
              Risk level: Higher fraud and chargeback risk because the card is
              not physically read.
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-primary"
              onClick={() => this.setState({ showKeyEnteredInfo: false })}
            >
              Got it
            </button>
          </Modal.Footer>
        </Modal>

        {/* Recommended High-risk MCC Info Popup */}
        <Modal
          show={this.state.showMccInfo}
          onHide={() => this.setState({ showMccInfo: false })}
          size="lg"
          centered
        >
          <Modal.Header>
            <Modal.Title>Recommended High-risk MCC</Modal.Title>
            <button
              type="button"
              onClick={() => this.setState({ showMccInfo: false })}
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                fontSize: "1.6rem",
                fontWeight: "400",
                lineHeight: "1",
                color: "inherit",
                opacity: 0.7,
                cursor: "pointer",
                padding: "0 4px",
                marginLeft: "auto",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
            >
              &times;
            </button>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="table-responsive">
              <table className="table table-bordered ">
                <thead className="thead-dark">
                  <tr>
                    <th>Merchant Industry</th>
                    <th>Recommended MCC</th>
                    <th>MCC Description</th>
                    <th>Underwriting View</th>
                  </tr>
                </thead>
                <tbody>
                  {MCC_RECOMMENDATIONS.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.industry}</td>
                      <td className="font-weight-bold">{item.mcc}</td>
                      <td>{item.description}</td>
                      <td>
                        <span
                          className={`badge ${item.view.includes("High Risk")
                            ? "badge-danger"
                            : item.view.includes("Medium")
                              ? "badge-warning"
                              : "badge-info"
                            }`}
                        >
                          {item.view}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-primary"
              onClick={() => this.setState({ showMccInfo: false })}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>

        <Loader show={this.state.showLoader} />
      </div>
    );
  }
}

export default KycContent;
